import React, { useState, useEffect, useMemo, useRef } from "react";
import { Search, ChevronDown, Clock, Globe, Loader, Send, ArrowLeft } from "lucide-react";

// API base URL (local dev -> Express on :5000, production -> same-origin)
const API_BASE = process.env.NODE_ENV === "production" ? "" : "http://localhost:5000";
const PROD_API_FALLBACK = "https://appi-holidays.vercel.app";
const ITINERARY_DAY_LIMIT = 2;
const ITINERARY_HISTORY_LOOKBACK_DAYS = 45;

const PLANNER_TEXT = {
  en: {
    modeSingle: "Single City",
    modeMulti: "Multi-City",
    modeRoad: "Road Trip",
    modeInterrail: "InterRail",
    step1: "Step 1",
    step2: "Step 2",
    step3: "Step 3",
    setDestination: "Set Destination",
    chooseTripStyle: "Choose Trip Style",
    styleHint: "Pace, budget, and up to 4 interests",
    generate: "Generate",
    generateHint: "AI itinerary with live weather and nearby eats",
    selectedNow: "Selected Now",
    destination: "Destination",
    budget: "Budget",
    interests: "Interests",
    notSelected: "Not selected",
    paceQuestion: "What's your pace?",
    paceRelaxed: "Relaxed",
    paceBalanced: "Balanced",
    paceAction: "Action-packed",
    selectInterestsHint: "Select up to 4 interests",
    generating: "Generating...",
    generateItinerary: "Generate Itinerary",
    extraNotes: "Extra Notes",
    extraNotesPlaceholder: "Enter specific attractions or themes not covered above",
    itineraryProgressTitle: "Itinerary in Progress!",
    itineraryProgressBody: "Your itinerary will be ready in just a few moments.",
    airportToCity: "Airport to City Center",
    uponArrival: "Upon Arrival",
    airportsLabel: "Airports",
    trainsLabel: "Trains",
    metroLabel: "Metro",
    busServicesLabel: "Bus Services",
    expressServicesLabel: "Express Services",
    cityRoutesLabel: "City/Local Routes",
    taxisLabel: "Taxis",
    taxiRanksLabel: "Taxi Ranks",
    costLabel: "Cost",
    prebookLabel: "Pre-book",
    hotelShuttlesLabel: "Hotel Shuttles",
    carRentalLabel: "Car Rental",
    locationLabel: "Location",
    companiesLabel: "Companies",
    clearFilterLabel: "Clear Filter",
    countryLabel: "Country",
    selectedCountryLabel: "Selected Country",
    cityTownLabel: "City or Town",
    cityExamplePlaceholder: "e.g., Rome",
    numberDaysLabel: "Number of days",
    tripStartDateOptionalLabel: "Trip Start Date (optional)",
    checkInLabel: "Check-in",
    checkOutLabel: "Check-out",
    whereLabel: "Where",
    datesLabel: "Dates",
    guestsLabel: "Guests",
    searchPlaceholder: "City, airport, hotel, or landmark",
    countrySearchPlaceholder: "Search or select a country...",
    searchInContinentPlaceholder: "Search in {continent}...",
    selectFromListBelowPlaceholder: "Select from list below",
    temporarilyLockedLabel: "Temporarily locked",
    lockedLabel: "Locked",
    searchLabel: "Search",
    doneLabel: "Done",
    prevLabel: "Prev",
    nextLabel: "Next",
    continentAmericas: "N/S America",
    continentEurope: "Europe",
    continentAsia: "Asia",
    continentOceania: "Oceania",
    continentAfrica: "Africa",
    budgetOptionBudget: "Budget",
    budgetOptionMidRange: "Mid-range",
    budgetOptionLuxury: "Luxury",
    interestCulture: "Culture",
    interestNature: "Nature",
    interestFood: "Food",
    interestNightlife: "Nightlife",
    interestShopping: "Shopping",
    interestBeaches: "Beaches",
    interestMuseums: "Museums",
    interestHistory: "History",
    interestHiking: "Hiking",
    interestAdventure: "Adventure",
    interestFamily: "Family",
    interestRomance: "Romance",
    interestPhotography: "Photography",
    interestWildlife: "Wildlife",
    interestWellness: "Wellness",
    interestFestivals: "Festivals",
    interestSports: "Sports",
    interestWine: "Wine",
    interestRoadTrips: "Road Trips",
    interestWinterSports: "Winter Sports",
  },
  es: {
    modeSingle: "Ciudad unica",
    modeMulti: "Multi-ciudad",
    modeRoad: "Viaje por carretera",
    modeInterrail: "InterRail",
    step1: "Paso 1",
    step2: "Paso 2",
    step3: "Paso 3",
    setDestination: "Define destino",
    chooseTripStyle: "Elige estilo de viaje",
    styleHint: "Ritmo, presupuesto y hasta 4 intereses",
    generate: "Generar",
    generateHint: "Itinerario con IA, clima en vivo y restaurantes cercanos",
    selectedNow: "Seleccion actual",
    destination: "Destino",
    budget: "Presupuesto",
    interests: "Intereses",
    notSelected: "Sin seleccionar",
    paceQuestion: "Cual es tu ritmo?",
    paceRelaxed: "Relajado",
    paceBalanced: "Equilibrado",
    paceAction: "Intenso",
    selectInterestsHint: "Selecciona hasta 4 intereses",
    generating: "Generando...",
    generateItinerary: "Generar itinerario",
    extraNotes: "Notas extra",
    extraNotesPlaceholder: "Introduce atracciones o temas especificos no cubiertos arriba",
    itineraryProgressTitle: "Itinerario en progreso!",
    itineraryProgressBody: "Tu itinerario estara listo en unos momentos.",
    airportToCity: "Aeropuerto al Centro",
    uponArrival: "A tu llegada",
    airportsLabel: "Aeropuertos",
    trainsLabel: "Trenes",
    metroLabel: "Metro",
    busServicesLabel: "Servicios de autobus",
    expressServicesLabel: "Servicios expresos",
    cityRoutesLabel: "Rutas urbanas/locales",
    taxisLabel: "Taxis",
    taxiRanksLabel: "Paradas de taxi",
    costLabel: "Costo",
    prebookLabel: "Reserva previa",
    hotelShuttlesLabel: "Traslados de hotel",
    carRentalLabel: "Alquiler de coche",
    locationLabel: "Ubicacion",
    companiesLabel: "Empresas",
    clearFilterLabel: "Limpiar filtro",
    countryLabel: "Pais",
    selectedCountryLabel: "Pais seleccionado",
    cityTownLabel: "Ciudad o pueblo",
    cityExamplePlaceholder: "ej.: Roma",
    numberDaysLabel: "Numero de dias",
    tripStartDateOptionalLabel: "Fecha de inicio del viaje (opcional)",
    checkInLabel: "Check-in",
    checkOutLabel: "Check-out",
    whereLabel: "Destino",
    datesLabel: "Fechas",
    guestsLabel: "Huespedes",
    searchPlaceholder: "Ciudad, aeropuerto, hotel o punto de interes",
    countrySearchPlaceholder: "Busca o selecciona un pais...",
    searchInContinentPlaceholder: "Buscar en {continent}...",
    selectFromListBelowPlaceholder: "Selecciona de la lista abajo",
    temporarilyLockedLabel: "Bloqueado temporalmente",
    lockedLabel: "Bloqueado",
    searchLabel: "Buscar",
    doneLabel: "Listo",
    prevLabel: "Anterior",
    nextLabel: "Siguiente",
    continentAmericas: "N/S America",
    continentEurope: "Europa",
    continentAsia: "Asia",
    continentOceania: "Oceania",
    continentAfrica: "Africa",
    budgetOptionBudget: "Economico",
    budgetOptionMidRange: "Intermedio",
    budgetOptionLuxury: "Lujo",
    interestCulture: "Cultura",
    interestNature: "Naturaleza",
    interestFood: "Gastronomia",
    interestNightlife: "Vida nocturna",
    interestShopping: "Compras",
    interestBeaches: "Playas",
    interestMuseums: "Museos",
    interestHistory: "Historia",
    interestHiking: "Senderismo",
    interestAdventure: "Aventura",
    interestFamily: "Familia",
    interestRomance: "Romance",
    interestPhotography: "Fotografia",
    interestWildlife: "Vida salvaje",
    interestWellness: "Bienestar",
    interestFestivals: "Festivales",
    interestSports: "Deportes",
    interestWine: "Vino",
    interestRoadTrips: "Viajes por carretera",
    interestWinterSports: "Deportes de invierno",
  },
  fr: {
    modeSingle: "Ville unique",
    modeMulti: "Multi-ville",
    modeRoad: "Road trip",
    modeInterrail: "InterRail",
    step1: "Etape 1",
    step2: "Etape 2",
    step3: "Etape 3",
    setDestination: "Definir la destination",
    chooseTripStyle: "Choisir le style de voyage",
    styleHint: "Rythme, budget et jusqu'a 4 interets",
    generate: "Generer",
    generateHint: "Itineraire IA avec meteo en direct et restos proches",
    selectedNow: "Selection actuelle",
    destination: "Destination",
    budget: "Budget",
    interests: "Interets",
    notSelected: "Non selectionne",
    paceQuestion: "Quel est votre rythme ?",
    paceRelaxed: "Detendu",
    paceBalanced: "Equilibre",
    paceAction: "Dynamique",
    selectInterestsHint: "Selectionnez jusqu'a 4 interets",
    generating: "Generation...",
    generateItinerary: "Generer l'itineraire",
    extraNotes: "Notes supplementaires",
    extraNotesPlaceholder: "Entrez des attractions ou themes specifiques non couverts ci-dessus",
    itineraryProgressTitle: "Itineraire en cours!",
    itineraryProgressBody: "Votre itineraire sera pret dans quelques instants.",
    airportToCity: "Aeroport vers le centre-ville",
    uponArrival: "A l'arrivee",
    airportsLabel: "Aeroports",
    trainsLabel: "Trains",
    metroLabel: "Metro",
    busServicesLabel: "Services de bus",
    expressServicesLabel: "Services express",
    cityRoutesLabel: "Lignes urbaines/locales",
    taxisLabel: "Taxis",
    taxiRanksLabel: "Stations de taxi",
    costLabel: "Cout",
    prebookLabel: "Pre-reservation",
    hotelShuttlesLabel: "Navettes hotel",
    carRentalLabel: "Location de voiture",
    locationLabel: "Emplacement",
    companiesLabel: "Entreprises",
    clearFilterLabel: "Effacer le filtre",
    countryLabel: "Pays",
    selectedCountryLabel: "Pays selectionne",
    cityTownLabel: "Ville ou village",
    cityExamplePlaceholder: "ex.: Rome",
    numberDaysLabel: "Nombre de jours",
    tripStartDateOptionalLabel: "Date de debut du voyage (optionnel)",
    checkInLabel: "Arrivee",
    checkOutLabel: "Depart",
    whereLabel: "Destination",
    datesLabel: "Dates",
    guestsLabel: "Voyageurs",
    searchPlaceholder: "Ville, aeroport, hotel ou lieu",
    countrySearchPlaceholder: "Rechercher ou selectionner un pays...",
    searchInContinentPlaceholder: "Rechercher en {continent}...",
    selectFromListBelowPlaceholder: "Selectionnez dans la liste ci-dessous",
    temporarilyLockedLabel: "Temporairement verrouille",
    lockedLabel: "Verrouille",
    searchLabel: "Rechercher",
    doneLabel: "Termine",
    prevLabel: "Prec.",
    nextLabel: "Suiv.",
    continentAmericas: "N/S Amerique",
    continentEurope: "Europe",
    continentAsia: "Asie",
    continentOceania: "Oceanie",
    continentAfrica: "Afrique",
    budgetOptionBudget: "Economique",
    budgetOptionMidRange: "Moyen",
    budgetOptionLuxury: "Luxe",
    interestCulture: "Culture",
    interestNature: "Nature",
    interestFood: "Gastronomie",
    interestNightlife: "Vie nocturne",
    interestShopping: "Shopping",
    interestBeaches: "Plages",
    interestMuseums: "Musees",
    interestHistory: "Histoire",
    interestHiking: "Randonnee",
    interestAdventure: "Aventure",
    interestFamily: "Famille",
    interestRomance: "Romance",
    interestPhotography: "Photographie",
    interestWildlife: "Faune",
    interestWellness: "Bien-etre",
    interestFestivals: "Festivals",
    interestSports: "Sports",
    interestWine: "Vin",
    interestRoadTrips: "Road trips",
    interestWinterSports: "Sports d'hiver",
  },
  de: {
    modeSingle: "Einzelstadt",
    modeMulti: "Mehrere Stadte",
    modeRoad: "Roadtrip",
    modeInterrail: "InterRail",
    step1: "Schritt 1",
    step2: "Schritt 2",
    step3: "Schritt 3",
    setDestination: "Ziel festlegen",
    chooseTripStyle: "Reisestil wahlen",
    styleHint: "Tempo, Budget und bis zu 4 Interessen",
    generate: "Erstellen",
    generateHint: "KI-Reiseplan mit Live-Wetter und Restaurants in der Nahe",
    selectedNow: "Aktuell gewahlt",
    destination: "Ziel",
    budget: "Budget",
    interests: "Interessen",
    notSelected: "Nicht ausgewahlt",
    paceQuestion: "Wie ist dein Tempo?",
    paceRelaxed: "Entspannt",
    paceBalanced: "Ausgewogen",
    paceAction: "Aktionsreich",
    selectInterestsHint: "Wahle bis zu 4 Interessen",
    generating: "Wird erstellt...",
    generateItinerary: "Reiseplan erstellen",
    extraNotes: "Extra-Notizen",
    extraNotesPlaceholder: "Gib konkrete Attraktionen oder Themen ein, die oben nicht enthalten sind",
    itineraryProgressTitle: "Reiseplan in Arbeit!",
    itineraryProgressBody: "Dein Reiseplan ist in wenigen Augenblicken fertig.",
    airportToCity: "Flughafen ins Stadtzentrum",
    uponArrival: "Bei Ankunft",
    airportsLabel: "Flughafen",
    trainsLabel: "Zuge",
    metroLabel: "U-Bahn",
    busServicesLabel: "Busverbindungen",
    expressServicesLabel: "Expressdienste",
    cityRoutesLabel: "Stadt-/Lokalrouten",
    taxisLabel: "Taxis",
    taxiRanksLabel: "Taxistande",
    costLabel: "Kosten",
    prebookLabel: "Vorab buchen",
    hotelShuttlesLabel: "Hotel-Shuttles",
    carRentalLabel: "Mietwagen",
    locationLabel: "Standort",
    companiesLabel: "Anbieter",
    clearFilterLabel: "Filter loschen",
    countryLabel: "Land",
    selectedCountryLabel: "Ausgewahltes Land",
    cityTownLabel: "Stadt oder Ort",
    cityExamplePlaceholder: "z. B. Rom",
    numberDaysLabel: "Anzahl der Tage",
    tripStartDateOptionalLabel: "Reisestart (optional)",
    checkInLabel: "Check-in",
    checkOutLabel: "Check-out",
    whereLabel: "Ziel",
    datesLabel: "Daten",
    guestsLabel: "Gaste",
    searchPlaceholder: "Stadt, Flughafen, Hotel oder Wahrzeichen",
    countrySearchPlaceholder: "Land suchen oder auswahlen...",
    searchInContinentPlaceholder: "In {continent} suchen...",
    selectFromListBelowPlaceholder: "Unten aus der Liste auswahlen",
    temporarilyLockedLabel: "Vorubergehend gesperrt",
    lockedLabel: "Gesperrt",
    searchLabel: "Suchen",
    doneLabel: "Fertig",
    prevLabel: "Zuruck",
    nextLabel: "Weiter",
    continentAmericas: "N/S Amerika",
    continentEurope: "Europa",
    continentAsia: "Asien",
    continentOceania: "Ozeanien",
    continentAfrica: "Afrika",
    budgetOptionBudget: "Gunstig",
    budgetOptionMidRange: "Mittelklasse",
    budgetOptionLuxury: "Luxus",
    interestCulture: "Kultur",
    interestNature: "Natur",
    interestFood: "Essen",
    interestNightlife: "Nachtleben",
    interestShopping: "Shopping",
    interestBeaches: "Strande",
    interestMuseums: "Museen",
    interestHistory: "Geschichte",
    interestHiking: "Wandern",
    interestAdventure: "Abenteuer",
    interestFamily: "Familie",
    interestRomance: "Romantik",
    interestPhotography: "Fotografie",
    interestWildlife: "Tierwelt",
    interestWellness: "Wellness",
    interestFestivals: "Festivals",
    interestSports: "Sport",
    interestWine: "Wein",
    interestRoadTrips: "Roadtrips",
    interestWinterSports: "Wintersport",
  },
  it: {
    modeSingle: "Citta singola",
    modeMulti: "Multi-citta",
    modeRoad: "Viaggio on the road",
    modeInterrail: "InterRail",
    step1: "Passo 1",
    step2: "Passo 2",
    step3: "Passo 3",
    setDestination: "Imposta destinazione",
    chooseTripStyle: "Scegli stile di viaggio",
    styleHint: "Ritmo, budget e fino a 4 interessi",
    generate: "Genera",
    generateHint: "Itinerario AI con meteo live e ristoranti vicini",
    selectedNow: "Selezione attuale",
    destination: "Destinazione",
    budget: "Budget",
    interests: "Interessi",
    notSelected: "Non selezionato",
    paceQuestion: "Qual e il tuo ritmo?",
    paceRelaxed: "Rilassato",
    paceBalanced: "Bilanciato",
    paceAction: "Intenso",
    selectInterestsHint: "Seleziona fino a 4 interessi",
    generating: "Generazione in corso...",
    generateItinerary: "Genera itinerario",
    extraNotes: "Note extra",
    extraNotesPlaceholder: "Inserisci attrazioni o temi specifici non coperti sopra",
    itineraryProgressTitle: "Itinerario in corso!",
    itineraryProgressBody: "Il tuo itinerario sara pronto tra pochi istanti.",
    airportToCity: "Aeroporto al Centro",
    uponArrival: "All'arrivo",
    airportsLabel: "Aeroporti",
    trainsLabel: "Treni",
    metroLabel: "Metro",
    busServicesLabel: "Servizi bus",
    expressServicesLabel: "Servizi express",
    cityRoutesLabel: "Linee urbane/locali",
    taxisLabel: "Taxi",
    taxiRanksLabel: "Posteggi taxi",
    costLabel: "Costo",
    prebookLabel: "Prenota prima",
    hotelShuttlesLabel: "Navette hotel",
    carRentalLabel: "Noleggio auto",
    locationLabel: "Posizione",
    companiesLabel: "Aziende",
    clearFilterLabel: "Cancella filtro",
    countryLabel: "Paese",
    selectedCountryLabel: "Paese selezionato",
    cityTownLabel: "Citta o paese",
    cityExamplePlaceholder: "es.: Roma",
    numberDaysLabel: "Numero di giorni",
    tripStartDateOptionalLabel: "Data inizio viaggio (opzionale)",
    checkInLabel: "Check-in",
    checkOutLabel: "Check-out",
    whereLabel: "Destinazione",
    datesLabel: "Date",
    guestsLabel: "Ospiti",
    searchPlaceholder: "Citta, aeroporto, hotel o punto di interesse",
    countrySearchPlaceholder: "Cerca o seleziona un paese...",
    searchInContinentPlaceholder: "Cerca in {continent}...",
    selectFromListBelowPlaceholder: "Seleziona dalla lista qui sotto",
    temporarilyLockedLabel: "Temporaneamente bloccato",
    lockedLabel: "Bloccato",
    searchLabel: "Cerca",
    doneLabel: "Fatto",
    prevLabel: "Prec.",
    nextLabel: "Succ.",
    continentAmericas: "N/S America",
    continentEurope: "Europa",
    continentAsia: "Asia",
    continentOceania: "Oceania",
    continentAfrica: "Africa",
    budgetOptionBudget: "Economico",
    budgetOptionMidRange: "Intermedio",
    budgetOptionLuxury: "Lusso",
    interestCulture: "Cultura",
    interestNature: "Natura",
    interestFood: "Gastronomia",
    interestNightlife: "Vita notturna",
    interestShopping: "Shopping",
    interestBeaches: "Spiagge",
    interestMuseums: "Musei",
    interestHistory: "Storia",
    interestHiking: "Escursionismo",
    interestAdventure: "Avventura",
    interestFamily: "Famiglia",
    interestRomance: "Romantico",
    interestPhotography: "Fotografia",
    interestWildlife: "Fauna",
    interestWellness: "Benessere",
    interestFestivals: "Festival",
    interestSports: "Sport",
    interestWine: "Vino",
    interestRoadTrips: "Viaggi on the road",
    interestWinterSports: "Sport invernali",
  },
  'pt-BR': {
    modeSingle: "Cidade unica",
    modeMulti: "Multi-cidade",
    modeRoad: "Viagem de estrada",
    modeInterrail: "InterRail",
    step1: "Passo 1",
    step2: "Passo 2",
    step3: "Passo 3",
    setDestination: "Definir destino",
    chooseTripStyle: "Escolher estilo da viagem",
    styleHint: "Ritmo, orcamento e ate 4 interesses",
    generate: "Gerar",
    generateHint: "Roteiro com IA, clima ao vivo e restaurantes proximos",
    selectedNow: "Selecionado agora",
    destination: "Destino",
    budget: "Orcamento",
    interests: "Interesses",
    notSelected: "Nao selecionado",
    paceQuestion: "Qual e o seu ritmo?",
    paceRelaxed: "Relaxado",
    paceBalanced: "Equilibrado",
    paceAction: "Intenso",
    selectInterestsHint: "Selecione ate 4 interesses",
    generating: "Gerando...",
    generateItinerary: "Gerar roteiro",
    extraNotes: "Observacoes extras",
    extraNotesPlaceholder: "Digite atracoes ou temas especificos nao cobertos acima",
    itineraryProgressTitle: "Roteiro em andamento!",
    itineraryProgressBody: "Seu roteiro estara pronto em alguns instantes.",
    airportToCity: "Aeroporto para o Centro",
    uponArrival: "Na chegada",
    airportsLabel: "Aeroportos",
    trainsLabel: "Trens",
    metroLabel: "Metro",
    busServicesLabel: "Servicos de onibus",
    expressServicesLabel: "Servicos expressos",
    cityRoutesLabel: "Rotas urbanas/locais",
    taxisLabel: "Taxis",
    taxiRanksLabel: "Pontos de taxi",
    costLabel: "Custo",
    prebookLabel: "Reserva antecipada",
    hotelShuttlesLabel: "Traslados de hotel",
    carRentalLabel: "Aluguel de carro",
    locationLabel: "Local",
    companiesLabel: "Empresas",
    clearFilterLabel: "Limpar filtro",
    countryLabel: "Pais",
    selectedCountryLabel: "Pais selecionado",
    cityTownLabel: "Cidade ou vila",
    cityExamplePlaceholder: "ex.: Rio de Janeiro",
    numberDaysLabel: "Numero de dias",
    tripStartDateOptionalLabel: "Data de inicio da viagem (opcional)",
    checkInLabel: "Check-in",
    checkOutLabel: "Check-out",
    whereLabel: "Destino",
    datesLabel: "Datas",
    guestsLabel: "Hospedes",
    searchPlaceholder: "Cidade, aeroporto, hotel ou ponto turistico",
    countrySearchPlaceholder: "Pesquise ou selecione um pais...",
    searchInContinentPlaceholder: "Pesquisar em {continent}...",
    selectFromListBelowPlaceholder: "Selecione na lista abaixo",
    temporarilyLockedLabel: "Temporariamente bloqueado",
    lockedLabel: "Bloqueado",
    searchLabel: "Buscar",
    doneLabel: "Concluir",
    prevLabel: "Anterior",
    nextLabel: "Proximo",
    continentAmericas: "N/S America",
    continentEurope: "Europa",
    continentAsia: "Asia",
    continentOceania: "Oceania",
    continentAfrica: "Africa",
    budgetOptionBudget: "Economico",
    budgetOptionMidRange: "Intermediario",
    budgetOptionLuxury: "Luxo",
    interestCulture: "Cultura",
    interestNature: "Natureza",
    interestFood: "Gastronomia",
    interestNightlife: "Vida noturna",
    interestShopping: "Compras",
    interestBeaches: "Praias",
    interestMuseums: "Museus",
    interestHistory: "Historia",
    interestHiking: "Trilhas",
    interestAdventure: "Aventura",
    interestFamily: "Familia",
    interestRomance: "Romance",
    interestPhotography: "Fotografia",
    interestWildlife: "Vida selvagem",
    interestWellness: "Bem-estar",
    interestFestivals: "Festivais",
    interestSports: "Esportes",
    interestWine: "Vinhos",
    interestRoadTrips: "Viagens de estrada",
    interestWinterSports: "Esportes de inverno",
  },
  'pt-PT': {
    modeSingle: "Cidade unica",
    modeMulti: "Multi-cidade",
    modeRoad: "Viagem de estrada",
    modeInterrail: "InterRail",
    step1: "Passo 1",
    step2: "Passo 2",
    step3: "Passo 3",
    setDestination: "Definir destino",
    chooseTripStyle: "Escolher estilo da viagem",
    styleHint: "Ritmo, orcamento e ate 4 interesses",
    generate: "Gerar",
    generateHint: "Roteiro com IA, clima ao vivo e restaurantes proximos",
    selectedNow: "Selecionado agora",
    destination: "Destino",
    budget: "Orcamento",
    interests: "Interesses",
    notSelected: "Nao selecionado",
    paceQuestion: "Qual e o teu ritmo?",
    paceRelaxed: "Relaxado",
    paceBalanced: "Equilibrado",
    paceAction: "Intenso",
    selectInterestsHint: "Seleciona ate 4 interesses",
    generating: "A gerar...",
    generateItinerary: "Gerar roteiro",
    extraNotes: "Notas extra",
    extraNotesPlaceholder: "Introduz atracoes ou temas especificos nao cobertos acima",
    itineraryProgressTitle: "Roteiro em progresso!",
    itineraryProgressBody: "O seu roteiro estara pronto dentro de instantes.",
    airportToCity: "Aeroporto para o Centro",
    uponArrival: "A chegada",
    airportsLabel: "Aeroportos",
    trainsLabel: "Comboios",
    metroLabel: "Metro",
    busServicesLabel: "Servicos de autocarro",
    expressServicesLabel: "Servicos expresso",
    cityRoutesLabel: "Rotas urbanas/locais",
    taxisLabel: "Taxis",
    taxiRanksLabel: "Pracas de taxi",
    costLabel: "Custo",
    prebookLabel: "Pre-reserva",
    hotelShuttlesLabel: "Shuttles de hotel",
    carRentalLabel: "Aluguer de carro",
    locationLabel: "Localizacao",
    companiesLabel: "Empresas",
    clearFilterLabel: "Limpar filtro",
    countryLabel: "Pais",
    selectedCountryLabel: "Pais selecionado",
    cityTownLabel: "Cidade ou vila",
    cityExamplePlaceholder: "ex.: Lisboa",
    numberDaysLabel: "Numero de dias",
    tripStartDateOptionalLabel: "Data de inicio da viagem (opcional)",
    checkInLabel: "Check-in",
    checkOutLabel: "Check-out",
    whereLabel: "Destino",
    datesLabel: "Datas",
    guestsLabel: "Hospedes",
    searchPlaceholder: "Cidade, aeroporto, hotel ou ponto de interesse",
    countrySearchPlaceholder: "Pesquise ou selecione um pais...",
    searchInContinentPlaceholder: "Pesquisar em {continent}...",
    selectFromListBelowPlaceholder: "Selecione na lista abaixo",
    temporarilyLockedLabel: "Temporariamente bloqueado",
    lockedLabel: "Bloqueado",
    searchLabel: "Pesquisar",
    doneLabel: "Concluir",
    prevLabel: "Anterior",
    nextLabel: "Proximo",
    continentAmericas: "N/S America",
    continentEurope: "Europa",
    continentAsia: "Asia",
    continentOceania: "Oceania",
    continentAfrica: "Africa",
    budgetOptionBudget: "Economico",
    budgetOptionMidRange: "Intermedio",
    budgetOptionLuxury: "Luxo",
    interestCulture: "Cultura",
    interestNature: "Natureza",
    interestFood: "Gastronomia",
    interestNightlife: "Vida noturna",
    interestShopping: "Compras",
    interestBeaches: "Praias",
    interestMuseums: "Museus",
    interestHistory: "Historia",
    interestHiking: "Caminhadas",
    interestAdventure: "Aventura",
    interestFamily: "Familia",
    interestRomance: "Romance",
    interestPhotography: "Fotografia",
    interestWildlife: "Vida selvagem",
    interestWellness: "Bem-estar",
    interestFestivals: "Festivais",
    interestSports: "Desporto",
    interestWine: "Vinhos",
    interestRoadTrips: "Viagens de estrada",
    interestWinterSports: "Desportos de inverno",
  },
  pt: {
    modeSingle: "Cidade unica",
    modeMulti: "Multi-cidade",
    modeRoad: "Viagem de estrada",
    modeInterrail: "InterRail",
    step1: "Passo 1",
    step2: "Passo 2",
    step3: "Passo 3",
    setDestination: "Definir destino",
    chooseTripStyle: "Escolher estilo da viagem",
    styleHint: "Ritmo, orcamento e ate 4 interesses",
    generate: "Gerar",
    generateHint: "Roteiro com IA, clima ao vivo e restaurantes proximos",
    selectedNow: "Selecionado agora",
    destination: "Destino",
    budget: "Orcamento",
    interests: "Interesses",
    notSelected: "Nao selecionado",
    paceQuestion: "Qual e o teu ritmo?",
    paceRelaxed: "Relaxado",
    paceBalanced: "Equilibrado",
    paceAction: "Intenso",
    selectInterestsHint: "Seleciona ate 4 interesses",
    generating: "A gerar...",
    generateItinerary: "Gerar roteiro",
    extraNotes: "Notas extra",
    extraNotesPlaceholder: "Introduz atracoes ou temas especificos nao cobertos acima",
    itineraryProgressTitle: "Roteiro em progresso!",
    itineraryProgressBody: "O seu roteiro estara pronto dentro de instantes.",
    airportToCity: "Aeroporto para o Centro",
    uponArrival: "A chegada",
    airportsLabel: "Aeroportos",
    trainsLabel: "Comboios",
    metroLabel: "Metro",
    busServicesLabel: "Servicos de autocarro",
    expressServicesLabel: "Servicos expresso",
    cityRoutesLabel: "Rotas urbanas/locais",
    taxisLabel: "Taxis",
    taxiRanksLabel: "Pracas de taxi",
    costLabel: "Custo",
    prebookLabel: "Pre-reserva",
    hotelShuttlesLabel: "Shuttles de hotel",
    carRentalLabel: "Aluguer de carro",
    locationLabel: "Localizacao",
    companiesLabel: "Empresas",
    clearFilterLabel: "Limpar filtro",
    countryLabel: "Pais",
    selectedCountryLabel: "Pais selecionado",
    cityTownLabel: "Cidade ou vila",
    cityExamplePlaceholder: "ex.: Lisboa",
    numberDaysLabel: "Numero de dias",
    tripStartDateOptionalLabel: "Data de inicio da viagem (opcional)",
    checkInLabel: "Check-in",
    checkOutLabel: "Check-out",
    whereLabel: "Destino",
    datesLabel: "Datas",
    guestsLabel: "Hospedes",
    searchPlaceholder: "Cidade, aeroporto, hotel ou ponto de interesse",
    countrySearchPlaceholder: "Pesquise ou selecione um pais...",
    searchInContinentPlaceholder: "Pesquisar em {continent}...",
    selectFromListBelowPlaceholder: "Selecione na lista abaixo",
    temporarilyLockedLabel: "Temporariamente bloqueado",
    lockedLabel: "Bloqueado",
    searchLabel: "Pesquisar",
    doneLabel: "Concluir",
    prevLabel: "Anterior",
    nextLabel: "Proximo",
    continentAmericas: "N/S America",
    continentEurope: "Europa",
    continentAsia: "Asia",
    continentOceania: "Oceania",
    continentAfrica: "Africa",
    budgetOptionBudget: "Economico",
    budgetOptionMidRange: "Intermedio",
    budgetOptionLuxury: "Luxo",
    interestCulture: "Cultura",
    interestNature: "Natureza",
    interestFood: "Gastronomia",
    interestNightlife: "Vida noturna",
    interestShopping: "Compras",
    interestBeaches: "Praias",
    interestMuseums: "Museus",
    interestHistory: "Historia",
    interestHiking: "Caminhadas",
    interestAdventure: "Aventura",
    interestFamily: "Familia",
    interestRomance: "Romance",
    interestPhotography: "Fotografia",
    interestWildlife: "Vida selvagem",
    interestWellness: "Bem-estar",
    interestFestivals: "Festivais",
    interestSports: "Desporto",
    interestWine: "Vinhos",
    interestRoadTrips: "Viagens de estrada",
    interestWinterSports: "Desportos de inverno",
  },
  ja: {
    modeSingle: "1都市",
    modeMulti: "複数都市",
    modeRoad: "ロードトリップ",
    modeInterrail: "インターレール",
    step1: "ステップ1",
    step2: "ステップ2",
    step3: "ステップ3",
    setDestination: "目的地を設定",
    chooseTripStyle: "旅行スタイルを選択",
    styleHint: "ペース、予算、興味を最大4つまで",
    generate: "作成",
    generateHint: "AI旅程、現地天気、周辺グルメ",
    selectedNow: "現在の設定",
    destination: "目的地",
    budget: "予算",
    interests: "興味・関心",
    notSelected: "未選択",
    paceQuestion: "どんなペースで旅行したいですか？",
    paceRelaxed: "ゆったり",
    paceBalanced: "バランス重視",
    paceAction: "アクティブ重視",
    selectInterestsHint: "興味・関心を最大4つまで選択",
    generating: "生成中...",
    generateItinerary: "旅程を生成",
    extraNotes: "追加の要望",
    extraNotesPlaceholder: "上記にない具体的な観光地やテーマをご入力ください",
    itineraryProgressTitle: "旅程を作成中です！",
    itineraryProgressBody: "まもなく旅程が完成します。",
    airportToCity: "空港から市内中心部へ",
    uponArrival: "到着時",
    airportsLabel: "空港",
    trainsLabel: "鉄道",
    metroLabel: "地下鉄",
    busServicesLabel: "バス",
    expressServicesLabel: "急行便",
    cityRoutesLabel: "市内/ローカル路線",
    taxisLabel: "タクシー",
    taxiRanksLabel: "タクシー乗り場",
    costLabel: "料金",
    prebookLabel: "事前予約",
    hotelShuttlesLabel: "ホテルシャトル",
    carRentalLabel: "レンタカー",
    locationLabel: "場所",
    companiesLabel: "運営会社",
    clearFilterLabel: "フィルター解除",
    countryLabel: "国",
    selectedCountryLabel: "選択した国",
    cityTownLabel: "都市または町",
    cityExamplePlaceholder: "例: 東京",
    numberDaysLabel: "日数",
    tripStartDateOptionalLabel: "旅行開始日（任意）",
    checkInLabel: "チェックイン",
    checkOutLabel: "チェックアウト",
    whereLabel: "目的地",
    datesLabel: "日付",
    guestsLabel: "宿泊人数",
    searchPlaceholder: "都市、空港、ホテル、観光地",
    countrySearchPlaceholder: "国を検索または選択...",
    searchInContinentPlaceholder: "{continent}で検索...",
    selectFromListBelowPlaceholder: "下のリストから選択",
    temporarilyLockedLabel: "一時的に利用不可",
    lockedLabel: "利用不可",
    searchLabel: "検索",
    doneLabel: "完了",
    prevLabel: "前へ",
    nextLabel: "次へ",
    continentAmericas: "南北アメリカ",
    continentEurope: "ヨーロッパ",
    continentAsia: "アジア",
    continentOceania: "オセアニア",
    continentAfrica: "アフリカ",
    budgetOptionBudget: "節約重視",
    budgetOptionMidRange: "標準",
    budgetOptionLuxury: "ラグジュアリー",
    interestCulture: "文化",
    interestNature: "自然",
    interestFood: "グルメ",
    interestNightlife: "ナイトライフ",
    interestShopping: "ショッピング",
    interestBeaches: "ビーチ",
    interestMuseums: "美術館・博物館",
    interestHistory: "歴史",
    interestHiking: "ハイキング",
    interestAdventure: "アドベンチャー",
    interestFamily: "ファミリー",
    interestRomance: "ロマンス",
    interestPhotography: "写真",
    interestWildlife: "野生動物",
    interestWellness: "ウェルネス",
    interestFestivals: "フェスティバル",
    interestSports: "スポーツ",
    interestWine: "ワイン",
    interestRoadTrips: "ロードトリップ",
    interestWinterSports: "ウィンタースポーツ",
  },
};

const getPlannerText = (language = "en") => {
  if (PLANNER_TEXT[language]) return PLANNER_TEXT[language];
  const base = String(language || "").toLowerCase().split("-")[0];
  return PLANNER_TEXT[base] || PLANNER_TEXT.en;
};

const LANGUAGE_NAMES = {
  en: "English",
  es: "Spanish",
  fr: "French",
  de: "German",
  it: "Italian",
  ja: "Japanese",
  pt: "Portuguese",
  "pt-BR": "Brazilian Portuguese",
  "pt-PT": "European Portuguese",
};

const resolveOutputLanguage = (language = "en") => {
  if (LANGUAGE_NAMES[language]) return LANGUAGE_NAMES[language];
  const base = String(language || "").toLowerCase().split("-")[0];
  return LANGUAGE_NAMES[base] || "English";
};

const resolveOutputStyleInstruction = (language = "en") => {
  const normalized = String(language || "").toLowerCase();
  if (normalized === "ja" || normalized.startsWith("ja-")) {
    return "For Japanese output, use formal polite Japanese (丁寧語, です/ます調). Avoid casual/slang tone.";
  }
  return "";
};

const clampSingleTripDays = (value) => Math.min(ITINERARY_DAY_LIMIT, Math.max(1, Number(value) || 1));

const normalizeHistoryText = (value = "") =>
  String(value || "")
    .toLowerCase()
    .replace(/[^a-z0-9\s]/g, " ")
    .replace(/\s+/g, " ")
    .trim();

const buildActivityHistoryKey = ({ title = "", location = "", category = "" } = {}) => {
  const t = normalizeHistoryText(title);
  const l = normalizeHistoryText(location);
  const c = normalizeHistoryText(category);
  return [t, l, c].filter(Boolean).join("|");
};

const getOrCreatePlannerUserId = () => {
  if (typeof window === "undefined") return "";
  const storageKey = "appi.user.id.v1";
  const existing = String(window.localStorage.getItem(storageKey) || "").trim();
  if (existing) return existing;

  const generated =
    typeof crypto !== "undefined" && typeof crypto.randomUUID === "function"
      ? crypto.randomUUID()
      : `u_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;

  window.localStorage.setItem(storageKey, generated);
  return generated;
};

const callItineraryHistoryApi = async (payload) => {
  const call = async (url) => {
    const response = await fetch(url, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    });

    let data = {};
    try {
      data = await response.json();
    } catch {
      data = {};
    }

    return { response, data };
  };

  const onCustomDomain =
    typeof window !== "undefined" &&
    /appiholidays\.com$/i.test(window.location.hostname) &&
    !/vercel\.app$/i.test(window.location.hostname);

  let result = await call(`${API_BASE}/api/itinerary-history`);
  if (!result.response.ok && onCustomDomain && process.env.NODE_ENV === "production") {
    result = await call(`${PROD_API_FALLBACK}/api/itinerary-history`);
  }

  return result;
};

const fetchItineraryHistory = async ({ userId = "", city = "", country = "" }) => {
  if (!userId) return { excludeActivityKeys: [], excludeActivityLabels: [] };

  try {
    const { response, data } = await callItineraryHistoryApi({
      action: "fetch",
      userId,
      destination: city,
      country,
      lookbackDays: ITINERARY_HISTORY_LOOKBACK_DAYS,
      maxItems: 300,
    });

    if (!response.ok) return { excludeActivityKeys: [], excludeActivityLabels: [] };

    return {
      excludeActivityKeys: Array.isArray(data?.excludeActivityKeys) ? data.excludeActivityKeys.filter(Boolean) : [],
      excludeActivityLabels: Array.isArray(data?.excludeActivityLabels) ? data.excludeActivityLabels.filter(Boolean) : [],
    };
  } catch {
    return { excludeActivityKeys: [], excludeActivityLabels: [] };
  }
};

const persistItineraryHistory = async ({ userId = "", city = "", country = "", activities = [] }) => {
  if (!userId || !Array.isArray(activities) || activities.length === 0) return;

  try {
    await callItineraryHistoryApi({
      action: "record",
      userId,
      destination: city,
      country,
      activities: activities.map((item) => ({
        title: String(item?.title || "").trim(),
        location: String(item?.location || "").trim(),
        category: String(item?.category || "").trim(),
      })),
    });
  } catch {
    // Non-blocking by design.
  }
};

const dedupeItineraryActivities = (days = [], blockedActivityKeys = new Set()) => {
  const seenInRun = new Set();
  let removedCount = 0;

  const nextDays = (Array.isArray(days) ? days : []).map((day) => {
    const originalActivities = Array.isArray(day?.activities) ? day.activities : [];
    const kept = [];

    originalActivities.forEach((activity) => {
      const key = buildActivityHistoryKey({
        title: activity?.title,
        location: activity?.location,
        category: activity?.category,
      });

      if (!key) {
        kept.push(activity);
        return;
      }

      if (blockedActivityKeys.has(key) || seenInRun.has(key)) {
        removedCount += 1;
        return;
      }

      seenInRun.add(key);
      kept.push(activity);
    });

    const safeActivities = kept.length > 0 ? kept : originalActivities.slice(0, 1);
    return { ...day, activities: safeActivities };
  });

  return { days: nextDays, removedCount };
};

// --- 1. CORE GEOGRAPHICAL DATA (STATIC) ---
const DESTINATION_DATA = {
  Americas: [
    "Argentina",
    "Bahamas",
    "Bolivia",
    "Brazil",
    "Canada",
    "Chile",
    "Colombia",
    "Costa Rica",
    "Cuba",
    "Dominican Republic",
    "Ecuador",
    "Jamaica",
    "Mexico",
    "Panama",
    "Peru",
    "United States",
    "Venezuela",
  ],
  Europe: [
    "Albania",
    "Andorra",
    "Austria",
    "Belarus",
    "Belgium",
    "Bulgaria",
    "Croatia",
    "Czechia",
    "Denmark",
    "Estonia",
    "Finland",
    "France",
    "Germany",
    "Greece",
    "Hungary",
    "Iceland",
    "Ireland",
    "Italy",
    "Latvia",
    "Lithuania",
    "Netherlands",
    "Norway",
    "Poland",
    "Portugal",
    "Romania",
    "Russia",
    "Serbia",
    "Spain",
    "Sweden",
    "Switzerland",
    "Turkey",
    "Ukraine",
    "United Kingdom",
  ],
  Asia: [
    "Afghanistan",
    "Armenia",
    "Azerbaijan",
    "Bahrain",
    "Bangladesh",
    "Bhutan",
    "Brunei",
    "Cambodia",
    "China",
    "India",
    "Indonesia",
    "Iran",
    "Iraq",
    "Israel",
    "Japan",
    "Jordan",
    "Kazakhstan",
    "Kuwait",
    "Kyrgyzstan",
    "Laos",
    "Lebanon",
    "Malaysia",
    "Maldives",
    "Mongolia",
    "Myanmar",
    "Nepal",
    "Oman",
    "Pakistan",
    "Philippines",
    "Qatar",
    "Saudi Arabia",
    "Singapore",
    "South Korea",
    "Sri Lanka",
    "Syria",
    "Taiwan",
    "Tajikistan",
    "Thailand",
    "Turkmenistan",
    "United Arab Emirates",
    "Uzbekistan",
    "Vietnam",
    "Yemen",
  ],
  Oceania: [
    "Australia",
    "Fiji",
    "Kiribati",
    "Marshall Islands",
    "Micronesia (F.S.)",
    "Nauru",
    "New Zealand",
    "Palau",
    "Papua New Guinea",
    "Samoa",
    "Solomon Islands",
    "Tonga",
    "Tuvalu",
    "Vanuatu",
  ],
  Africa: [
    "Algeria",
    "Angola",
    "Cameroon",
    "Egypt",
    "Ethiopia",
    "Ghana",
    "Kenya",
    "Madagascar",
    "Mauritius",
    "Morocco",
    "Mozambique",
    "Namibia",
    "Nigeria",
    "Rwanda",
    "Senegal",
    "South Africa",
    "Tanzania",
    "Tunisia",
    "Zambia",
    "Zimbabwe",
  ],
};

// Map of internal data keys to user-friendly display names
const CONTINENT_DISPLAY_MAP = {
  Americas: "N/S America",
  Europe: "Europe",
  Asia: "Asia",
  Oceania: "Oceania",
  Africa: "Africa",
};

// Array of all continent keys and all country names
const allContinentsKeys = Object.keys(DESTINATION_DATA);
const allCountries = Object.values(DESTINATION_DATA).flat();
const REGENERATE_NOTE = "Tip: Please regenerate your itinerary, and we will try again with a cleaner response.";
const ROAD_TRIP_FEATURES = [
  "Camping sites",
  "Glamping sites",
  "Hotels",
  "B&Bs",
  "Campervan stopovers",
  "Motorbike-friendly stops",
  "EV charging stations",
  "HVO fuel stops",
  "Coastal activities",
  "Mountain activities",
];
const INTERRAIL_EXCLUDED_FEATURES = new Set([
  "Campervan stopovers",
  "Motorbike-friendly stops",
  "EV charging stations",
  "HVO fuel stops",
]);
const INTERRAIL_DEFAULT_FEATURES = ["Hotels", "B&Bs", "Places of interest"];
const ANALYTICS_STORAGE_KEY = "appi.admin.analytics.v1";
const INSTALL_PROMPT_DISMISSED_KEY = "appi.install.prompt.dismissed.v1";
const INSTALL_PROMPT_TRACK_SESSION_KEY = "appi.install.prompt.shown.v1";
const INSTALL_COMPLETED_TRACK_KEY = "appi.install.completed.tracked.v1";
const MAX_RECENT_INSTALL_EVENTS = 25;
const ADMIN_INSIGHTS_POLL_MS = 15000;

const fetchAdminAuthJson = async (method = "GET", body) => {
  const response = await fetch(`${API_BASE}/api/admin-auth`, {
    method,
    credentials: "include",
    headers: body ? { "Content-Type": "application/json" } : undefined,
    body: body ? JSON.stringify(body) : undefined,
  });

  let data;
  try {
    data = await response.json();
  } catch {
    data = { error: "Non-JSON response from admin auth endpoint" };
  }

  return { response, data };
};

const fetchAdminInsightsJson = async () => {
  const response = await fetch(`${API_BASE}/api/admin-insights`, {
    method: "GET",
    credentials: "include",
  });

  let data;
  try {
    data = await response.json();
  } catch {
    data = { error: "Non-JSON response from admin insights endpoint" };
  }

  return { response, data };
};

const fetchAdminDealSearchJson = async ({ city = "", country = "", checkIn = "", checkOut = "", adults = 2, maxResults = 20 } = {}) => {
  const params = new URLSearchParams();
  if (city) params.set("city", String(city).trim());
  if (country) params.set("country", String(country).trim());
  if (checkIn) params.set("checkIn", String(checkIn).trim());
  if (checkOut) params.set("checkOut", String(checkOut).trim());
  params.set("adults", String(Math.max(1, Number(adults) || 2)));
  params.set("maxResults", String(Math.max(5, Math.min(50, Number(maxResults) || 20))));

  const response = await fetch(`${API_BASE}/api/deal-search?${params.toString()}`, {
    method: "GET",
    credentials: "include",
  });

  let data;
  try {
    data = await response.json();
  } catch {
    data = { error: "Non-JSON response from deal search endpoint" };
  }

  return { response, data };
};

const postInstallAnalyticsEvent = async (eventType, payload = {}) => {
  try {
    await fetch(`${API_BASE}/api/admin-insights`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        eventType,
        source: String(payload.source || "install_shell"),
        outcome: String(payload.outcome || ""),
        path: typeof window !== "undefined" ? window.location.pathname : "",
      }),
    });
  } catch {
    // Best-effort tracking only.
  }
};

const detectClientDeviceType = () => {
  if (typeof navigator === "undefined") return "unknown";
  const ua = String(navigator.userAgent || "").toLowerCase();
  if (/mobile|iphone|android|ipad|ipod|windows phone|blackberry|opera mini/i.test(ua)) return "mobile";
  return "desktop";
};

const postVisitorSessionEvent = async (payload = {}) => {
  try {
    await fetch(`${API_BASE}/api/admin-insights`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        eventType: "visitor_session",
        source: String(payload.source || "public_planner"),
        deviceType: detectClientDeviceType(),
        path: typeof window !== "undefined" ? window.location.pathname : "",
      }),
    });
  } catch {
    // Best-effort tracking only.
  }
};

const createEmptyAnalytics = () => ({
  overview: {
    pageViews: 0,
    singleGenerations: 0,
    multiGenerations: 0,
    roadGenerations: 0,
    accommodationClicks: 0,
    routeMapOpens: 0,
    poiDirections: 0,
    roomBookingsConfirmed: 0,
    bookingRevenue: 0,
    installPromptShown: 0,
    installClicked: 0,
    installCompleted: 0,
  },
  daily: {},
  modeUsage: { single: 0, multi: 0, road: 0 },
  bookingClicksByCity: {},
  recentInstallEvents: [],
  lastUpdated: null,
});

const getAnalyticsDayKey = (date = new Date()) => date.toISOString().slice(0, 10);

const readAnalyticsSnapshot = () => {
  if (typeof window === "undefined") return createEmptyAnalytics();

  try {
    const raw = window.localStorage.getItem(ANALYTICS_STORAGE_KEY);
    if (!raw) return createEmptyAnalytics();
    const parsed = JSON.parse(raw);
    return {
      ...createEmptyAnalytics(),
      ...parsed,
      overview: { ...createEmptyAnalytics().overview, ...(parsed?.overview || {}) },
      modeUsage: { ...createEmptyAnalytics().modeUsage, ...(parsed?.modeUsage || {}) },
      daily: parsed?.daily || {},
      bookingClicksByCity: parsed?.bookingClicksByCity || {},
      recentInstallEvents: Array.isArray(parsed?.recentInstallEvents) ? parsed.recentInstallEvents : [],
    };
  } catch {
    return createEmptyAnalytics();
  }
};

const writeAnalyticsSnapshot = (snapshot) => {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(ANALYTICS_STORAGE_KEY, JSON.stringify(snapshot));
  } catch {}
};

const recordAnalyticsEvent = (type, payload = {}) => {
  const snapshot = readAnalyticsSnapshot();
  const dayKey = getAnalyticsDayKey();
  const dayBucket = snapshot.daily[dayKey] || {
    visits: 0,
    single: 0,
    multi: 0,
    road: 0,
    bookingClicks: 0,
    confirmed: 0,
    installPromptShown: 0,
    installClicked: 0,
    installCompleted: 0,
  };

  const appendInstallEvent = (eventType, details = {}) => {
    const event = {
      eventType,
      createdAt: new Date().toISOString(),
      source: String(details.source || "install_shell"),
      outcome: String(details.outcome || ""),
    };
    const existing = Array.isArray(snapshot.recentInstallEvents) ? snapshot.recentInstallEvents : [];
    snapshot.recentInstallEvents = [event, ...existing].slice(0, MAX_RECENT_INSTALL_EVENTS);
  };

  switch (type) {
    case "page_view":
      snapshot.overview.pageViews += 1;
      dayBucket.visits += 1;
      break;
    case "generate_single_success":
      snapshot.overview.singleGenerations += 1;
      snapshot.modeUsage.single += 1;
      dayBucket.single += 1;
      break;
    case "generate_multi_success":
      snapshot.overview.multiGenerations += 1;
      snapshot.modeUsage.multi += 1;
      dayBucket.multi += 1;
      break;
    case "generate_road_success":
      snapshot.overview.roadGenerations += 1;
      snapshot.modeUsage.road += 1;
      dayBucket.road += 1;
      break;
    case "booking_click":
      snapshot.overview.accommodationClicks += 1;
      dayBucket.bookingClicks += 1;
      if (payload.city) {
        const cityKey = String(payload.city).trim();
        snapshot.bookingClicksByCity[cityKey] = (snapshot.bookingClicksByCity[cityKey] || 0) + 1;
      }
      break;
    case "route_map_open":
      snapshot.overview.routeMapOpens += 1;
      break;
    case "poi_direction_open":
      snapshot.overview.poiDirections += 1;
      break;
    case "booking_confirmed":
      snapshot.overview.roomBookingsConfirmed += 1;
      dayBucket.confirmed += 1;
      if (Number.isFinite(Number(payload.revenue))) {
        snapshot.overview.bookingRevenue += Number(payload.revenue);
      }
      break;
    case "app_install_prompt_shown":
      snapshot.overview.installPromptShown += 1;
      dayBucket.installPromptShown += 1;
      appendInstallEvent("app_install_prompt_shown", payload);
      break;
    case "app_install_clicked":
      snapshot.overview.installClicked += 1;
      dayBucket.installClicked += 1;
      appendInstallEvent("app_install_clicked", payload);
      break;
    case "app_install_completed":
      snapshot.overview.installCompleted += 1;
      dayBucket.installCompleted += 1;
      appendInstallEvent("app_install_completed", payload);
      break;
    default:
      break;
  }

  snapshot.daily[dayKey] = dayBucket;
  snapshot.lastUpdated = new Date().toISOString();
  writeAnalyticsSnapshot(snapshot);
  return snapshot;
};

const buildAnalyticsSummary = (snapshot) => {
  const safe = snapshot || createEmptyAnalytics();
  const now = new Date();
  const dailySeries = Array.from({ length: 7 }).map((_, index) => {
    const date = new Date(now);
    date.setDate(now.getDate() - (6 - index));
    const key = getAnalyticsDayKey(date);
    const bucket = safe.daily[key] || {};
    return {
      key,
      label: date.toLocaleDateString(undefined, { weekday: "short" }),
      visits: bucket.visits || 0,
      bookings: bucket.bookingClicks || 0,
      generated: (bucket.single || 0) + (bucket.multi || 0) + (bucket.road || 0),
    };
  });

  const topCities = Object.entries(safe.bookingClicksByCity || {})
    .sort((a, b) => b[1] - a[1])
    .slice(0, 5)
    .map(([city, clicks]) => ({ city, clicks }));

  return {
    dailySeries,
    topCities,
    totalGenerations:
      (safe.overview.singleGenerations || 0) +
      (safe.overview.multiGenerations || 0) +
      (safe.overview.roadGenerations || 0),
  };
};

function AdminDashboardPage({ analytics, installInsights, visitorInsights, bookingInsights, onBackToPlanner }) {
  const initialDealCheckIn = useMemo(() => {
    const date = new Date();
    date.setUTCDate(date.getUTCDate() + 14);
    return formatIsoDateUtc(date);
  }, []);
  const initialDealCheckOut = useMemo(() => {
    const date = new Date();
    date.setUTCDate(date.getUTCDate() + 17);
    return formatIsoDateUtc(date);
  }, []);
  const [dealCity, setDealCity] = useState("");
  const [dealCountry, setDealCountry] = useState("");
  const [dealCheckIn, setDealCheckIn] = useState(initialDealCheckIn);
  const [dealCheckOut, setDealCheckOut] = useState(initialDealCheckOut);
  const [dealAdults, setDealAdults] = useState(2);
  const [dealTypeFilter, setDealTypeFilter] = useState("all");
  const [dealResults, setDealResults] = useState([]);
  const [dealSourceUrl, setDealSourceUrl] = useState("");
  const [dealLoading, setDealLoading] = useState(false);
  const [dealError, setDealError] = useState("");

  const summary = buildAnalyticsSummary(analytics);
  const recentInstallEvents = Array.isArray(installInsights?.recentInstallEvents)
    ? installInsights.recentInstallEvents.slice(0, 12)
    : Array.isArray(analytics?.recentInstallEvents)
      ? analytics.recentInstallEvents.slice(0, 12)
      : [];
  const installCompletedTotal = Number(installInsights?.installCompletedTotal || analytics?.overview?.installCompleted || 0);
  const installClickedTotal = Number(installInsights?.installClickedTotal || analytics?.overview?.installClicked || 0);
  const installPromptShownTotal = Number(installInsights?.installPromptShownTotal || analytics?.overview?.installPromptShown || 0);
  const topInstallCitiesToday = Array.isArray(installInsights?.topInstallCitiesToday)
    ? installInsights.topInstallCitiesToday
    : Array.isArray(installInsights?.topInstallCities)
      ? installInsights.topInstallCities
      : [];
  const sharedTrackedVisitsTotal = Number(visitorInsights?.total || 0);
  const legacyLocalVisitsTotal = Number(analytics?.overview?.pageViews || 0);
  const trackedVisitsTotal = Math.max(sharedTrackedVisitsTotal, legacyLocalVisitsTotal);
  const mobileVisitsTotal = Number(visitorInsights?.mobile || 0);
  const desktopVisitsTotal = Number(visitorInsights?.desktop || 0);
  const topLocations = Array.isArray(visitorInsights?.topLocations) ? visitorInsights.topLocations : [];
  const topLocationsToday = Array.isArray(visitorInsights?.topLocationsToday) ? visitorInsights.topLocationsToday : [];
  const visibleTopLocations = topLocationsToday.length > 0 ? topLocationsToday : topLocations;
  const sharedConfirmedBookingsTotal = Number(bookingInsights?.confirmedTotal || 0);
  const legacyConfirmedBookingsTotal = Number(analytics?.overview?.roomBookingsConfirmed || 0);
  const confirmedBookingsTotal = Math.max(sharedConfirmedBookingsTotal, legacyConfirmedBookingsTotal);
  const sharedBookingRevenueTotal = Number(bookingInsights?.revenueTotal || 0);
  const legacyBookingRevenueTotal = Number(analytics?.overview?.bookingRevenue || 0);
  const bookingRevenueTotal = Math.max(sharedBookingRevenueTotal, legacyBookingRevenueTotal);

  const formatInstallEventType = (eventType) => {
    if (eventType === "app_install_completed") return "Install completed";
    if (eventType === "app_install_clicked") return "Install clicked";
    if (eventType === "app_install_prompt_shown") return "Install prompt shown";
    return "Install event";
  };

  const modeUsageEntries = [
    { key: "single", label: "Single City", value: analytics?.modeUsage?.single || 0, color: "bg-blue-500" },
    { key: "multi", label: "Multi-City", value: analytics?.modeUsage?.multi || 0, color: "bg-green-500" },
    { key: "road", label: "Road Trip", value: analytics?.modeUsage?.road || 0, color: "bg-red-500" },
  ];
  const maxModeUsage = Math.max(1, ...modeUsageEntries.map((item) => item.value));
  const maxDaily = Math.max(1, ...summary.dailySeries.map((item) => Math.max(item.visits, item.generated, item.bookings)));
  const visibleDealResults = dealTypeFilter === "all"
    ? dealResults
    : dealResults.filter((deal) => String(deal?.hotelType || "mixed") === dealTypeFilter);

  const formatHotelTypeLabel = (value) => {
    const type = String(value || "mixed").toLowerCase();
    if (type === "beach") return "Beach";
    if (type === "city") return "City";
    return "Mixed";
  };

  const getHotelTypeBadgeClass = (value) => {
    const type = String(value || "mixed").toLowerCase();
    if (type === "beach") return "border-sky-200 bg-sky-100 text-sky-800";
    if (type === "city") return "border-violet-200 bg-violet-100 text-violet-800";
    return "border-slate-200 bg-slate-100 text-slate-700";
  };

  const runDealSearch = async () => {
    setDealError("");
    setDealLoading(true);
    try {
      const { response, data } = await fetchAdminDealSearchJson({
        city: dealCity,
        country: dealCountry,
        checkIn: dealCheckIn,
        checkOut: dealCheckOut,
        adults: dealAdults,
        maxResults: 20,
      });

      if (!response.ok) {
        setDealResults([]);
        setDealSourceUrl("");
        setDealError(data?.error || "Could not fetch deals.");
        return;
      }

      setDealResults(Array.isArray(data?.deals) ? data.deals : []);
      setDealSourceUrl(String(data?.searchUrl || ""));
    } catch {
      setDealResults([]);
      setDealSourceUrl("");
      setDealError("Deal search failed. Please try again.");
    } finally {
      setDealLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <div className="text-xs font-semibold uppercase tracking-[0.2em] text-emerald-700">Admin Dashboard</div>
          <h1 className="mt-1 text-2xl font-extrabold text-slate-900">Traffic, itinerary usage, and room-booking funnel</h1>
          <p className="mt-2 max-w-3xl text-sm text-slate-600">
            This dashboard tracks live planner usage and booking intent in the browser today. Confirmed room bookings and revenue will populate automatically once the room-booking API sends confirmation callbacks into the app.
          </p>
        </div>
        <button
          type="button"
          onClick={onBackToPlanner}
          className="inline-flex items-center gap-2 rounded-xl border border-slate-300 bg-white px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50"
        >
          <ArrowLeft className="h-4 w-4" /> Back To Planner
        </button>
      </div>

      <div className="rounded-2xl border border-emerald-200 bg-white p-5 shadow-sm">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div>
            <div className="text-sm font-bold text-slate-900">Deal Search (Admin)</div>
            <div className="mt-1 text-xs text-slate-600">Pick city or country, choose any future dates, and get direct booking links.</div>
          </div>
          <button
            type="button"
            onClick={runDealSearch}
            disabled={dealLoading}
            className="inline-flex items-center rounded-xl bg-emerald-600 px-4 py-2 text-sm font-semibold text-white hover:bg-emerald-700 disabled:cursor-not-allowed disabled:bg-slate-300"
          >
            {dealLoading ? "Searching..." : "Run Deal Search"}
          </button>
        </div>

        <div className="mt-4 grid grid-cols-1 gap-3 md:grid-cols-6">
          <input
            type="text"
            value={dealCity}
            onChange={(event) => setDealCity(event.target.value)}
            placeholder="City (e.g. London)"
            className="rounded-xl border border-slate-300 px-3 py-2 text-sm"
          />
          <input
            type="text"
            value={dealCountry}
            onChange={(event) => setDealCountry(event.target.value)}
            placeholder="Country (optional)"
            className="rounded-xl border border-slate-300 px-3 py-2 text-sm"
          />
          <input
            type="date"
            value={dealCheckIn}
            onChange={(event) => setDealCheckIn(event.target.value)}
            className="rounded-xl border border-slate-300 px-3 py-2 text-sm"
          />
          <input
            type="date"
            value={dealCheckOut}
            onChange={(event) => setDealCheckOut(event.target.value)}
            className="rounded-xl border border-slate-300 px-3 py-2 text-sm"
          />
          <input
            type="number"
            min="1"
            max="8"
            value={dealAdults}
            onChange={(event) => setDealAdults(Math.max(1, Math.min(8, Number(event.target.value) || 2)))}
            className="rounded-xl border border-slate-300 px-3 py-2 text-sm"
          />
          <select
            value={dealTypeFilter}
            onChange={(event) => setDealTypeFilter(event.target.value)}
            className="rounded-xl border border-slate-300 px-3 py-2 text-sm"
          >
            <option value="all">All hotel types</option>
            <option value="beach">Beach hotels</option>
            <option value="city">City hotels</option>
            <option value="mixed">Mixed / Other</option>
          </select>
        </div>

        {dealError && (
          <div className="mt-3 rounded-xl border border-rose-200 bg-rose-50 px-3 py-2 text-xs text-rose-700">{dealError}</div>
        )}

        {dealSourceUrl && (
          <div className="mt-3 text-xs text-slate-600">
            Source search URL: <a className="font-semibold text-emerald-700 underline" href={dealSourceUrl} target="_blank" rel="noopener noreferrer">Open source search</a>
          </div>
        )}

        <div className="mt-3 text-xs text-slate-500">
          Showing {Math.min(12, visibleDealResults.length)} of {dealResults.length} deals
          {dealTypeFilter !== "all" ? ` (${formatHotelTypeLabel(dealTypeFilter)} filter)` : ""}.
        </div>

        <div className="mt-2 space-y-2 text-sm text-slate-700">
          {visibleDealResults.length > 0 ? (
            visibleDealResults.slice(0, 12).map((deal, index) => (
              <div key={`${deal.directLink}-${index}`} className="flex flex-wrap items-center justify-between gap-2 rounded-xl border border-slate-200 bg-slate-50 px-3 py-2">
                <div>
                  <div className="font-semibold text-slate-900">{index + 1}. {deal.hotelName || "Hotel"}</div>
                  <div className="text-xs text-slate-600">
                    {deal.totalPrice?.display || "Price unavailable"}
                    {deal.perNight?.display ? ` • ${deal.perNight.display}` : ""}
                    {Number.isFinite(Number(deal.starRating)) ? ` • ${deal.starRating} stars` : ""}
                  </div>
                  <div className="mt-1">
                    <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[11px] font-semibold ${getHotelTypeBadgeClass(deal.hotelType)}`}>
                      {formatHotelTypeLabel(deal.hotelType)}
                    </span>
                  </div>
                </div>
                <a
                  href={deal.directLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="rounded-lg border border-emerald-500 px-3 py-1.5 text-xs font-semibold text-emerald-700 hover:bg-emerald-50"
                >
                  Open Deal
                </a>
              </div>
            ))
          ) : (
            <div className="rounded-xl border border-dashed border-slate-200 bg-slate-50 px-3 py-4 text-xs text-slate-500">
              {dealResults.length > 0
                ? "No deals match that hotel-type filter. Try switching filter to All hotel types."
                : "No deals loaded yet. Pick city/country and dates, then run search."}
            </div>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
        <div className="rounded-2xl border border-blue-200 bg-blue-50 p-4">
          <div className="text-xs font-semibold uppercase tracking-wide text-blue-700">Tracked Visits</div>
          <div className="mt-2 text-3xl font-extrabold text-blue-900">{trackedVisitsTotal}</div>
          <div className="mt-1 text-xs text-blue-800">Shared server visits: {sharedTrackedVisitsTotal} • Legacy local visits: {legacyLocalVisitsTotal}</div>
        </div>
        <div className="rounded-2xl border border-green-200 bg-green-50 p-4">
          <div className="text-xs font-semibold uppercase tracking-wide text-green-700">Itineraries Generated</div>
          <div className="mt-2 text-3xl font-extrabold text-green-900">{summary.totalGenerations}</div>
          <div className="mt-1 text-xs text-green-800">Successful Single City, Multi-City, and Road Trip generations.</div>
        </div>
        <div className="rounded-2xl border border-amber-200 bg-amber-50 p-4">
          <div className="text-xs font-semibold uppercase tracking-wide text-amber-700">Accommodation Clicks</div>
          <div className="mt-2 text-3xl font-extrabold text-amber-900">{analytics?.overview?.accommodationClicks || 0}</div>
          <div className="mt-1 text-xs text-amber-800">Booking-intent clicks captured from room and stay cards.</div>
        </div>
        <div className="rounded-2xl border border-rose-200 bg-rose-50 p-4">
          <div className="text-xs font-semibold uppercase tracking-wide text-rose-700">Confirmed Bookings</div>
          <div className="mt-2 text-3xl font-extrabold text-rose-900">{confirmedBookingsTotal}</div>
          <div className="mt-1 text-xs text-rose-800">Ready for booking API confirmations and revenue webhooks.</div>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-4">
        <div className="rounded-2xl border border-violet-200 bg-violet-50 p-4">
          <div className="text-xs font-semibold uppercase tracking-wide text-violet-700">App Downloads (Immediate)</div>
          <div className="mt-2 text-3xl font-extrabold text-violet-900">{installClickedTotal}</div>
          <div className="mt-1 text-xs text-violet-800">Install clicks are tracked immediately across all users/devices.</div>
        </div>
        <div className="rounded-2xl border border-sky-200 bg-sky-50 p-4">
          <div className="text-xs font-semibold uppercase tracking-wide text-sky-700">Install Completed</div>
          <div className="mt-2 text-3xl font-extrabold text-sky-900">{installCompletedTotal}</div>
          <div className="mt-1 text-xs text-sky-800">Confirmed installed app opens (includes iOS standalone fallback).</div>
        </div>
        <div className="rounded-2xl border border-indigo-200 bg-indigo-50 p-4">
          <div className="text-xs font-semibold uppercase tracking-wide text-indigo-700">Install Prompts Shown</div>
          <div className="mt-2 text-3xl font-extrabold text-indigo-900">{installPromptShownTotal}</div>
          <div className="mt-1 text-xs text-indigo-800">Prompt impressions recorded once per browser session.</div>
        </div>
        <div className="rounded-2xl border border-fuchsia-200 bg-fuchsia-50 p-4">
          <div className="text-xs font-semibold uppercase tracking-wide text-fuchsia-700">Install Cities (Today)</div>
          <div className="mt-2 space-y-1 text-xs text-fuchsia-900">
            {topInstallCitiesToday.length > 0 ? (
              topInstallCitiesToday.slice(0, 3).map((entry) => (
                <div key={`${entry.location}-${entry.downloads}-${entry.completed}`} className="rounded bg-white/70 px-2 py-1">
                  <div className="flex items-center justify-between">
                    <span>{entry.location}</span>
                    <span className="font-semibold">{entry.downloads} downloads</span>
                  </div>
                  <div className="text-[11px] text-fuchsia-700">{entry.completed} completed • {entry.prompts} prompts</div>
                </div>
              ))
            ) : (
              <div>No install location data yet today.</div>
            )}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
        <div className="rounded-2xl border border-teal-200 bg-teal-50 p-4">
          <div className="text-xs font-semibold uppercase tracking-wide text-teal-700">Mobile Visits</div>
          <div className="mt-2 text-3xl font-extrabold text-teal-900">{mobileVisitsTotal}</div>
          <div className="mt-1 text-xs text-teal-800">Sessions detected from mobile devices.</div>
        </div>
        <div className="rounded-2xl border border-indigo-200 bg-indigo-50 p-4">
          <div className="text-xs font-semibold uppercase tracking-wide text-indigo-700">Desktop Visits</div>
          <div className="mt-2 text-3xl font-extrabold text-indigo-900">{desktopVisitsTotal}</div>
          <div className="mt-1 text-xs text-indigo-800">Sessions detected from desktop devices.</div>
        </div>
        <div className="rounded-2xl border border-cyan-200 bg-cyan-50 p-4">
          <div className="text-xs font-semibold uppercase tracking-wide text-cyan-700">Top Locations (Today)</div>
          <div className="mt-2 space-y-1 text-xs text-cyan-900">
            {visibleTopLocations.length > 0 ? (
              visibleTopLocations.slice(0, 3).map((entry) => (
                <div key={`${entry.location}-${entry.visits}`} className="flex items-center justify-between rounded bg-white/60 px-2 py-1">
                  <span>{entry.location}</span>
                  <span className="font-semibold">{entry.visits}</span>
                </div>
              ))
            ) : (
              <div>No location data yet today.</div>
            )}
          </div>
        </div>

        <div className="rounded-2xl border border-indigo-200 bg-indigo-50 p-4">
          <div className="text-xs font-semibold uppercase tracking-wide text-indigo-700">Navigation Opens</div>
          <div className="mt-2 text-3xl font-extrabold text-indigo-900">{analytics?.overview?.routeMapOpens || 0}</div>
          <div className="mt-1 text-xs text-indigo-800">Google Maps, Apple Maps, and Waze route handoffs opened from Road Trip mode.</div>
        </div>
        <div className="rounded-2xl border border-cyan-200 bg-cyan-50 p-4">
          <div className="text-xs font-semibold uppercase tracking-wide text-cyan-700">POI Directions Clicks</div>
          <div className="mt-2 text-3xl font-extrabold text-cyan-900">{analytics?.overview?.poiDirections || 0}</div>
          <div className="mt-1 text-xs text-cyan-800">Directions opened from Places of Interest and route activities.</div>
        </div>
        <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
          <div className="text-xs font-semibold uppercase tracking-wide text-slate-700">Booking API Status</div>
          <div className="mt-2 text-lg font-extrabold text-slate-900">Ready for room inventory + confirmations</div>
          <div className="mt-1 text-xs text-slate-600">The dashboard is already prepared for stay inventory, completed bookings, and revenue metrics once the booking API is connected.</div>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-5 xl:grid-cols-[1.1fr_0.9fr]">
        <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
          <div className="text-sm font-bold text-slate-900">Last 7 Days</div>
          <div className="mt-1 text-xs text-slate-500">Visits, itinerary generations, and booking clicks by day.</div>
          <div className="mt-5 grid grid-cols-7 gap-3">
            {summary.dailySeries.map((day) => (
              <div key={day.key} className="flex flex-col items-center gap-2">
                <div className="flex h-40 items-end gap-1">
                  <div className="w-3 rounded-t bg-blue-400" style={{ height: `${Math.max(8, (day.visits / maxDaily) * 140)}px` }} title={`Visits: ${day.visits}`} />
                  <div className="w-3 rounded-t bg-green-400" style={{ height: `${Math.max(8, (day.generated / maxDaily) * 140)}px` }} title={`Generated: ${day.generated}`} />
                  <div className="w-3 rounded-t bg-amber-400" style={{ height: `${Math.max(8, (day.bookings / maxDaily) * 140)}px` }} title={`Booking clicks: ${day.bookings}`} />
                </div>
                <div className="text-[11px] font-semibold text-slate-600">{day.label}</div>
              </div>
            ))}
          </div>
          <div className="mt-4 flex flex-wrap gap-3 text-xs text-slate-600">
            <span className="inline-flex items-center gap-2"><span className="h-3 w-3 rounded bg-blue-400" /> Visits</span>
            <span className="inline-flex items-center gap-2"><span className="h-3 w-3 rounded bg-green-400" /> Itineraries</span>
            <span className="inline-flex items-center gap-2"><span className="h-3 w-3 rounded bg-amber-400" /> Booking clicks</span>
          </div>
        </div>

        <div className="space-y-5">
          <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
            <div className="text-sm font-bold text-slate-900">Usage By Mode</div>
            <div className="mt-4 space-y-3">
              {modeUsageEntries.map((item) => (
                <div key={item.key}>
                  <div className="mb-1 flex items-center justify-between text-xs text-slate-600">
                    <span>{item.label}</span>
                    <span>{item.value}</span>
                  </div>
                  <div className="h-3 rounded-full bg-slate-100">
                    <div className={`h-3 rounded-full ${item.color}`} style={{ width: `${Math.max(6, (item.value / maxModeUsage) * 100)}%` }} />
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
            <div className="text-sm font-bold text-slate-900">Room Booking Funnel</div>
            <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-3">
              <div className="rounded-xl border border-amber-200 bg-amber-50 p-3">
                <div className="text-[11px] font-semibold uppercase tracking-wide text-amber-700">Stay Clicks</div>
                <div className="mt-2 text-2xl font-extrabold text-amber-900">{analytics?.overview?.accommodationClicks || 0}</div>
              </div>
              <div className="rounded-xl border border-rose-200 bg-rose-50 p-3">
                <div className="text-[11px] font-semibold uppercase tracking-wide text-rose-700">Confirmed</div>
                <div className="mt-2 text-2xl font-extrabold text-rose-900">{confirmedBookingsTotal}</div>
              </div>
              <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-3">
                <div className="text-[11px] font-semibold uppercase tracking-wide text-emerald-700">Booking Revenue</div>
                <div className="mt-2 text-2xl font-extrabold text-emerald-900">€{Number(bookingRevenueTotal || 0).toFixed(0)}</div>
              </div>
            </div>
            <div className="mt-3 text-xs text-slate-500">
              Confirmed bookings and revenue will remain zero until the room-booking API posts booking confirmations back into AppiHolidays.
            </div>
          </div>

          <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
            <div className="text-sm font-bold text-slate-900">Top Cities By Booking Interest</div>
            <div className="mt-4 space-y-2 text-sm text-slate-700">
              {summary.topCities.length > 0 ? (
                summary.topCities.map((item, index) => (
                  <div key={item.city} className="flex items-center justify-between rounded-xl border border-slate-100 bg-slate-50 px-3 py-2">
                    <span>{index + 1}. {item.city}</span>
                    <span className="font-semibold text-slate-900">{item.clicks} clicks</span>
                  </div>
                ))
              ) : (
                <div className="rounded-xl border border-dashed border-slate-200 bg-slate-50 px-3 py-4 text-xs text-slate-500">
                  No stay-click data yet. Once users open accommodation links, the most in-demand cities will appear here.
                </div>
              )}
            </div>
          </div>

          <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
            <div className="text-sm font-bold text-slate-900">Recent Install Events ({recentInstallEvents.length})</div>
            <div className="mt-4 space-y-2 text-sm text-slate-700">
              {recentInstallEvents.length > 0 ? (
                recentInstallEvents.map((event, index) => (
                  <div key={`${event.eventType}-${event.createdAt}-${index}`} className="flex items-center justify-between rounded-xl border border-slate-100 bg-slate-50 px-3 py-2">
                    <span className="font-medium text-slate-800">{formatInstallEventType(event.eventType)}</span>
                    <span className="text-xs text-slate-500">{new Date(event.createdAt).toLocaleString()}</span>
                  </div>
                ))
              ) : (
                <div className="rounded-xl border border-dashed border-slate-200 bg-slate-50 px-3 py-4 text-xs text-slate-500">
                  No install events yet. Prompt impressions, clicks, and completed downloads will appear here.
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

    </div>
  );
}

function AdminAccessGate({ onLogin, onBackToPlanner, errorMessage, loading }) {
  const [username, setUsername] = useState("bren.anita@gmail.com");
  const [password, setPassword] = useState("");
  const [accessCode, setAccessCode] = useState("");

  return (
    <div className="mx-auto max-w-xl rounded-3xl border border-slate-200 bg-white p-6 shadow-sm sm:p-8">
      <div className="text-xs font-semibold uppercase tracking-[0.2em] text-rose-600">Restricted</div>
      <h1 className="mt-2 text-2xl font-extrabold text-slate-900">Admin dashboard access</h1>
      <p className="mt-3 text-sm leading-6 text-slate-600">
        This dashboard is hidden from the public site. Sign in with admin email and password to open analytics for this browser session.
      </p>

      <form
        className="mt-6 space-y-4"
        onSubmit={async (event) => {
          event.preventDefault();
          await onLogin({ username, password, accessCode });
        }}
      >
        <div>
          <label className="mb-2 block text-xs font-semibold uppercase tracking-wide text-slate-600">Admin Email</label>
          <input
            type="email"
            value={username}
            onChange={(event) => setUsername(event.target.value)}
            className="w-full rounded-2xl border border-slate-300 px-4 py-3 text-sm text-slate-900 outline-none transition focus:border-emerald-500 focus:ring-2 focus:ring-emerald-100"
            placeholder="bren.anita@gmail.com"
            autoComplete="username"
          />
        </div>

        <div>
          <label className="mb-2 block text-xs font-semibold uppercase tracking-wide text-slate-600">Password</label>
          <input
            type="password"
            value={password}
            onChange={(event) => setPassword(event.target.value)}
            className="w-full rounded-2xl border border-slate-300 px-4 py-3 text-sm text-slate-900 outline-none transition focus:border-emerald-500 focus:ring-2 focus:ring-emerald-100"
            placeholder="Enter admin password"
            autoComplete="current-password"
          />
        </div>

        <div>
          <label className="mb-2 block text-xs font-semibold uppercase tracking-wide text-slate-600">Access Code (Legacy Optional)</label>
          <input
            type="password"
            value={accessCode}
            onChange={(event) => setAccessCode(event.target.value)}
            className="w-full rounded-2xl border border-slate-300 px-4 py-3 text-sm text-slate-900 outline-none transition focus:border-emerald-500 focus:ring-2 focus:ring-emerald-100"
            placeholder="Enter admin access code"
            autoComplete="current-password"
          />
        </div>

        {errorMessage && <div className="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">{errorMessage}</div>}

        <div className="flex flex-wrap items-center gap-3">
          <button
            type="submit"
            disabled={loading}
            className="inline-flex items-center justify-center rounded-2xl bg-slate-900 px-5 py-3 text-sm font-semibold text-white transition hover:bg-slate-800"
          >
            {loading ? "Unlocking..." : "Unlock Dashboard"}
          </button>
          <button
            type="button"
            onClick={onBackToPlanner}
            className="inline-flex items-center gap-2 rounded-2xl border border-slate-300 bg-white px-4 py-3 text-sm font-semibold text-slate-700 transition hover:bg-slate-50"
          >
            <ArrowLeft className="h-4 w-4" /> Back To Planner
          </button>
        </div>
      </form>

      <div className="mt-5 text-xs text-slate-500">
        This admin route uses a server-issued session cookie. Set ADMIN_USERNAME and ADMIN_PASSWORD in deployment env (or ADMIN_ACCESS_CODE for legacy access).
      </div>
    </div>
  );
}

// ---- Helper: Build itinerary prompt ----
function buildItineraryPrompt({ city, country, days, people = 2, theme, budget, pace, interests, excludedActivities = [], outputLanguage = "English", outputStyleInstruction = "" }) {
  const destination = city || country || "Unknown Location";
  const context = city ? `the city of ${city}${country ? `, ${country}` : ""}` : country || "Unknown Location";
  const exclusions = Array.isArray(excludedActivities)
    ? excludedActivities.map((entry) => String(entry || "").trim()).filter(Boolean).slice(0, 40)
    : [];

  const noRepeatInstruction = exclusions.length
    ? `\nAvoid repeating these previously used activities in this destination:\n${exclusions.map((item) => `- ${item}`).join("\n")}\nIf similar landmarks are needed, choose clearly different alternatives.`
    : "";

  return `
Generate a detailed, dynamic, and multi-day guided tour itinerary for ${context} focusing on ${destination}.

  Language requirement:
  - Write ALL user-facing text in ${outputLanguage}.
  - Keep only JSON keys in English (as required by schema).
  ${outputStyleInstruction ? `- ${outputStyleInstruction}` : ""}

Use a guided-tour voice focused on the place itself (no directions): write 2–3 vivid sentences per activity with sensory detail and at least two concrete local facts (names/dates/styles/dishes/rituals). Include a short "culturalStory" (1–2 sentences) for EVERY activity (legend/origin/ritual OR named artist/architect/chef with a year, and why locals care).

The itinerary must be:
- A specific number of days: ${days}, and for ${people} people.
- Focused on a specific theme: ${theme || "balanced sightseeing"}.
- Detailed with specific times for each activity.
- Include recommended local transportation for each attraction.
- Suggest diverse places to eat and mention cuisine or signature dishes.
- Weave in cultural stories, historical context, or local folklore.
- Provide a balance of major attractions and lesser-known spots.
- End with a suitable final activity (e.g., farewell dinner or a final shopping trip).
${noRepeatInstruction}
`;
}

// Helper: extract JSON from model output (handles ```json fences + extra chatter)
function extractJson(text = "") {
  let t = String(text).trim();

  // Remove leading ```json or ``` fence
  t = t.replace(/^```(?:json)?\s*/i, "").trim();
  // Remove trailing ``` if it is at the very end
  t = t.replace(/\s*```$/i, "").trim();

  const firstObj = t.indexOf("{");
  const lastObj = t.lastIndexOf("}");
  const firstArr = t.indexOf("[");
  const lastArr = t.lastIndexOf("]");

  if (firstObj !== -1 && lastObj !== -1 && lastObj > firstObj) return t.slice(firstObj, lastObj + 1);
  if (firstArr !== -1 && lastArr !== -1 && lastArr > firstArr) return t.slice(firstArr, lastArr + 1);

  return t;
}

const weatherConditionFromCode = (code) => {
  const numeric = Number(code);
  if (!Number.isFinite(numeric)) return { condition: "Partly cloudy", summary: "Variable conditions" };

  if (numeric === 0) return { condition: "Clear sky", summary: "Sunny and clear" };
  if ([1, 2].includes(numeric)) return { condition: "Partly cloudy", summary: "Mostly fair with some clouds" };
  if (numeric === 3) return { condition: "Overcast", summary: "Cloudy throughout the day" };
  if ([45, 48].includes(numeric)) return { condition: "Fog", summary: "Low visibility possible" };
  if ([51, 53, 55, 56, 57].includes(numeric)) return { condition: "Drizzle", summary: "Light to moderate drizzle" };
  if ([61, 63, 65, 66, 67, 80, 81, 82].includes(numeric)) return { condition: "Rain", summary: "Periods of rain expected" };
  if ([71, 73, 75, 77, 85, 86].includes(numeric)) return { condition: "Snow", summary: "Snowfall likely" };
  if ([95, 96, 99].includes(numeric)) return { condition: "Thunderstorm", summary: "Storm risk during the day" };
  return { condition: "Partly cloudy", summary: "Mixed weather conditions" };
};

const fetchLiveDailyWeather = async ({ city, country, days }) => {
  const dayCount = Math.min(Math.max(Number(days) || 1, 1), 14);
  const query = [city, country].filter(Boolean).join(", ").trim();
  if (!query) return null;

  const geoUrl = `https://geocoding-api.open-meteo.com/v1/search?name=${encodeURIComponent(query)}&count=1&language=en&format=json`;
  const geoRes = await fetch(geoUrl);
  if (!geoRes.ok) return null;

  const geoData = await geoRes.json();
  const first = Array.isArray(geoData?.results) ? geoData.results[0] : null;
  if (!first || !Number.isFinite(first.latitude) || !Number.isFinite(first.longitude)) return null;

  const weatherUrl =
    `https://api.open-meteo.com/v1/forecast?latitude=${first.latitude}&longitude=${first.longitude}` +
    `&daily=weather_code,temperature_2m_max,temperature_2m_min,wind_speed_10m_max,precipitation_probability_max&timezone=auto&forecast_days=${dayCount}`;
  const weatherRes = await fetch(weatherUrl);
  if (!weatherRes.ok) return null;

  const weatherData = await weatherRes.json();
  const daily = weatherData?.daily;
  if (!daily || !Array.isArray(daily.time)) return null;

  return daily.time.map((_, idx) => {
    const high = daily.temperature_2m_max?.[idx];
    const low = daily.temperature_2m_min?.[idx];
    const wind = daily.wind_speed_10m_max?.[idx];
    const rainChance = daily.precipitation_probability_max?.[idx];
    const mapped = weatherConditionFromCode(daily.weather_code?.[idx]);

    return {
      summary: mapped.summary,
      condition: mapped.condition,
      high: Number.isFinite(high) ? `${Math.round(high)}°C` : "N/A",
      low: Number.isFinite(low) ? `${Math.round(low)}°C` : "N/A",
      humidity: Number.isFinite(rainChance) ? `${Math.round(rainChance)}% precip chance` : "N/A",
      wind: Number.isFinite(wind) ? `${Math.round(wind)} km/h` : "N/A",
    };
  });
};

// 4) Accommodation suggestions (curated lists)
const STAYS = {
  Dublin: {
    Budget: [
      {
        name: "Generator Dublin (Hostel)",
        desc: "Lively hostel in Smithfield with private & shared rooms, great for budget travelers near LUAS.",
      },
      {
        name: "Abbey Court Hostel",
        desc: "Colorful hostel by the River Liffey, free breakfast, close to Temple Bar.",
      },
      {
        name: "Kinlay House",
        desc: "Simple hostel near Christchurch Cathedral, walkable to most sights.",
      },
      {
        name: "Travelodge Dublin City Centre",
        desc: "No-frills hotel on Pearse St., clean rooms and strong value close to Trinity College.",
      },
      {
        name: "The Kildare Street Hotel",
        desc: "Classic budget option steps from National Museum and St. Stephen’s Green.",
      },
    ],
    "Mid-range": [
      {
        name: "The Davenport",
        desc: "Historic four-star elegance near Trinity College with Georgian charm and an excellent restaurant.",
      },
      {
        name: "Hilton Dublin",
        desc: "Modern four-star in the center with fitness facilities and easy access to shopping & nightlife.",
      },
      {
        name: "The Green Hotel",
        desc: "Contemporary stay on St. Stephen’s Green; stylish rooms and superb breakfast.",
      },
      {
        name: "Travelodge Dublin City Centre",
        desc: "Modern hotel on Pearse Street; comfortable rooms, great value, walkable to Temple Bar.",
      },
      {
        name: "Fitzpatrick Castle Hotel (Killiney)",
        desc: "Traditional Irish hospitality in a castle setting; beautiful grounds, DART into the city.",
      },
    ],
    Luxury: [
      {
        name: "The Shelbourne",
        desc: "Iconic five-star on St. Stephen’s Green with refined rooms and legendary service.",
      },
      {
        name: "The Westbury",
        desc: "Polished luxury just off Grafton Street; elegant design and upscale amenities.",
      },
      {
        name: "The Merrion",
        desc: "Georgian townhouses with a serene courtyard garden and fine dining.",
      },
      {
        name: "The Marker",
        desc: "Contemporary luxury in Docklands with a rooftop bar and spa.",
      },
      {
        name: "InterContinental Dublin",
        desc: "Spacious rooms in Ballsbridge; tranquil vibe with premium facilities.",
      },
    ],
  },
  __generic: {
    Budget: [
      { name: "City Central Hostel", desc: "Clean, social hostel with private & shared options close to transit." },
      { name: "Backpackers Lodge", desc: "Budget-friendly stay with 24/7 desk and communal kitchen." },
      { name: "Comfort Inn Express", desc: "Reliable basics, walkable location, breakfast included." },
      { name: "Urban Micro Hotel", desc: "Compact rooms, good Wi-Fi, near restaurants and cafes." },
      { name: "Railway View Guesthouse", desc: "Family-run guesthouse with friendly hosts and easy transport links." },
    ],
    "Mid-range": [
      { name: "Central Park Hotel", desc: "Modern rooms, great breakfast, close to main attractions." },
      { name: "Boutique City Hotel", desc: "Stylish interiors, friendly staff, and a solid on-site bar." },
      { name: "Riverside Suites", desc: "Spacious rooms with nice views and convenient location." },
      { name: "Garden Court Hotel", desc: "Quiet street, comfortable beds, easy access to transit." },
      { name: "Old Town Residence", desc: "Character property with updated amenities near historic center." },
    ],
    Luxury: [
      { name: "Grand Heritage Hotel", desc: "Premium central location with upscale rooms and concierge service." },
      { name: "Rooftop Spa Hotel", desc: "High-end stay with spa facilities and skyline views." },
      { name: "Riverside Luxury Suites", desc: "Spacious suites with refined interiors and top service." },
      { name: "Boutique Five-Star", desc: "Design-led luxury with exceptional dining options." },
      { name: "Historic Palace Hotel", desc: "Landmark property with heritage architecture and fine dining." },
    ],
  },
};

const selectStayPool = (cityName = "") => {
  const c = (cityName || "").toLowerCase();
  if (c.includes("dublin")) return STAYS.Dublin;
  return STAYS.__generic;
};

const hashString = (value = "") => {
  let hash = 0;
  for (let i = 0; i < String(value).length; i += 1) {
    hash = ((hash << 5) - hash + String(value).charCodeAt(i)) | 0;
  }
  return hash;
};

const rotateArrayBySeed = (items = [], seed = "") => {
  const list = Array.isArray(items) ? items.slice() : [];
  if (list.length <= 1) return list;
  const offset = Math.abs(hashString(seed)) % list.length;
  return [...list.slice(offset), ...list.slice(0, offset)];
};

const normalizeStayKey = (value = "") =>
  String(value || "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "")
    .trim();

const isBudgetAlignedHotelSuggestion = (line = "", budget = "Mid-range") => {
  const text = String(line || "").toLowerCase();
  const normalizedBudget = String(budget || "Mid-range").trim();
  if (!text) return false;

  const budgetKeywords = ["hostel", "guesthouse", "guest house", "b&b", "bnb", "budget", "dorm"];
  const luxuryKeywords = ["luxury", "palace", "st. regis", "waldorf", "ritz", "five-star", "5-star", "grand"];

  if (normalizedBudget === "Budget") {
    return budgetKeywords.some((token) => text.includes(token));
  }
  if (normalizedBudget === "Luxury") {
    if (budgetKeywords.some((token) => text.includes(token))) return false;
    return luxuryKeywords.some((token) => text.includes(token)) || text.includes("hotel");
  }

  // Mid-range should avoid obvious budget hostels and obvious ultra-luxury markers.
  if (budgetKeywords.some((token) => text.includes(token))) return false;
  if (luxuryKeywords.some((token) => text.includes(token))) return false;
  return true;
};

const buildSingleCityHotelOptions = ({ aiHotelLines = [], curatedStays = [], seed = "", limit = 5, includeAi = true }) => {
  const maxItems = Math.max(1, Number(limit) || 5);
  const budget = String((seed.split("|")[2] || "Mid-range")).trim() || "Mid-range";
  const aiCards = includeAi
    ? (Array.isArray(aiHotelLines) ? aiHotelLines : [])
    .map((line) => String(line || "").trim())
    .filter(Boolean)
    .filter((line) => isBudgetAlignedHotelSuggestion(line, budget))
    .map((line, index) => ({
      kind: "ai",
      line,
      key: `ai-${index}-${line.slice(0, 32)}`,
      dedupe: normalizeStayKey(line.split(/—| - /)[0] || line),
    }))
    : [];

  const curatedCards = rotateArrayBySeed(
    (Array.isArray(curatedStays) ? curatedStays : [])
      .filter((stay) => stay && typeof stay === "object")
      .map((stay, index) => ({
        kind: "curated",
        stay,
        key: `cur-${index}-${String(stay?.name || "").slice(0, 32)}`,
        dedupe: normalizeStayKey(stay?.name || ""),
      })),
    seed
  );

  const picked = [];
  const seen = new Set();
  const tryAdd = (item) => {
    if (!item || picked.length >= maxItems) return;
    const dedupeKey = item.dedupe || "";
    if (dedupeKey && seen.has(dedupeKey)) return;
    picked.push(item);
    if (dedupeKey) seen.add(dedupeKey);
  };

  // Ensure budget-specific variety appears while still prioritizing AI choices.
  curatedCards.slice(0, 2).forEach(tryAdd);
  aiCards.forEach(tryAdd);
  curatedCards.forEach(tryAdd);

  // Hard guarantee: always provide maxItems by cycling curated stays.
  if (picked.length < maxItems && curatedCards.length > 0) {
    let idx = 0;
    let guard = 0;
    while (picked.length < maxItems && guard < curatedCards.length * 4) {
      tryAdd(curatedCards[idx % curatedCards.length]);
      idx += 1;
      guard += 1;
    }
  }

  return picked.slice(0, maxItems);
};

const sanitizeHotelSuggestionLine = (line = "") => {
  return String(line || "")
    .replace(/\[(?:View on Booking\.com|Search stays on Booking\.com|Hotels on Appi)\s*↗?\]\([^)]*\)/gi, "")
    .replace(/\[(?:Map|Google Maps)\s*↗?\]\([^)]*\)/gi, "")
    .replace(/\b(?:View on Booking\.com|Search stays on Booking\.com)\b/gi, "Hotels on Appi")
    .replace(/\s{2,}/g, " ")
    .trim();
};

const extractMapLinkFromSuggestion = (line = "") => {
  const source = String(line || "");
  const markdownMatch = source.match(/\[(?:Map|Google Maps)?[^\]]*\]\((https?:\/\/[^)\s]+)\)/i);
  const fallbackMatch = source.match(/https?:\/\/\S+/i);
  const raw = markdownMatch?.[1] || fallbackMatch?.[0] || "";
  if (!raw) return "";

  // Remove common trailing punctuation captured by loose URL matching.
  const trimmed = raw.replace(/[),.;]+$/g, "");
  return normalizeUrl(trimmed);
};

const rewriteBookingCopy = (value) => {
  if (typeof value === "string") {
    return value
      .replace(/\[(?:Hotels on Appi|View on Booking\.com|Search stays on Booking\.com)\s*↗?\]\((https?:\/\/[^)\s]+)\)/gi, "Hotel links temporarily disabled (testing)")
      .replace(/View on Booking\.com\s*↗?/gi, "Hotels on Appi ↗")
      .replace(/Search stays on Booking\.com\s*↗?/gi, "Hotels on Appi ↗")
      .replace(/Hotels on Appi\s*↗?/gi, "Hotel links temporarily disabled (testing)")
      .replace(/https?:\/\/book\.appiholidays\.com\/\S*/gi, "")
      .replace(/Booking\.com/gi, "Appi");
  }
  if (Array.isArray(value)) {
    return value.map((item) => rewriteBookingCopy(item));
  }
  if (value && typeof value === "object") {
    const next = {};
    for (const [k, v] of Object.entries(value)) {
      next[k] = rewriteBookingCopy(v);
    }
    return next;
  }
  return value;
};

const estimateNightly = (budget = "Mid-range") => {
  const daily = { Budget: 90, "Mid-range": 180, Luxury: 350 };
  return daily[budget] ?? daily["Mid-range"];
};

// 5) Airport Transport helpers
const TRANSPORT = {
  dublin: {
    title: "Transport from Dublin Airport (DUB)",
    arrivalLabel: "Upon Arrival",
    buses: {
      express: [
        { name: "Aircoach 700X", note: "Express to city center via O'Connell Street (€8, every 15 mins, 35–45 mins)" },
        { name: "Dublin Express 782", note: "Express to city center via Eden Quay (€6, every 20 mins, ~45 mins)" },
      ],
      routes: [
        { name: "Dublin Bus 16", note: "Zone 1 → city center via O'Connell Street (€3.30, every 20 mins, ~55 mins)" },
        { name: "Dublin Bus 41", note: "Zone 2 → city center via Lower Abbey Street (€3.30, every 30 mins, ~45 mins)" },
        { name: "Dublin Bus 102", note: "Zone 14 → Sutton Station (€3.30, connects to DART)" },
        { name: "Route 757", note: "Express to Carlow/Portlaoise (€12–15)" },
      ],
    },
    taxis: {
      ranks: "Outside Arrivals: T1 (Zone 15) & T2 (Zone 9)",
      cost: "€25–35 to city center (20–30 mins, traffic dependent)",
      prebook: ["Dublin Airport Taxis", "Lynk Taxis"],
    },
    shuttles: [
      { name: "Clayton Hotel Burlington Road", note: "Complimentary shuttle (pre-book)" },
      { name: "Hilton Dublin", note: "Scheduled shuttle (€10)" },
      { name: "Crowne Plaza Dublin Airport", note: "Complimentary (on-site)" },
      { name: "Radisson Blu Dublin Airport", note: "Complimentary (on-site)" },
    ],
    carRental: {
      location: "Desks in Arrivals; cars in multi-storey car park",
      companies: ["Avis", "Europcar", "Hertz", "Enterprise", "Sixt"],
    },
    note: "LUAS (tram) does NOT serve Dublin Airport. Nearest is Broombridge (Red Line), requiring a bus connection.",
  },
  generic: (city = "your destination") => ({
    title: `Getting from the airport to ${city}`,
    arrivalLabel: "Upon Arrival",
    buses: {
      express: [{ name: "Airport Express", note: "Direct coach to the main station/city center every 15–30 mins" }],
      routes: [{ name: "Local Bus", note: "Cheapest option; allow extra time for stops" }],
    },
    taxis: { ranks: "Official ranks outside Arrivals", cost: "Metered; expect surge at peak times", prebook: ["Local taxi apps"] },
    shuttles: [{ name: "Hotel Shuttle", note: "Check with your hotel; often complimentary or small fee" }],
    carRental: { location: "Desks in Arrivals or Car Rental Centre", companies: ["Avis", "Hertz", "Enterprise", "Sixt"] },
    note: "Some airports have train/metro links; check signage on arrival.",
  }),
};

const selectAirportTransport = (destination = "") => {
  const d = (destination || "").toLowerCase();
  if (d.includes("dublin")) return TRANSPORT.dublin;
  return TRANSPORT.generic(destination || "your destination");
};

const VERIFIED_AIRPORT_RAIL_RULES = {
  directRail: [
    "amsterdam",
    "barcelona",
    "berlin",
    "frankfurt",
    "hong kong",
    "lisbon",
    "london",
    "madrid",
    "munich",
    "new york",
    "paris",
    "rome",
    "singapore",
    "tokyo",
    "vienna",
  ],
  noDirectRail: ["dublin", "las vegas"],
};

const resolveVerifiedRailStatus = ({ city = "", country = "", title = "", destination = "" }) => {
  const haystack = `${city} ${country} ${title} ${destination}`.toLowerCase();

  if (VERIFIED_AIRPORT_RAIL_RULES.noDirectRail.some((token) => haystack.includes(token))) {
    return {
      status: "blocked",
      note:
        "No verified direct rail station at this airport. Use bus/coach and taxi/chauffeur options from Arrivals.",
    };
  }

  if (VERIFIED_AIRPORT_RAIL_RULES.directRail.some((token) => haystack.includes(token))) {
    return { status: "allowed", note: "" };
  }

  return {
    status: "unknown",
    note:
      "Rail/metro details are shown only when verified. Bus/coach and taxi options are prioritized until rail availability is confirmed.",
  };
};

const isValidIsoDateString = (value = "") => /^\d{4}-\d{2}-\d{2}$/.test(String(value || "").trim());

const parseIsoDateUtc = (value = "") => {
  if (!isValidIsoDateString(value)) return null;
  const [yearStr, monthStr, dayStr] = String(value).split("-");
  const year = Number(yearStr);
  const month = Number(monthStr);
  const day = Number(dayStr);
  const date = new Date(Date.UTC(year, month - 1, day));
  if (
    Number.isNaN(date.getTime()) ||
    date.getUTCFullYear() !== year ||
    date.getUTCMonth() !== month - 1 ||
    date.getUTCDate() !== day
  ) {
    return null;
  }
  return date;
};

const formatIsoDateUtc = (date) => {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return "";
  const y = date.getUTCFullYear();
  const m = String(date.getUTCMonth() + 1).padStart(2, "0");
  const d = String(date.getUTCDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
};

const normalizeBookingSearchDates = (checkIn = "", checkOut = "", fallbackNights = 2) => {
  const todayIso = new Date().toISOString().slice(0, 10);
  const today = parseIsoDateUtc(todayIso);
  if (!today) return { checkInDate: "", checkOutDate: "" };

  const requestedCheckIn = parseIsoDateUtc(checkIn);
  const requestedCheckOut = parseIsoDateUtc(checkOut);

  const requestedNights =
    requestedCheckIn && requestedCheckOut && requestedCheckOut > requestedCheckIn
      ? Math.round((requestedCheckOut.getTime() - requestedCheckIn.getTime()) / (1000 * 60 * 60 * 24))
      : 0;

  const safeNights = Math.max(1, requestedNights || Number(fallbackNights) || 2);

  const safeCheckIn = requestedCheckIn && requestedCheckIn >= today ? requestedCheckIn : today;
  const safeCheckOut = requestedCheckOut && requestedCheckOut > safeCheckIn
    ? requestedCheckOut
    : (() => {
        const next = new Date(safeCheckIn.getTime());
        next.setUTCDate(next.getUTCDate() + safeNights);
        return next;
      })();

  return {
    checkInDate: formatIsoDateUtc(safeCheckIn),
    checkOutDate: formatIsoDateUtc(safeCheckOut),
  };
};

const CALENDAR_WEEKDAY_LABELS = ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"];

const getCalendarMonthStart = (date = new Date()) => {
  const base = date instanceof Date ? date : new Date();
  return new Date(Date.UTC(base.getUTCFullYear(), base.getUTCMonth(), 1));
};

const shiftCalendarMonth = (monthStart, delta = 0) => {
  const start = monthStart instanceof Date ? monthStart : getCalendarMonthStart(new Date());
  return new Date(Date.UTC(start.getUTCFullYear(), start.getUTCMonth() + delta, 1));
};

const buildCalendarMonthCells = (monthStart) => {
  const start = getCalendarMonthStart(monthStart);
  const firstWeekday = start.getUTCDay();
  const gridStart = new Date(start.getTime());
  gridStart.setUTCDate(gridStart.getUTCDate() - firstWeekday);

  return Array.from({ length: 42 }, (_, index) => {
    const date = new Date(gridStart.getTime());
    date.setUTCDate(gridStart.getUTCDate() + index);
    return {
      iso: formatIsoDateUtc(date),
      day: date.getUTCDate(),
      inMonth: date.getUTCMonth() === start.getUTCMonth(),
    };
  });
};

const formatBookingDateLabel = (isoDate = "") => {
  const parsed = parseIsoDateUtc(isoDate);
  if (!parsed) return "";
  return parsed.toLocaleDateString("en-IE", { month: "short", day: "numeric", timeZone: "UTC" });
};

const getBudgetBookingProfile = (budget = "") => {
  const normalized = String(budget || "").trim().toLowerCase();
  if (normalized === "luxury") {
    return {
      hint: "Luxury hotel",
      // Prefer upper-category properties.
      nflt: "class=5;class=4",
    };
  }
  if (normalized === "budget") {
    return {
      hint: "Budget hotel",
      // Prefer lower-category properties.
      nflt: "class=1;class=2",
    };
  }
  return {
    hint: "Mid-range hotel",
    // Prefer mid-category properties.
    nflt: "class=3;class=4",
  };
};

const buildAppiSearchLink = ({ destination = "", displayName = "", checkIn = "", checkOut = "", adults = 2 } = {}) => {
  const url = new URL("https://book.appiholidays.com/search");
  const destinationValue = String(destination || "").trim();
  const displayValue = String(displayName || destinationValue || "").trim();

  if (destinationValue) {
    url.searchParams.set("destination", destinationValue);
    url.searchParams.set("displayName", displayValue || destinationValue);
    url.searchParams.set("searchType", "city");
  }

  const normalizedDates = normalizeBookingSearchDates(checkIn, checkOut, 2);
  const inDate = parseIsoDateUtc(normalizedDates.checkInDate);
  const outDate = parseIsoDateUtc(normalizedDates.checkOutDate);
  if (inDate) url.searchParams.set("checkIn", formatIsoDateUtc(inDate));
  if (outDate) url.searchParams.set("checkOut", formatIsoDateUtc(outDate));

  const safeAdults = Math.max(1, Number(adults) || 2);
  url.searchParams.set("adults", String(safeAdults));
  return url.toString();
};

const buildBookingCityLink = (city = "", country = "", hotelName = "", checkIn = "", checkOut = "", budget = "", adults = 2) => {
  const budgetProfile = getBudgetBookingProfile(budget);
  const destination = String(city || hotelName || country || "").trim();
  const displayName = [hotelName || city, country, budgetProfile.hint].filter(Boolean).join(", ");
  return buildAppiSearchLink({
    destination,
    displayName,
    checkIn,
    checkOut,
    adults,
  });
};

const buildBookingLocationLink = (city = "", country = "", checkIn = "", checkOut = "", budget = "", adults = 2) => {
  const budgetProfile = getBudgetBookingProfile(budget);
  const destination = String(city || country || "").trim();
  const displayName = [city, country, budgetProfile.hint].filter(Boolean).join(", ");
  return buildAppiSearchLink({
    destination,
    displayName,
    checkIn,
    checkOut,
    adults,
  });
};

const APPI_HOTEL_DEEP_LINKS = {
  "hilton dublin": {
    slug: "lp379b1",
    name: "Hilton Dublin Airport",
    img: "https://static.cupid.travel/hotels/483767218.jpg",
    currency: "GBP",
  },
  "hilton dublin airport": {
    slug: "lp379b1",
    name: "Hilton Dublin Airport",
    img: "https://static.cupid.travel/hotels/483767218.jpg",
    currency: "GBP",
  },
  "powerscourt hotel resort spa": {
    slug: "lp3e5fa",
    name: "Powerscourt Hotel, Autograph Collection",
    img: "https://static.cupid.travel/hotels/480984635.jpg",
    currency: "EUR",
  },
  "powerscourt hotel resort and spa": {
    slug: "lp3e5fa",
    name: "Powerscourt Hotel, Autograph Collection",
    img: "https://static.cupid.travel/hotels/480984635.jpg",
    currency: "EUR",
  },
  "powerscourt hotel autograph collection": {
    slug: "lp3e5fa",
    name: "Powerscourt Hotel, Autograph Collection",
    img: "https://static.cupid.travel/hotels/480984635.jpg",
    currency: "EUR",
  },
  "powerscourt hotel": {
    slug: "lp3e5fa",
    name: "Powerscourt Hotel, Autograph Collection",
    img: "https://static.cupid.travel/hotels/480984635.jpg",
    currency: "EUR",
  },
};

const HOTEL_LINKS_ENABLED = true;
const HOTEL_LINKS_DISABLED_LABEL = "Hotel links temporarily disabled (testing)";

const normalizeHotelDeepLinkKey = (value = "") => {
  return String(value || "")
    .toLowerCase()
    .replace(/&/g, " and ")
    .replace(/[^a-z0-9\s]/g, " ")
    .replace(/\s+/g, " ")
    .trim();
};

const buildAppiHotelDeepLinkFromName = (hotelName = "", checkIn = "", checkOut = "", adults = 2) => {
  const key = normalizeHotelDeepLinkKey(hotelName);
  const match = APPI_HOTEL_DEEP_LINKS[key];
  if (!match?.slug) return "";

  const url = new URL(`https://book.appiholidays.com/hotel/${match.slug}`);
  const inDate = parseIsoDateUtc(checkIn);
  const outDate = parseIsoDateUtc(checkOut);
  if (inDate) url.searchParams.set("checkIn", formatIsoDateUtc(inDate));
  if (outDate) url.searchParams.set("checkOut", formatIsoDateUtc(outDate));
  url.searchParams.set("adults", String(Math.max(1, Number(adults) || 2)));
  url.searchParams.set("children", "");
  url.searchParams.set("currency", match.currency || "GBP");
  url.searchParams.set("name", match.name || hotelName);
  if (match.img) url.searchParams.set("img", match.img);
  return url.toString();
};

const withAppiHotelStayParams = (url, { checkIn = "", checkOut = "", adults = 2, hotelName = "" } = {}) => {
  const next = new URL(url.toString());
  const inDate = parseIsoDateUtc(checkIn);
  const outDate = parseIsoDateUtc(checkOut);
  if (inDate) next.searchParams.set("checkIn", formatIsoDateUtc(inDate));
  if (outDate) next.searchParams.set("checkOut", formatIsoDateUtc(outDate));
  next.searchParams.set("adults", String(Math.max(1, Number(adults) || 2)));
  if (!next.searchParams.has("children")) next.searchParams.set("children", "");
  if (!next.searchParams.has("currency")) next.searchParams.set("currency", "GBP");
  if (hotelName && !next.searchParams.has("name")) next.searchParams.set("name", hotelName);
  return next.toString();
};

const sanitizeAccommodationLink = (link, city = "", country = "", hotelName = "", checkIn = "", checkOut = "", budget = "", adults = 2) => {
  const budgetProfile = getBudgetBookingProfile(budget);
  const fallback = buildBookingCityLink(city, country, hotelName, checkIn, checkOut, budget, adults);
  const directByName = buildAppiHotelDeepLinkFromName(hotelName, checkIn, checkOut, adults);
  if (!link || typeof link !== "string") return fallback;

  try {
    const url = new URL(link);
    const host = url.hostname.toLowerCase();
    if (host.includes("book.appiholidays.com")) {
      if (url.pathname.toLowerCase().startsWith("/hotel/")) {
        return withAppiHotelStayParams(url, { checkIn, checkOut, adults, hotelName });
      }
      const destination = url.searchParams.get("destination") || city || hotelName || country;
      const displayName = url.searchParams.get("displayName") || [hotelName || city, country, budgetProfile.hint].filter(Boolean).join(", ");
      return buildAppiSearchLink({
        destination,
        displayName,
        checkIn,
        checkOut,
        adults: Number(url.searchParams.get("adults") || adults),
      });
    }

    if (!host.includes("booking.com")) return fallback;

    const path = url.pathname.toLowerCase();
    const isPropertyPage = path.includes("/hotel/");

    // For known hotel names, use direct Appi deep-link; otherwise fallback to search.
    if (isPropertyPage) {
      return directByName || fallback;
    }

    const ss = url.searchParams.get("ss") || "";
    const destination = city || country || ss;
    const displayName = [hotelName || city || ss, country, budgetProfile.hint].filter(Boolean).join(", ");
    return buildAppiSearchLink({
      destination,
      displayName,
      checkIn,
      checkOut,
      adults,
    });
  } catch {
    return fallback;
  }
};

const buildMultiCityDateRanges = (stops = [], tripStartDate = "") => {
  const start = parseIsoDateUtc(tripStartDate);
  if (!start) return [];

  let cursor = new Date(start.getTime());
  return stops.map((stop) => {
    const nights = Math.max(1, Number(stop?.nights) || 1);
    const checkInDate = formatIsoDateUtc(cursor);

    const next = new Date(cursor.getTime());
    next.setUTCDate(next.getUTCDate() + nights);
    const checkOutDate = formatIsoDateUtc(next);

    cursor = next;
    return { checkInDate, checkOutDate };
  });
};

const buildRoadTripLegDateRanges = (legs = [], tripStartDate = "", totalTripDays = 0) => {
  const start = parseIsoDateUtc(tripStartDate);
  if (!start || !Array.isArray(legs) || legs.length === 0) return [];

  const safeTotalDays = Math.max(0, Number(totalTripDays) || 0);
  const dayNumbers = legs.map((leg, index) => {
    const candidate = Number(leg?.day);
    return Number.isFinite(candidate) && candidate >= 1 ? Math.floor(candidate) : index + 1;
  });

  return legs.map((_, index) => {
    const currentDay = Math.max(1, dayNumbers[index] || index + 1);
    const nextKnownDay = dayNumbers[index + 1];
    let nextDay = Number.isFinite(nextKnownDay) && nextKnownDay > currentDay ? nextKnownDay : currentDay + 1;

    if (index === legs.length - 1 && safeTotalDays >= currentDay) {
      nextDay = Math.max(nextDay, safeTotalDays + 1);
    }

    const checkIn = new Date(start.getTime());
    checkIn.setUTCDate(checkIn.getUTCDate() + (currentDay - 1));

    const checkOut = new Date(start.getTime());
    checkOut.setUTCDate(checkOut.getUTCDate() + (nextDay - 1));

    if (checkOut <= checkIn) {
      checkOut.setUTCDate(checkIn.getUTCDate() + 1);
    }

    const nights = Math.max(1, Math.round((checkOut.getTime() - checkIn.getTime()) / (1000 * 60 * 60 * 24)));

    return {
      checkInDate: formatIsoDateUtc(checkIn),
      checkOutDate: formatIsoDateUtc(checkOut),
      nights,
    };
  });
};

const buildSingleCityStayDates = (tripStartDate = "", durationDays = 0) => {
  const start = parseIsoDateUtc(tripStartDate);
  if (!start) return { checkInDate: "", checkOutDate: "", nights: 0 };

  const safeDays = Math.max(1, Number(durationDays) || 1);
  const checkInDate = formatIsoDateUtc(start);
  const checkOut = new Date(start.getTime());
  checkOut.setUTCDate(checkOut.getUTCDate() + safeDays);

  return {
    checkInDate,
    checkOutDate: formatIsoDateUtc(checkOut),
    nights: safeDays,
  };
};

const formatApiError = (err) => {
  if (!err) return "Unknown error";
  if (typeof err === "string") return err;
  if (typeof err === "number" || typeof err === "boolean") return String(err);

  if (typeof err === "object") {
    const direct = [err.message, err.error, err.details, err.statusText]
      .filter((v) => typeof v === "string" && v.trim())
      .join(" | ");
    if (direct) return direct;

    try {
      return JSON.stringify(err).slice(0, 300);
    } catch {
      return "Unexpected object error";
    }
  }

  return String(err);
};

const ROAD_TRIP_OVERNIGHT_LABELS = ["Best Value Pick", "Most Popular", "Best Location", "Hidden Gem"];

const buildRoadTripStayDisplayName = ({
  stopCity = "Route stop",
  type = "Hotel",
  budget = "Mid-range",
  candidateName = "",
}) => {
  const safeStopCity = String(stopCity || "Route stop").trim() || "Route stop";
  const safeType = String(type || "Hotel").trim() || "Hotel";
  const safeBudget = String(budget || "Mid-range").trim() || "Mid-range";
  const trimmedCandidate = String(candidateName || "").trim();

  if (safeType === "Camping") return `Camping stays in ${safeStopCity}`;
  if (safeType === "Glamping") return `Glamping stays in ${safeStopCity}`;
  if (safeType === "Campervan stopover") return `Stopovers in ${safeStopCity}`;

  if (trimmedCandidate && /hostel/i.test(trimmedCandidate)) {
    return `${safeBudget} stays in ${safeStopCity}`;
  }

  return `${safeBudget} stays in ${safeStopCity}`;
};

const extractRoadTripStopCity = (value = "") => {
  const cleaned = cleanDirectionsDestination(String(value || "").trim());
  if (!cleaned) return "Route stop";

  const inMatch = cleaned.match(/\bin\s+([A-Z][A-Za-z' -]{1,60})/);
  if (inMatch?.[1]) {
    return inMatch[1]
      .split(/,|\.|;|\bincluding\b|\bwith\b/i)[0]
      .trim();
  }

  return cleaned
    .split(/,|\.|;|\bincluding\b|\bwith\b/i)[0]
    .trim() || "Route stop";
};

const buildRoadTripOvernightFallback = ({
  stopCity = "Route stop",
  country = "",
  budget = "Mid-range",
  features = [],
  checkIn = "",
  checkOut = "",
}) => {
  const featureSet = new Set(Array.isArray(features) ? features : []);

  const preferredTypes = [
    featureSet.has("Hotels") ? "Hotel" : null,
    featureSet.has("B&Bs") ? "B&B" : null,
    featureSet.has("Camping sites") ? "Camping" : null,
    featureSet.has("Glamping sites") ? "Glamping" : null,
    featureSet.has("Campervan stopovers") ? "Campervan stopover" : null,
  ].filter(Boolean);

  const defaultTypes = ["Hotel", "B&B", "Camping", "Glamping", "Campervan stopover", "Hostel"];
  const combinedTypes = [...new Set([...preferredTypes, ...defaultTypes])].slice(0, 4);

  const budgetPriceMap = {
    Budget: {
      Hotel: "EUR60-EUR95 / night",
      "B&B": "EUR55-EUR90 / night",
      Camping: "EUR18-EUR35 / night",
      Glamping: "EUR50-EUR95 / night",
      "Campervan stopover": "EUR10-EUR25 / night",
      Hostel: "EUR22-EUR45 / night",
    },
    "Mid-range": {
      Hotel: "EUR95-EUR160 / night",
      "B&B": "EUR85-EUR145 / night",
      Camping: "EUR25-EUR45 / night",
      Glamping: "EUR80-EUR160 / night",
      "Campervan stopover": "EUR15-EUR35 / night",
      Hostel: "EUR35-EUR65 / night",
    },
    Luxury: {
      Hotel: "EUR180-EUR320 / night",
      "B&B": "EUR150-EUR260 / night",
      Camping: "EUR35-EUR70 / night",
      Glamping: "EUR170-EUR320 / night",
      "Campervan stopover": "EUR20-EUR45 / night",
      Hostel: "EUR45-EUR90 / night",
    },
  };

  const typeNameMap = {
    Hotel: `${stopCity} City Hotel`,
    "B&B": `${stopCity} Guesthouse & B&B`,
    Camping: `${stopCity} Camping Park`,
    Glamping: `${stopCity} Glamping Retreat`,
    "Campervan stopover": `${stopCity} Motorhome Stopover`,
    Hostel: `${stopCity} Central Hostel`,
  };

  const buildRoadTripBookingLink = () => {
    return buildBookingLocationLink(stopCity, country, checkIn, checkOut, budget);
  };

  return ROAD_TRIP_OVERNIGHT_LABELS.map((label, index) => {
    const type = combinedTypes[index] || defaultTypes[index] || "Hotel";
    const priceRange = budgetPriceMap[budget]?.[type] || budgetPriceMap["Mid-range"][type] || "Price range varies";
    const stayName = typeNameMap[type] || `${stopCity} Stay`;
    const descriptionMap = {
      Hotel: `Comfortable hotel base for exploring ${stopCity}.`,
      "B&B": `Well-rated guesthouse option with easy access around ${stopCity}.`,
      Camping: `Practical camping option close to ${stopCity} for an overnight stop.`,
      Glamping: `Upgraded outdoor stay with extra comfort near ${stopCity}.`,
      "Campervan stopover": `Convenient overnight stop with access to ${stopCity}.`,
      Hostel: `Simple, central stay option for a short stop in ${stopCity}.`,
    };
    return {
      label,
      type,
      name: stayName,
      priceRange,
      why: descriptionMap[type] || `${label} for ${stopCity}.`,
      bookingLink: buildRoadTripBookingLink(),
    };
  });
};

const normalizeTransportItems = (value) => {
  if (!Array.isArray(value)) return [];
  return value
    .map((item) => {
      if (typeof item === "string") return { name: item, note: "" };
      if (item && typeof item === "object") {
        const name = item.name || item.title || item.route || "Service";
        const note = item.note || item.details || item.info || "";
        return { name: String(name), note: String(note) };
      }
      return null;
    })
    .filter(Boolean);
};

const normalizeUrl = (value) => {
  if (typeof value !== "string") return "";
  let v = value.trim();
  if (!v) return "";

  // Extract URL from markdown-style links: [label](url)
  const mdMatch = v.match(/\[[^\]]+\]\((https?:\/\/[^\s)]+)\)/i);
  if (mdMatch && mdMatch[1]) v = mdMatch[1];

  // Extract first absolute URL if embedded in free text.
  const absoluteMatch = v.match(/https?:\/\/[^\s)]+/i);
  if (absoluteMatch && absoluteMatch[0]) return absoluteMatch[0];

  if (/^www\./i.test(v)) return `https://${v}`;
  if (/^(maps\.)?google\./i.test(v) || /^maps\.google\.com/i.test(v)) return `https://${v}`;

  return "";
};

const parseNameAndUrl = (raw) => {
  if (typeof raw !== "string") return { name: "", mapLink: "" };
  const text = raw.trim();
  if (!text) return { name: "", mapLink: "" };

  const markdown = text.match(/^\s*\[([^\]]+)\]\((https?:\/\/[^\s)]+)\)\s*$/i);
  if (markdown) {
    return {
      name: markdown[1].trim() || "Place",
      mapLink: normalizeUrl(markdown[2]),
    };
  }

  const extractedUrl = normalizeUrl(text);
  if (extractedUrl) {
    const cleanedName = text
      .replace(/https?:\/\/[^\s)]+/gi, "")
      .replace(/[-–|•]+\s*$/, "")
      .trim();
    return {
      name: cleanedName || "Place",
      mapLink: extractedUrl,
    };
  }

  return { name: text, mapLink: "" };
};

const extractMapsDestinationFromUrl = (link) => {
  if (!link || typeof link !== "string") return "";
  try {
    const url = new URL(link);
    const candidates = [
      url.searchParams.get("destination"),
      url.searchParams.get("q"),
      url.searchParams.get("query"),
      url.searchParams.get("daddr"),
    ].filter(Boolean);

    if (candidates.length > 0) {
      return String(candidates[0]).replace(/\+/g, " ").trim();
    }

    // Extract place names from URLs like /maps/place/<name>/...
    const placeMatch = url.pathname.match(/\/place\/([^/]+)/i);
    if (placeMatch && placeMatch[1]) {
      return decodeURIComponent(placeMatch[1]).replace(/\+/g, " ").trim();
    }
  } catch {
    return "";
  }
  return "";
};

const splitNameAndDetail = (value) => {
  const raw = typeof value === "string" ? value.trim() : "";
  if (!raw) return { name: "", detail: "" };

  // Support separators with and without surrounding spaces, including colon.
  const parts = raw.split(/\s*[\u2013\u2014\-|:]\s*|\s*\|\s*/);
  if (parts.length <= 1) return { name: raw, detail: "" };

  const name = (parts[0] || "").trim();
  const detail = parts.slice(1).join(" - ").trim();
  return { name: name || raw, detail };
};

const cleanDirectionsDestination = (value) => {
  if (typeof value !== "string") return "";
  return value
    .replace(/^(visit|explore|discover|stop at)\s+/i, "")
    .replace(/^(scenic drive (along|through)\s+)/i, "")
    .replace(/^(walking tour (of|around)\s+)/i, "")
    .replace(/^the\s+/i, "")
    .trim();
};

const buildGooglePlaceLink = (destination) => {
  const cleanDestination = typeof destination === "string" ? destination.trim() : "";
  if (!cleanDestination) return "";
  return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(cleanDestination)}`;
};

const resolveGooglePlaceLink = (candidateLink, fallbackDestination) => {
  const normalized = normalizeUrl(candidateLink || "");
  const fallback = buildGooglePlaceLink(fallbackDestination);
  if (!normalized) return fallback;

  try {
    const url = new URL(normalized);
    const host = url.hostname.toLowerCase();
    if (!host.includes("google.")) return normalized;

    const path = url.pathname.toLowerCase();
    const destination =
      extractMapsDestinationFromUrl(normalized) ||
      (typeof fallbackDestination === "string" ? fallbackDestination.trim() : "");

    if (path.includes("/maps/dir") || url.searchParams.get("destination") || url.searchParams.get("daddr")) {
      return buildGooglePlaceLink(destination);
    }

    return normalized;
  } catch {
    return fallback;
  }
};

const normalizeEatPlace = (item) => {
  if (!item) return null;
  if (typeof item === "string") {
    const parsed = parseNameAndUrl(item);
    return {
      name: parsed.name || "Place",
      cuisine: "",
      price: "",
      distance: "",
      mapLink: parsed.mapLink || "",
    };
  }
  if (typeof item !== "object") return null;

  const rawName = item.name || item.title || item.place || item.restaurant || item.establishment || "";
  const nameAndUrl = parseNameAndUrl(String(rawName || ""));
  const mapLink = normalizeUrl(
    item.mapLink || item.link || item.url || item.website || item.maps || item.googleMaps || item.map || item.directions || ""
  ) || nameAndUrl.mapLink;

  return {
    name: nameAndUrl.name || item.name || item.title || "Place",
    cuisine: item.cuisine || item.type || "",
    price: item.price || item.cost || "",
    distance: item.distance || item.walk || "",
    mapLink,
  };
};

const normalizeMealList = (items) => {
  if (!Array.isArray(items)) return [];
  return items.map(normalizeEatPlace).filter(Boolean);
};

const canonicalEatName = (item) => String(item?.name || "").trim().toLowerCase();

const firstUniqueEat = (candidates, usedNames) => {
  for (const item of candidates || []) {
    const key = canonicalEatName(item);
    if (!key) continue;
    if (usedNames.has(key)) continue;
    usedNames.add(key);
    return item;
  }
  return null;
};

const buildNearbyEatFallback = ({ destination = "city center", budget = "Mid-range", activity = {} }) => {
  const placeBase = activity.location || activity.title || destination || "city center";
  const mapFor = (query) => `https://maps.google.com/?q=${encodeURIComponent(query)}`;

  const profiles = {
    Budget: {
      breakfast: [
        { name: `Corner bakery near ${placeBase}`, cuisine: "Pastries & coffee", price: "EUR5-10", distance: "4-8 min walk" },
        { name: `Local cafe by ${placeBase}`, cuisine: "Budget breakfast", price: "EUR6-12", distance: "5-10 min walk" },
      ],
      lunch: [
        { name: `Market lunch spot near ${placeBase}`, cuisine: "Local plates", price: "EUR8-15", distance: "5-10 min walk" },
        { name: `Casual eatery around ${placeBase}`, cuisine: "Street food", price: "EUR7-14", distance: "6-12 min walk" },
      ],
      dinner: [
        { name: `Trattoria near ${placeBase}`, cuisine: "Regional comfort food", price: "EUR12-22", distance: "6-10 min walk" },
        { name: `Neighborhood dinner bar by ${placeBase}`, cuisine: "Local classics", price: "EUR10-20", distance: "8-12 min walk" },
      ],
    },
    "Mid-range": {
      breakfast: [
        { name: `Brunch cafe near ${placeBase}`, cuisine: "Cafe brunch", price: "EUR10-18", distance: "4-8 min walk" },
        { name: `Artisan coffee house by ${placeBase}`, cuisine: "Breakfast & pastries", price: "EUR9-16", distance: "5-9 min walk" },
      ],
      lunch: [
        { name: `Bistro near ${placeBase}`, cuisine: "Seasonal lunch menu", price: "EUR15-28", distance: "5-10 min walk" },
        { name: `Local kitchen by ${placeBase}`, cuisine: "Regional specialties", price: "EUR14-26", distance: "6-12 min walk" },
      ],
      dinner: [
        { name: `Evening bistro near ${placeBase}`, cuisine: "Modern regional", price: "EUR28-45", distance: "6-10 min walk" },
        { name: `Chef-led dining room by ${placeBase}`, cuisine: "Seasonal tasting plates", price: "EUR30-50", distance: "8-12 min walk" },
      ],
    },
    Luxury: {
      breakfast: [
        { name: `Hotel salon near ${placeBase}`, cuisine: "Luxury breakfast", price: "EUR18-30", distance: "5-10 min walk" },
        { name: `Signature cafe by ${placeBase}`, cuisine: "Champagne brunch", price: "EUR20-34", distance: "6-10 min walk" },
      ],
      lunch: [
        { name: `Fine lunch room near ${placeBase}`, cuisine: "Refined regional", price: "EUR28-45", distance: "5-10 min walk" },
        { name: `Terrace restaurant by ${placeBase}`, cuisine: "Chef specials", price: "EUR30-48", distance: "6-12 min walk" },
      ],
      dinner: [
        { name: `Signature dining near ${placeBase}`, cuisine: "Tasting menu", price: "EUR70-120", distance: "8-15 min taxi" },
        { name: `Premier restaurant by ${placeBase}`, cuisine: "Fine dining", price: "EUR80-140", distance: "10-15 min taxi" },
      ],
    },
  };

  const chosen = profiles[budget] || profiles["Mid-range"];
  return {
    breakfast: chosen.breakfast.map((item) => ({ ...item, mapLink: mapFor(`${item.name} ${destination}`) })),
    lunch: chosen.lunch.map((item) => ({ ...item, mapLink: mapFor(`${item.name} ${destination}`) })),
    dinner: chosen.dinner.map((item) => ({ ...item, mapLink: mapFor(`${item.name} ${destination}`) })),
  };
};

const normalizeNearbyEats = (activity, context = {}) => {
  const src = activity && typeof activity === "object" ? activity : {};

  const nearbyEatsRaw = src.nearbyEats && typeof src.nearbyEats === "object" ? src.nearbyEats : {};
  const breakfast = normalizeMealList(nearbyEatsRaw.breakfast || src.breakfastEats || src.breakfastSpots);
  const lunch = normalizeMealList(nearbyEatsRaw.lunch || src.lunchEats || src.lunchSpots);
  const dinner = normalizeMealList(nearbyEatsRaw.dinner || src.dinnerEats || src.dinnerSpots);

  // Backward compatibility with older model outputs.
  const nearbyRestaurants = Array.isArray(src.nearbyRestaurants) ? src.nearbyRestaurants.map(normalizeEatPlace).filter(Boolean) : [];

  const fallback = buildNearbyEatFallback({
    destination: context.destination,
    budget: context.budget,
    activity: src,
  });

  const breakfastCandidates = breakfast.length > 0 ? breakfast : nearbyRestaurants.length > 0 ? nearbyRestaurants : fallback.breakfast;
  const lunchCandidates = lunch.length > 0 ? lunch : nearbyRestaurants.length > 0 ? nearbyRestaurants : fallback.lunch;
  const dinnerCandidates = dinner.length > 0 ? dinner : nearbyRestaurants.length > 0 ? nearbyRestaurants : fallback.dinner;

  // Always return exactly 3 nearby eats total: one each for breakfast/lunch/dinner,
  // and never reuse the same place across meal sections.
  const usedNames = new Set();
  const breakfastPick =
    firstUniqueEat(breakfastCandidates, usedNames) ||
    firstUniqueEat([...(fallback.breakfast || []), ...(fallback.lunch || []), ...(fallback.dinner || [])], usedNames);
  const lunchPick =
    firstUniqueEat(lunchCandidates, usedNames) ||
    firstUniqueEat([...(fallback.lunch || []), ...(fallback.dinner || []), ...(fallback.breakfast || [])], usedNames);
  const dinnerPick =
    firstUniqueEat(dinnerCandidates, usedNames) ||
    firstUniqueEat([...(fallback.dinner || []), ...(fallback.lunch || []), ...(fallback.breakfast || [])], usedNames);

  return {
    breakfast: breakfastPick ? [breakfastPick] : [],
    lunch: lunchPick ? [lunchPick] : [],
    dinner: dinnerPick ? [dinnerPick] : [],
  };
};

const DINNER_BY_BUDGET = {
  Budget: {
    title: "Street-food and local trattoria dinner",
    cost: "EUR15-30 per person",
    tip: "Look for set menus and early-evening specials for better value.",
    places: [
      { name: "Neighborhood trattoria", cuisine: "Local comfort food", price: "EUR10-18", distance: "5-10 min walk" },
      { name: "Street-food market", cuisine: "Regional specialties", price: "EUR6-14", distance: "10-15 min walk" },
    ],
  },
  "Mid-range": {
    title: "Classic bistro dinner with local specialties",
    cost: "EUR30-60 per person",
    tip: "Reserve a table in advance and ask for the house specialty.",
    places: [
      { name: "Modern local bistro", cuisine: "Contemporary regional", price: "EUR18-35", distance: "5-10 min walk" },
      { name: "Chef-led neighborhood restaurant", cuisine: "Seasonal tasting plates", price: "EUR25-40", distance: "10-15 min walk" },
    ],
  },
  Luxury: {
    title: "Fine-dining dinner experience",
    cost: "EUR70-140 per person",
    tip: "Choose the tasting menu with wine pairing for the full experience.",
    places: [
      { name: "Signature fine-dining restaurant", cuisine: "Haute cuisine", price: "EUR65-110", distance: "10-15 min taxi" },
      { name: "Michelin-guide destination", cuisine: "Modern tasting menu", price: "EUR80-140", distance: "10-20 min taxi" },
    ],
  },
};

const buildBudgetDinnerActivity = ({ destination = "the city center", budget = "Mid-range", dayNumber = 1 }) => {
  const profile = DINNER_BY_BUDGET[budget] || DINNER_BY_BUDGET["Mid-range"];
  const destinationLabel = destination || "the city center";
  const mapQuery = encodeURIComponent(`${destinationLabel} dinner restaurants`);
  const nearby = profile.places.map((place) => ({
    ...place,
    mapLink: `https://maps.google.com/?q=${encodeURIComponent(`${place.name} ${destinationLabel}`)}`,
  }));

  return {
    time: "8:00 PM",
    title: profile.title,
    category: "Dining",
    description: `At 8:00 PM, enjoy a budget-tailored dinner in ${destinationLabel}. Focus on authentic flavors and venues that match a ${budget} travel style.`,
    culturalStory:
      dayNumber % 2 === 0
        ? "Dinner culture here is social and unhurried, with local dishes often tied to family recipes and neighborhood identity."
        : "Evening meals are a key ritual in local life, where signature dishes reflect seasonal produce and regional culinary traditions.",
    location: `${destinationLabel} dining district`,
    duration: "1.5-2 hours",
    cost: profile.cost,
    tip: profile.tip,
    specialties: "Ask for the local signature dish and a regional dessert.",
    theme: "Food & Culture",
    mapLink: `https://maps.google.com/?q=${mapQuery}`,
    nearbyEats: {
      breakfast: [],
      lunch: [],
      dinner: nearby,
    },
  };
};

const hasEightPmDinner = (activities = []) => {
  const normalize = (value) => String(value || "").toLowerCase().replace(/\s+/g, " ").trim();
  return activities.some((activity) => {
    if (!activity || typeof activity !== "object") return false;
    const t = normalize(activity.time);
    const category = normalize(activity.category);
    const title = normalize(activity.title);
    const mentionsEight =
      t.includes("8:00 pm") ||
      t.includes("8 pm") ||
      t.includes("20:00") ||
      title.includes("8:00 pm") ||
      title.includes("8 pm");
    const isDinnerLike = category.includes("dining") || category.includes("food") || title.includes("dinner");
    return mentionsEight && isDinnerLike;
  });
};

const parseTimeToMinutes = (value) => {
  const raw = String(value || "").trim().toLowerCase();
  if (!raw) return Number.POSITIVE_INFINITY;

  const ampm = raw.match(/(\d{1,2})(?::(\d{2}))?\s*(am|pm)/i);
  if (ampm) {
    let hour = Number(ampm[1]);
    const minute = Number(ampm[2] || 0);
    const meridiem = ampm[3].toLowerCase();
    if (meridiem === "pm" && hour !== 12) hour += 12;
    if (meridiem === "am" && hour === 12) hour = 0;
    return hour * 60 + minute;
  }

  const twentyFour = raw.match(/\b(\d{1,2}):(\d{2})\b/);
  if (twentyFour) return Number(twentyFour[1]) * 60 + Number(twentyFour[2]);

  return Number.POSITIVE_INFINITY;
};

const sortActivitiesByTime = (activities = []) =>
  [...activities].sort((left, right) => parseTimeToMinutes(left?.time) - parseTimeToMinutes(right?.time));

const normalizeArrivalTransport = (raw, destination = "") => {
  const fallback = selectAirportTransport(destination);
  const src = raw && typeof raw === "object" ? raw : {};

  const busesRaw = src.buses && typeof src.buses === "object" ? src.buses : {};
  const taxisRaw = src.taxis;
  const taxiObj =
    taxisRaw && typeof taxisRaw === "object"
      ? {
          ranks: taxisRaw.ranks || taxisRaw.stands || "",
          cost: taxisRaw.cost || taxisRaw.fare || taxisRaw.price || "",
          prebook: Array.isArray(taxisRaw.prebook)
            ? taxisRaw.prebook
            : Array.isArray(taxisRaw.apps)
              ? taxisRaw.apps
              : Array.isArray(taxisRaw.options)
                ? taxisRaw.options
                : [],
        }
      : {
          ranks: "",
          cost: typeof taxisRaw === "string" ? taxisRaw : "",
          prebook: [],
        };

  const normalized = {
    title: src.title || fallback.title || "Airport to City Center",
    arrivalLabel: src.arrivalLabel || fallback.arrivalLabel || "Upon Arrival",
    airports: Array.isArray(src.airports) ? src.airports.map((a) => String(a)) : Array.isArray(fallback.airports) ? fallback.airports : [],
    trains: normalizeTransportItems(src.trains || src.train),
    metro: normalizeTransportItems(src.metro || src.subway),
    buses: {
      express: normalizeTransportItems(busesRaw.express || src.expressBuses || src.airportExpress),
      routes: normalizeTransportItems(busesRaw.routes || busesRaw.local || src.localBuses),
    },
    taxis: {
      ranks: taxiObj.ranks || fallback.taxis?.ranks || "",
      cost: taxiObj.cost || fallback.taxis?.cost || "",
      prebook: taxiObj.prebook.length > 0 ? taxiObj.prebook : Array.isArray(fallback.taxis?.prebook) ? fallback.taxis.prebook : [],
    },
    shuttles: normalizeTransportItems(src.shuttles || src.hotelShuttles),
    carRental: {
      location: src.carRental?.location || src.carHire?.location || fallback.carRental?.location || "",
      companies: Array.isArray(src.carRental?.companies)
        ? src.carRental.companies
        : Array.isArray(src.carHire?.companies)
          ? src.carHire.companies
          : Array.isArray(fallback.carRental?.companies)
            ? fallback.carRental.companies
            : [],
    },
    note: src.note || src.notes || fallback.note || "",
  };

  if (normalized.trains.length === 0 && Array.isArray(fallback.trains)) normalized.trains = fallback.trains;
  if (normalized.metro.length === 0 && Array.isArray(fallback.metro)) normalized.metro = fallback.metro;
  if (normalized.buses.express.length === 0 && Array.isArray(fallback.buses?.express)) normalized.buses.express = fallback.buses.express;
  if (normalized.buses.routes.length === 0 && Array.isArray(fallback.buses?.routes)) normalized.buses.routes = fallback.buses.routes;
  if (normalized.shuttles.length === 0 && Array.isArray(fallback.shuttles)) normalized.shuttles = fallback.shuttles;

  const railStatus = resolveVerifiedRailStatus({
    destination,
    title: normalized.title,
  });

  if (railStatus.status !== "allowed") {
    normalized.trains = [];
    normalized.metro = [];
    normalized.note = normalized.note ? `${normalized.note} ${railStatus.note}` : railStatus.note;
  }

  return normalized;
};

const normalizeMultiTransportOption = (value, fallbackName = "Option") => {
  if (!value) return null;
  if (typeof value === "string") {
    return { name: fallbackName, duration: "", cost: "", tip: value };
  }
  if (typeof value !== "object") return null;

  const name = value.name || value.title || value.service || fallbackName;
  const duration = value.duration || value.time || "";
  const cost = value.cost || value.fare || value.price || "";
  const tip = value.tip || value.note || value.details || "";
  return {
    name: String(name),
    duration: String(duration),
    cost: String(cost),
    tip: String(tip),
  };
};

const sanitizeMultiArrivalTransport = (arrivalTransport, cityName = "", country = "") => {
  const src = arrivalTransport && typeof arrivalTransport === "object" ? arrivalTransport : {};

  const bus = normalizeMultiTransportOption(src.bus, "Airport Bus");
  const taxi = normalizeMultiTransportOption(src.taxi, "Taxi");
  let train = normalizeMultiTransportOption(src.train, "Train");
  let note = String(src.note || "");

  const railStatus = resolveVerifiedRailStatus({
    city: cityName,
    country,
    title: src.title || "",
  });

  if (railStatus.status !== "allowed") {
    train = null;
    note = note ? `${note} ${railStatus.note}` : railStatus.note;
  }

  const ensuredBus =
    bus ||
    {
      name: "Airport Coach/Bus",
      duration: "Varies by destination",
      cost: "Check operator fares",
      tip: "Use official airport bus or coach operators shown in Arrivals.",
    };

  const ensuredTaxi =
    taxi ||
    {
      name: "Taxi / Chauffeur",
      duration: "Traffic dependent",
      cost: "Metered or pre-booked fixed fare",
      tip: "Use official taxi ranks or pre-booked private chauffeur providers.",
    };

  return {
    title: src.title || `${cityName} Airport -> City Center`,
    train,
    bus: ensuredBus,
    taxi: ensuredTaxi,
    note,
  };
};

// --- 3. REUSABLE DESTINATION INPUT COMPONENT ---
function DestinationInputs({ onDataChange, initialData, language = "en" }) {
  const [selectedContinent, setSelectedContinent] = useState(initialData.continent || "");
  const [countryInput, setCountryInput] = useState(initialData.country || "");
  const [selectedCountry, setSelectedCountry] = useState(initialData.country || "");
  const [selectedCity, setSelectedCity] = useState(initialData.city || "");
  const [tripDuration, setTripDuration] = useState(initialData.duration || "");
  const [tripStartDate, setTripStartDate] = useState(initialData.startDate || "");
  const singleCityStayDates = useMemo(
    () => buildSingleCityStayDates(tripStartDate, tripDuration),
    [tripStartDate, tripDuration]
  );

  const [isInputFocused, setIsInputFocused] = useState(false);
  const plannerText = useMemo(() => ({ ...PLANNER_TEXT.en, ...getPlannerText(language) }), [language]);

  const isReadOnly = !!selectedContinent;

  useEffect(() => {
    const safeDuration = String(clampSingleTripDays(tripDuration));
    onDataChange({
      country: selectedCountry,
      city: selectedCity,
      duration: safeDuration,
      startDate: tripStartDate,
      continent: selectedContinent,
    });
  }, [selectedCountry, selectedCity, tripDuration, tripStartDate, selectedContinent, onDataChange]);

  const filteredCountries = useMemo(() => {
    let baseCountries = allCountries;
    if (selectedContinent) baseCountries = DESTINATION_DATA[selectedContinent] || [];
    const searchLower = countryInput.toLowerCase();
    let matches = baseCountries;
    if (searchLower) matches = baseCountries.filter((country) => country.toLowerCase().startsWith(searchLower));
    matches.sort((a, b) => a.localeCompare(b));
    return matches.slice(0, 40);
  }, [countryInput, selectedContinent]);

  const handleContinentSelect = (continentKey) => {
    const newContinent = selectedContinent === continentKey ? "" : continentKey;
    setSelectedContinent(newContinent);
    setSelectedCountry("");
    setCountryInput("");
    setSelectedCity("");
  };

  const handleCountrySelect = (country) => {
    setSelectedCountry(country);
    setCountryInput(country);
    setSelectedCity("");
    setIsInputFocused(false);
  };

  return (
    <div className="bg-white shadow-md rounded-2xl p-4 sm:p-5 mb-5 border-l-4 border-green-500">

      <div className="flex flex-wrap gap-2 pb-4">
        {allContinentsKeys.map((continentKey) => (
          <button
            key={continentKey}
            onClick={() => handleContinentSelect(continentKey)}
            className={`
              px-3 sm:px-4 py-2 text-xs sm:text-sm font-medium rounded-full transition duration-200 ease-in-out flex items-center
              ${
                selectedContinent === continentKey
                  ? "bg-green-600 text-white shadow-md ring-2 ring-green-400"
                  : "bg-gray-200 text-gray-800 hover:bg-green-100 hover:text-green-800"
              }
            `}
          >
            {continentKey === "Americas" && <span className="mr-1">🌎</span>}
            {continentKey === "Europe" && <span className="mr-1">🇪🇺</span>}
            {continentKey === "Asia" && <span className="mr-1">🍜</span>}
            {continentKey === "Oceania" && <span className="mr-1">🏝️</span>}
            {continentKey === "Africa" && <span className="mr-1">🦁</span>}
            {continentKey === "Americas"
              ? plannerText.continentAmericas
              : continentKey === "Europe"
              ? plannerText.continentEurope
              : continentKey === "Asia"
              ? plannerText.continentAsia
              : continentKey === "Oceania"
              ? plannerText.continentOceania
              : continentKey === "Africa"
              ? plannerText.continentAfrica
              : CONTINENT_DISPLAY_MAP[continentKey] || continentKey}
          </button>
        ))}
        {selectedContinent && (
          <button
            onClick={() => handleContinentSelect("")}
            className="px-3 sm:px-4 py-2 text-xs sm:text-sm font-medium rounded-full bg-red-100 text-red-700 hover:bg-red-200 transition duration-200 ease-in-out ml-0 sm:ml-4 flex items-center"
          >
            <Globe className="h-3 w-3 sm:h-4 sm:w-4 mr-1" /> {plannerText.clearFilterLabel}
          </button>
        )}
      </div>

      <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-2">{plannerText.countryLabel}</label>
      <div className="relative mb-4">
        <div className="relative">
          <input
            type="text"
            value={countryInput}
            readOnly={isReadOnly}
            onChange={(e) => {
              setCountryInput(e.target.value);
              setSelectedCountry("");
              setSelectedCity("");
            }}
            onFocus={() => setIsInputFocused(true)}
            onBlur={() => setTimeout(() => setIsInputFocused(false), 100)}
            placeholder={
              isReadOnly
                ? plannerText.selectFromListBelowPlaceholder
                : selectedContinent
                ? plannerText.searchInContinentPlaceholder.replace(
                    "{continent}",
                    plannerText[`continent${selectedContinent}`] || CONTINENT_DISPLAY_MAP[selectedContinent]
                  )
                : plannerText.countrySearchPlaceholder
            }
            className="w-full border border-gray-300 rounded-xl p-3 pl-10 focus:ring-green-500 focus:border-green-500 transition duration-150 text-sm sm:text-base"
          />
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
        </div>

        {(isInputFocused || countryInput) && selectedCountry === "" && filteredCountries.length > 0 && (
          <ul className="absolute z-10 w-full bg-white border border-gray-300 rounded-xl shadow-lg mt-1 max-h-60 overflow-y-auto">
            {filteredCountries.map((country) => (
              <li
                key={country}
                className="p-2 sm:p-3 cursor-pointer hover:bg-green-50 transition duration-150 text-gray-800 flex justify-between items-center text-xs sm:text-sm"
                onMouseDown={(e) => {
                  e.preventDefault();
                  handleCountrySelect(country);
                }}
              >
                {country}
                <ChevronDown className="h-3 w-3 sm:h-4 sm:w-4 text-green-500 rotate-[-90deg]" />
              </li>
            ))}
          </ul>
        )}
        {selectedCountry && (
          <p className="mt-2 text-xs sm:text-sm text-green-600 font-semibold bg-green-50 p-2 rounded-lg border border-green-200">
            {plannerText.selectedCountryLabel}: {selectedCountry}
          </p>
        )}
      </div>

      <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-2">{plannerText.cityTownLabel}</label>
      <input
        type="text"
        value={selectedCity}
        onChange={(e) => setSelectedCity(e.target.value)}
        placeholder={plannerText.cityExamplePlaceholder}
        className="w-full border border-gray-300 rounded-xl p-3 mb-4 focus:ring-green-500 focus:border-green-500 transition duration-150 text-sm sm:text-base"
      />

      <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-2 mt-4">
        <Clock className="h-3 w-3 sm:h-4 sm:w-4 inline mr-1 text-gray-600" /> {plannerText.numberDaysLabel}
      </label>
      <input
        type="number"
        value={tripDuration}
        onChange={(e) => setTripDuration(String(clampSingleTripDays(e.target.value)))}
        placeholder="1-2"
        className="w-full border border-gray-300 rounded-xl p-3 focus:ring-green-500 focus:border-green-500 transition duration-150 text-sm sm:text-base"
        min="1"
        max={ITINERARY_DAY_LIMIT}
        inputMode="numeric"
      />

      <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-2 mt-4">{plannerText.tripStartDateOptionalLabel}</label>
      <input
        type="date"
        value={tripStartDate}
        onChange={(e) => setTripStartDate(e.target.value)}
        className="w-full border border-gray-300 rounded-xl p-3 focus:ring-green-500 focus:border-green-500 transition duration-150 text-sm sm:text-base"
      />

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-4">
        <div>
          <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-2">{plannerText.checkInLabel}</label>
          <input
            type="date"
            value={singleCityStayDates.checkInDate}
            readOnly
            disabled
            className="w-full border border-gray-200 bg-gray-50 text-gray-500 rounded-xl p-3 text-sm sm:text-base"
          />
        </div>
        <div>
          <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-2">{plannerText.checkOutLabel}</label>
          <input
            type="date"
            value={singleCityStayDates.checkOutDate}
            readOnly
            disabled
            className="w-full border border-gray-200 bg-gray-50 text-gray-500 rounded-xl p-3 text-sm sm:text-base"
          />
        </div>
      </div>
    </div>
  );
}

// --- Policy Content Data ---
const policyData = {
  privacy: {
    title: "Privacy Policy",
    content: (
      <>
        <p className="mb-4">Effective Date: October 24, 2025</p>
        <p className="mb-4">
          <strong>AppiHolidays</strong> is committed to protecting your privacy. This policy explains what information is collected,
          how it is used, and how it is protected.
        </p>
        <h3 className="text-xl font-semibold mt-6 mb-2">Information Collected</h3>
        <ul className="list-disc list-inside space-y-1 ml-4 mb-4">
          <li>
            <strong>Planning Data:</strong> We collect the destination, duration, pace, budget, interests, and extra notes you provide
            to generate your itinerary.
          </li>
          <li>
            <strong>No PII:</strong> We do not collect personally identifiable information (PII) such as your name, email address, or
            exact location.
          </li>
          <li>
            <strong>Local Storage:</strong> Your planning data and generated itineraries are saved locally in your browser&apos;s <strong>Local Storage</strong> for your convenience. This data is <strong>not</strong> stored on our servers.
          </li>
          <li>
            <strong>Firebase Usage:</strong> AppiHolidays uses Firebase (Firestore via secure server-side APIs) for operational features including analytics insights,
            itinerary history deduplication, and nearby eats caching. Public planning form content remains primarily browser-side unless submitted to these features.
          </li>
        </ul>
        <h3 className="text-xl font-semibold mt-6 mb-2">Data Processing</h3>
        <p className="mb-4">
          All itinerary generation is powered by the Google Gemini API. Your planning data is sent to this API to produce the structured
          itinerary. Communications are secured via HTTPS.
        </p>
      </>
    ),
  },
  cookies: {
    title: "Cookies Policy",
    content: (
      <>
        <p className="mb-4">Effective Date: October 24, 2025</p>
        <p className="mb-4">
          This policy explains how we use "cookies" (which, in this single-file application, refers primarily to <strong>Local Storage</strong> data) to provide a seamless experience.
        </p>
        <h3 className="text-xl font-semibold mt-6 mb-2">Functional Local Storage Data</h3>
        <p className="mb-4">We use functional client-side storage to remember your state:</p>
        <ul className="list-disc list-inside space-y-1 ml-4 mb-4">
          <li><strong>Form Data</strong> (<code>appi.form.v1</code>): Saves your selected country, duration, pace, budget, and interests.</li>
          <li><strong>Itinerary Data</strong> (<code>appi.itinerary.v1</code>): Saves the last successfully generated itinerary JSON.</li>
        </ul>
      </>
    ),
  },
  security: {
    title: "Security Policy",
    content: (
      <>
        <p className="mb-4">Effective Date: October 3, 2026</p>
        <p className="mb-4">
          AppiHolidays applies layered controls to protect user requests and platform integrations.
        </p>
        <h3 className="text-xl font-semibold mt-6 mb-2">Security Controls</h3>
        <ul className="list-disc list-inside space-y-1 ml-4 mb-4">
          <li>HTTPS-only API communication between browser, AppiHolidays endpoints, and approved upstream services.</li>
          <li>Server-side secrets and credentials are stored in environment variables, not exposed to client code.</li>
          <li>Admin dashboard access is protected by signed session cookies and restricted admin authentication checks.</li>
          <li>Booking webhook ingestion supports optional shared-secret validation headers for trusted server-to-server calls.</li>
          <li>Firebase service account access is server-side only; client browsers do not receive Firebase admin credentials.</li>
        </ul>
        <h3 className="text-xl font-semibold mt-6 mb-2">Reporting</h3>
        <p className="mb-4">
          If you identify a potential security issue, report it to the Appi team privately with reproduction details so we can investigate quickly.
        </p>
      </>
    ),
  },
  about: {
    title: "About AppiHolidays",
    content: (
      <>
        <p className="mb-4">
          AppiHolidays is part of the Appi environment: a growing set of connected products built to make planning, booking, and travel decisions simpler.
        </p>
        <p className="mb-4">
          The Appi environment currently includes <strong>AppiHolidays</strong> and <strong>AppiHotels</strong>, with <strong>AppiFriends</strong> coming soon.
          Together, these products are designed to deliver practical tools across trip inspiration, accommodation discovery, and lifestyle experiences.
        </p>
        <p className="mb-4">
          Appi is built in Ireland and is entirely Irish owned. The platform now has over 2 million integrated properties, with inventory growing daily.
        </p>
        <h3 className="text-xl font-semibold mt-6 mb-2">How It Works</h3>
        <p className="mb-4">
          AppiHolidays leverages AI-powered APIs to process your preferences and produce a structured itinerary. The planner is designed to act as a
          personal tour guide, incorporating local facts, cultural stories, transportation tips, and practical recommendations.
        </p>
        <h3 className="text-xl font-semibold mt-6 mb-2">Technology</h3>
        <p className="mb-4">
          The Appi environment runs on modern web technologies, including React-based interfaces and API-driven services that power itinerary generation,
          hotel workflows, and future cross-product integrations.
        </p>
      </>
    ),
  },
};

// --- Policy Page Component ---
const PolicyPage = ({ policyName, onBackToPlanner }) => {
  const policy = policyData[policyName];
  const title = policy?.title || "Page Not Found";
  const content = policy?.content || <p className="text-red-600">The requested policy page could not be loaded.</p>;

  return (
    <div className="bg-white shadow-xl rounded-2xl p-6 sm:p-8 mb-8 border-l-4 border-indigo-500">
      <div className="flex justify-between items-center mb-6 border-b pb-4">
        <h1 className="text-3xl font-extrabold text-gray-900">{title}</h1>
        <button
          onClick={onBackToPlanner}
          className="flex items-center gap-1 px-4 py-2 rounded-xl bg-gray-100 text-gray-700 hover:bg-gray-200 transition duration-150 text-sm font-semibold"
        >
          <ArrowLeft className="w-4 h-4" /> Back to Planner
        </button>
      </div>
      <div className="text-gray-700 leading-relaxed text-sm sm:text-base">{content}</div>
    </div>
  );
};

export default function TravelPlanner({ language = "en" }) {
  const [currentPage, setCurrentPage] = useState("planner");
  const [analyticsSnapshot, setAnalyticsSnapshot] = useState(() => readAnalyticsSnapshot());
  const [installInsights, setInstallInsights] = useState({
    installPromptShownTotal: 0,
    installClickedTotal: 0,
    installCompletedTotal: 0,
    recentInstallEvents: [],
    topInstallCities: [],
    topInstallCitiesToday: [],
  });
  const [visitorInsights, setVisitorInsights] = useState({
    total: 0,
    mobile: 0,
    desktop: 0,
    unknown: 0,
    topLocations: [],
    topLocationsToday: [],
  });
  const [bookingInsights, setBookingInsights] = useState({
    confirmedTotal: 0,
    revenueTotal: 0,
    revenueCurrency: "EUR",
    recentBookingEvents: [],
  });
  const [adminAuthorized, setAdminAuthorized] = useState(false);
  const [adminAuthLoading, setAdminAuthLoading] = useState(false);
  const [adminAccessError, setAdminAccessError] = useState("");
  const [deferredInstallPrompt, setDeferredInstallPrompt] = useState(null);
  const [isAppInstalled, setIsAppInstalled] = useState(() => {
    if (typeof window === "undefined") return false;
    const standaloneFromMedia = typeof window.matchMedia === "function" && window.matchMedia("(display-mode: standalone)").matches;
    const standaloneFromNavigator = Boolean(window.navigator?.standalone);
    return standaloneFromMedia || standaloneFromNavigator;
  });
  const [isInstallPromptDismissed, setIsInstallPromptDismissed] = useState(() => {
    if (typeof window === "undefined") return false;
    return window.localStorage.getItem(INSTALL_PROMPT_DISMISSED_KEY) === "1";
  });

  const [input, setInput] = useState("");
  const [destinationData, setDestinationData] = useState({
    country: "",
    region: "",
    city: "",
    duration: "",
    startDate: "",
    continent: "",
  });

  const [pace, setPace] = useState(1);
  const [selectedInterests, setSelectedInterests] = useState([]);
  const selectedInterestsRef = useRef([]);
  const [selectedBudget, setSelectedBudget] = useState("");
  const [loading, setLoading] = useState(false);
  const [showCtaPopup, setShowCtaPopup] = useState(false);
  const [singleCityHotelSeed, setSingleCityHotelSeed] = useState(0);

  const [itineraryJson, setItineraryJson] = useState(null);
  const [itineraryText, setItineraryText] = useState("");

  // Multi-City mode
  const [tripMode, setTripMode] = useState("single"); // "single" | "multi" | "road" | "interrail"
  const [modeButtonActiveClasses, setModeButtonActiveClasses] = useState({
    single: "bg-blue-500 text-white shadow-sm shadow-blue-200",
    multi: "bg-red-500 text-white shadow-sm shadow-red-200",
    road: "bg-yellow-400 text-gray-900 shadow-sm shadow-yellow-200",
    interrail: "bg-emerald-500 text-white shadow-sm shadow-emerald-200",
  });
  const [multiStops, setMultiStops] = useState([
    { id: 1, city: "", country: "", nights: 2 },
    { id: 2, city: "", country: "", nights: 2 },
  ]);
  const [multiStartDate, setMultiStartDate] = useState("");
  const multiStopDateRanges = useMemo(() => buildMultiCityDateRanges(multiStops, multiStartDate), [multiStops, multiStartDate]);
  const [multiResult, setMultiResult] = useState(null);
  const [multiLoading, setMultiLoading] = useState(false);

  const roadFeatureActiveClassOptions = [
    "bg-blue-500 text-white border-blue-500 shadow-sm shadow-blue-200",
    "bg-red-500 text-white border-red-500 shadow-sm shadow-red-200",
    "bg-yellow-400 text-gray-900 border-yellow-400 shadow-sm shadow-yellow-200",
    "bg-green-500 text-white border-green-500 shadow-sm shadow-green-200",
  ];
  const getRandomRoadFeatureClass = () => roadFeatureActiveClassOptions[Math.floor(Math.random() * roadFeatureActiveClassOptions.length)];

  // Road Trip mode (isolated from single/multi state)
  const [roadTripForm, setRoadTripForm] = useState({
    startPoint: "",
    endPoint: "",
    tripDays: 5,
    startDate: "",
    countryContext: "",
    routeStyle: "Balanced",
    vehicleType: "Car",
  });
  const [roadTripFeatures, setRoadTripFeatures] = useState([
    "Camping sites",
    "EV charging stations",
    "Places of interest",
  ]);
  const [roadFeatureColorClasses, setRoadFeatureColorClasses] = useState(() => ({
    "Camping sites": getRandomRoadFeatureClass(),
    "EV charging stations": getRandomRoadFeatureClass(),
    "Places of interest": getRandomRoadFeatureClass(),
  }));
  const [roadTripLoading, setRoadTripLoading] = useState(false);
  const [roadTripResult, setRoadTripResult] = useState(null);
  const plannerText = useMemo(() => ({ ...PLANNER_TEXT.en, ...getPlannerText(language) }), [language]);
  const displayBudgetLabel = (value) => {
    if (value === "Budget") return plannerText.budgetOptionBudget;
    if (value === "Mid-range") return plannerText.budgetOptionMidRange;
    if (value === "Luxury") return plannerText.budgetOptionLuxury;
    return value;
  };
  const interestDisplayMap = {
    Culture: plannerText.interestCulture,
    Nature: plannerText.interestNature,
    Food: plannerText.interestFood,
    Nightlife: plannerText.interestNightlife,
    Shopping: plannerText.interestShopping,
    Beaches: plannerText.interestBeaches,
    Museums: plannerText.interestMuseums,
    History: plannerText.interestHistory,
    Hiking: plannerText.interestHiking,
    Adventure: plannerText.interestAdventure,
    Family: plannerText.interestFamily,
    Romance: plannerText.interestRomance,
    Photography: plannerText.interestPhotography,
    Wildlife: plannerText.interestWildlife,
    Wellness: plannerText.interestWellness,
    Festivals: plannerText.interestFestivals,
    Sports: plannerText.interestSports,
    Wine: plannerText.interestWine,
    "Road Trips": plannerText.interestRoadTrips,
    "Winter Sports": plannerText.interestWinterSports,
  };
  const displayInterestLabel = (value) => interestDisplayMap[value] || value;

  const visibleRoadFeatures = useMemo(() => {
    const base = [...ROAD_TRIP_FEATURES, "Places of interest"];
    if (tripMode !== "interrail") return base;
    return base.filter((feature) => !INTERRAIL_EXCLUDED_FEATURES.has(feature));
  }, [tripMode]);

  const modeButtonStripeClasses = {
    single: "before:bg-blue-500",
    multi: "before:bg-green-500",
    road: "before:bg-red-500",
    interrail: "before:bg-emerald-500",
  };

  const modeButtonActiveClassOptions = {
    single: [
      "bg-blue-500 text-white shadow-sm shadow-blue-200",
      "bg-green-500 text-white shadow-sm shadow-green-200",
      "bg-red-500 text-white shadow-sm shadow-red-200",
      "bg-yellow-400 text-gray-900 shadow-sm shadow-yellow-200",
    ],
    multi: [
      "bg-red-500 text-white shadow-sm shadow-red-200",
      "bg-blue-500 text-white shadow-sm shadow-blue-200",
      "bg-green-500 text-white shadow-sm shadow-green-200",
      "bg-yellow-400 text-gray-900 shadow-sm shadow-yellow-200",
    ],
    road: [
      "bg-yellow-400 text-gray-900 shadow-sm shadow-yellow-200",
      "bg-blue-500 text-white shadow-sm shadow-blue-200",
      "bg-green-500 text-white shadow-sm shadow-green-200",
      "bg-red-500 text-white shadow-sm shadow-red-200",
    ],
    interrail: [
      "bg-emerald-500 text-white shadow-sm shadow-emerald-200",
      "bg-blue-500 text-white shadow-sm shadow-blue-200",
      "bg-red-500 text-white shadow-sm shadow-red-200",
      "bg-yellow-400 text-gray-900 shadow-sm shadow-yellow-200",
    ],
  };

  const handleTripModeSelect = (mode) => {
    const options = modeButtonActiveClassOptions[mode] || modeButtonActiveClassOptions.single;
    const randomClass = options[Math.floor(Math.random() * options.length)];
    setModeButtonActiveClasses((prev) => ({ ...prev, [mode]: randomClass }));
    if (mode === "interrail") {
      setRoadTripForm((prev) => ({ ...prev, vehicleType: "Interrail (Train)" }));
      setRoadTripFeatures((prev) => {
        const filtered = prev.filter((feature) => !INTERRAIL_EXCLUDED_FEATURES.has(feature));
        return filtered.length > 0 ? filtered : [...INTERRAIL_DEFAULT_FEATURES];
      });
    }
    setTripMode(mode);
  };

  const itineraryRef = useRef(null);

  const getSharePayload = (mode = tripMode) => {
    const meta = itineraryJson?.meta || {};
    let destination = meta.destination || destinationData.city || destinationData.country || "my trip";
    let modeLabel = "trip itinerary";
    let budgetLabel = meta.budget || selectedBudget || "your budget";
    let days = meta.durationDays || itineraryJson?.days?.length || destinationData.duration || "";

    if (mode === "multi") {
      modeLabel = "multi-city trip";
      destination = multiResult?.tripTitle || multiResult?.cities?.map((c) => c?.cityName).filter(Boolean).join(" to ") || destination;
      budgetLabel = multiResult?.budget || budgetLabel;
      days = (multiResult?.totalNights ? Number(multiResult.totalNights) + 1 : "") || days;
    } else if (mode === "road" || mode === "interrail") {
      modeLabel = mode === "interrail" ? "interrail trip" : "road trip";
      destination =
        roadTripResult?.title ||
        [roadTripForm.startPoint, roadTripForm.endPoint].filter(Boolean).join(" to ") ||
        destination;
      budgetLabel = selectedBudget || budgetLabel;
      days = roadTripResult?.totalDays || roadTripForm.tripDays || days;
    }

    const dayText = days ? `for ${days} day${Number(days) === 1 ? "" : "s"}` : "";
    const shareText = `I planned a ${modeLabel} to ${destination} ${dayText} on ${budgetLabel} with AppiHolidays.`.replace(/\s+/g, " ").trim();
    const shareUrl =
      typeof window !== "undefined"
        ? `${window.location.origin}${window.location.pathname}${window.location.search}`
        : "https://www.appiholidays.com";
    return {
      shareText,
      shareUrl,
      fullText: `${shareText} ${shareUrl}`.trim(),
    };
  };

  const openShareWindow = (url) => {
    if (typeof window === "undefined" || !url) return;
    window.open(url, "_blank", "noopener,noreferrer");
  };

  const copyToClipboard = async (value) => {
    if (!value) return false;
    if (typeof navigator !== "undefined" && navigator.clipboard?.writeText) {
      try {
        await navigator.clipboard.writeText(value);
        return true;
      } catch {}
    }

    if (typeof document === "undefined") return false;
    try {
      const helper = document.createElement("textarea");
      helper.value = value;
      helper.setAttribute("readonly", "");
      helper.style.position = "fixed";
      helper.style.opacity = "0";
      document.body.appendChild(helper);
      helper.focus();
      helper.select();
      const copied = document.execCommand("copy");
      document.body.removeChild(helper);
      return copied;
    } catch {
      return false;
    }
  };

  const handleShareClick = async (platform, mode = tripMode) => {
    const { shareText, shareUrl, fullText } = getSharePayload(mode);

    if (platform === "whatsapp") {
      const url = `https://wa.me/?text=${encodeURIComponent(fullText)}`;
      openShareWindow(url);
      return;
    }

    if (platform === "facebook") {
      const url = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}&quote=${encodeURIComponent(shareText)}`;
      openShareWindow(url);
      return;
    }

    if (platform === "linkedin") {
      const url = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`;
      openShareWindow(url);
      return;
    }

    if (platform === "instagram") {
      const copied = await copyToClipboard(fullText);
      if (copied) {
        showToast("Instagram does not support direct web shares. Link copied so you can paste in Instagram.", "success", 3500);
      } else {
        showToast("Instagram share link could not be copied. Please copy and paste the page URL manually.", "error", 3500);
      }
      openShareWindow("https://www.instagram.com/");
    }
  };

  const [toast, setToast] = useState({ show: false, type: "error", message: "" });
  const [showMobileMoreSheet, setShowMobileMoreSheet] = useState(false);
  const showToast = (message, type = "error", timeoutMs = 4000) => {
    setToast({ show: true, type, message });
    if (timeoutMs > 0) setTimeout(() => setToast((t) => ({ ...t, show: false })), timeoutMs);
  };

  const trackAnalytics = (type, payload = {}) => {
    const nextSnapshot = recordAnalyticsEvent(type, payload);
    setAnalyticsSnapshot(nextSnapshot);

    if (["app_install_prompt_shown", "app_install_clicked", "app_install_completed"].includes(type)) {
      void postInstallAnalyticsEvent(type, payload);
    }

    return nextSnapshot;
  };

  const refreshInstallInsights = async () => {
    try {
      const { response: insightsResponse, data: insightsData } = await fetchAdminInsightsJson();
      if (insightsResponse.ok) {
        if (insightsData?.installs) {
          setInstallInsights({
            installPromptShownTotal: Number(insightsData.installs.installPromptShownTotal || 0),
            installClickedTotal: Number(insightsData.installs.installClickedTotal || 0),
            installCompletedTotal: Number(insightsData.installs.installCompletedTotal || 0),
            recentInstallEvents: Array.isArray(insightsData.installs.recentInstallEvents) ? insightsData.installs.recentInstallEvents : [],
            topInstallCities: Array.isArray(insightsData.installs.topInstallCities) ? insightsData.installs.topInstallCities : [],
            topInstallCitiesToday: Array.isArray(insightsData.installs.topInstallCitiesToday) ? insightsData.installs.topInstallCitiesToday : [],
          });
        }

        if (insightsData?.visitors) {
          setVisitorInsights({
            total: Number(insightsData.visitors.total || 0),
            mobile: Number(insightsData.visitors.mobile || 0),
            desktop: Number(insightsData.visitors.desktop || 0),
            unknown: Number(insightsData.visitors.unknown || 0),
            topLocations: Array.isArray(insightsData.visitors.topLocations) ? insightsData.visitors.topLocations : [],
            topLocationsToday: Array.isArray(insightsData.visitors.topLocationsToday) ? insightsData.visitors.topLocationsToday : [],
          });
        }

        if (insightsData?.bookings) {
          setBookingInsights({
            confirmedTotal: Number(insightsData.bookings.confirmedTotal || 0),
            revenueTotal: Number(insightsData.bookings.revenueTotal || 0),
            revenueCurrency: String(insightsData.bookings.revenueCurrency || "EUR"),
            recentBookingEvents: Array.isArray(insightsData.bookings.recentBookingEvents)
              ? insightsData.bookings.recentBookingEvents
              : [],
          });
        }
      }
    } catch {
      // Keep current insights snapshot if refresh fails.
    }
  };

  const refreshAdminAuth = async () => {
    setAdminAuthLoading(true);
    try {
      const { response, data } = await fetchAdminAuthJson("GET");
      const authenticated = response.ok && !!data?.authenticated;
      setAdminAuthorized(authenticated);
      if (authenticated) {
        setAdminAccessError("");
        setAnalyticsSnapshot(readAnalyticsSnapshot());
        await refreshInstallInsights();
      }
      return authenticated;
    } catch {
      setAdminAuthorized(false);
      return false;
    } finally {
      setAdminAuthLoading(false);
    }
  };

  const unlockAdminDashboard = async ({ username, password, accessCode }) => {
    setAdminAuthLoading(true);
    try {
      const { response, data } = await fetchAdminAuthJson("POST", { username, password, accessCode });
      if (!response.ok) {
        setAdminAuthorized(false);
        setAdminAccessError(data?.error || "Invalid admin login details.");
        return;
      }

      setAdminAuthorized(true);
      setAdminAccessError("");
      setAnalyticsSnapshot(readAnalyticsSnapshot());
      await refreshInstallInsights();
    } catch {
      setAdminAuthorized(false);
      setAdminAccessError("Admin login failed. Please try again.");
    } finally {
      setAdminAuthLoading(false);
    }
  };

  const lockAdminDashboard = async () => {
    setAdminAuthLoading(true);
    try {
      await fetchAdminAuthJson("DELETE");
    } catch {}
    setAdminAccessError("");
    setAdminAuthorized(false);
    window.location.hash = "";
    setAdminAuthLoading(false);
  };

  // Hash routing
  useEffect(() => {
    const updateCurrentPageFromHash = () => {
      const hash = window.location.hash.slice(1);
      const page = hash && (policyData[hash] || hash === "admin") ? hash : "planner";
      setCurrentPage(page);
    };
    updateCurrentPageFromHash();
    window.addEventListener("hashchange", updateCurrentPageFromHash);
    return () => window.removeEventListener("hashchange", updateCurrentPageFromHash);
  }, []);

  useEffect(() => {
    if (currentPage !== "admin") {
      trackAnalytics("page_view", { page: currentPage });
      void postVisitorSessionEvent({ source: "public_page_load" });
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage]);

  useEffect(() => {
    if (currentPage === "admin") {
      setAnalyticsSnapshot(readAnalyticsSnapshot());
      refreshAdminAuth();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage]);

  useEffect(() => {
    if (currentPage !== "admin" || !adminAuthorized) return undefined;

    const intervalId = setInterval(() => {
      void refreshInstallInsights();
    }, ADMIN_INSIGHTS_POLL_MS);

    return () => clearInterval(intervalId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, adminAuthorized]);

  useEffect(() => {
    if (typeof window === "undefined") return undefined;

    const mediaQuery = typeof window.matchMedia === "function" ? window.matchMedia("(display-mode: standalone)") : null;
    const syncInstallState = () => {
      const standaloneFromMedia = mediaQuery ? mediaQuery.matches : false;
      const standaloneFromNavigator = Boolean(window.navigator?.standalone);
      setIsAppInstalled(standaloneFromMedia || standaloneFromNavigator);
    };

    syncInstallState();

    const handleBeforeInstallPrompt = (event) => {
      event.preventDefault();
      setDeferredInstallPrompt(event);
    };

    const handleAppInstalled = () => {
      syncInstallState();
      setDeferredInstallPrompt(null);

      try {
        const alreadyTracked = window.localStorage.getItem(INSTALL_COMPLETED_TRACK_KEY) === "1";
        if (!alreadyTracked) {
          window.localStorage.setItem(INSTALL_COMPLETED_TRACK_KEY, "1");
          trackAnalytics("app_install_completed", { source: "install_shell", outcome: "accepted" });
        }
      } catch {
        trackAnalytics("app_install_completed", { source: "install_shell", outcome: "accepted" });
      }
    };

    window.addEventListener("beforeinstallprompt", handleBeforeInstallPrompt);
    window.addEventListener("appinstalled", handleAppInstalled);

    if (mediaQuery?.addEventListener) mediaQuery.addEventListener("change", syncInstallState);
    else if (mediaQuery?.addListener) mediaQuery.addListener(syncInstallState);

    return () => {
      window.removeEventListener("beforeinstallprompt", handleBeforeInstallPrompt);
      window.removeEventListener("appinstalled", handleAppInstalled);
      if (mediaQuery?.removeEventListener) mediaQuery.removeEventListener("change", syncInstallState);
      else if (mediaQuery?.removeListener) mediaQuery.removeListener(syncInstallState);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (typeof window === "undefined") return;
    if (currentPage !== "planner" || isAppInstalled || isInstallPromptDismissed || !deferredInstallPrompt) return;

    const alreadyTracked = window.sessionStorage.getItem(INSTALL_PROMPT_TRACK_SESSION_KEY) === "1";
    if (alreadyTracked) return;

    window.sessionStorage.setItem(INSTALL_PROMPT_TRACK_SESSION_KEY, "1");
    trackAnalytics("app_install_prompt_shown", { source: "install_shell" });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, isAppInstalled, isInstallPromptDismissed, deferredInstallPrompt]);

  useEffect(() => {
    if (typeof window === "undefined" || !isAppInstalled) return;

    const alreadyTracked = window.localStorage.getItem(INSTALL_COMPLETED_TRACK_KEY) === "1";
    if (alreadyTracked) return;

    window.localStorage.setItem(INSTALL_COMPLETED_TRACK_KEY, "1");
    trackAnalytics("app_install_completed", { source: "standalone_detected", outcome: "accepted" });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAppInstalled]);

  // Scroll to itinerary
  useEffect(() => {
    if (itineraryJson && itineraryRef.current) {
      itineraryRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, [itineraryJson]);

  const toggleInterest = (interest) => {
    setSelectedInterests((prev) => {
      let next;
      if (prev.includes(interest)) {
        next = prev.filter((item) => item !== interest);
      } else if (prev.length >= 4) {
        // Keep the most recent 4 interests to avoid over-selection.
        next = [...prev.slice(1), interest];
      } else {
        next = [...prev, interest];
      }
      selectedInterestsRef.current = next;
      return next;
    });
  };

  const validateItinerary = (obj) => {
    const errors = [];
    if (!obj || typeof obj !== "object") {
      errors.push("Root is not an object.");
      return { ok: false, errors };
    }
    if (!Array.isArray(obj.days) || obj.days.length === 0) {
      errors.push('Missing or empty "days" array.');
    }
    if (obj.meta) {
      const { pace: p, budget: b } = obj.meta;
      if (p && !["Relaxed", "Balanced", "Action-packed"].includes(p)) errors.push('"meta.pace" must be Relaxed | Balanced | Action-packed.');
      if (b && !["Budget", "Mid-range", "Luxury"].includes(b)) errors.push('"meta.budget" must be Budget | Mid-range | Luxury.');
    } else {
      errors.push('Missing "meta" object.');
    }
    return { ok: errors.length === 0, errors };
  };

  const hasSpecifics = (s = "") =>
    /\b(1[5-9]\d{2}|20\d{2}|[A-Z][a-z]{2,}|legend|ritual|festival|chef|architect|invented|origin)\b/.test(s);

  const extraStoryChecks = (obj) => {
    const errs = [];
    for (const d of obj?.days || []) {
      for (const a of d?.activities || []) {
        if (!a.culturalStory || a.culturalStory.trim().length < 12) {
          errs.push(`Activity "${a.title || a.time}" missing culturalStory.`);
        } else if (!hasSpecifics(a.culturalStory)) {
          errs.push(`Activity "${a.title}" culturalStory too generic — add a name/year/legend/ritual/dish origin.`);
        }
      }
    }
    return errs;
  };

  const openingStyleChecks = (obj) => {
    const errs = [];
    const bannedStarts = [
      /^you\s+awaken/i,
      /^you\s+awake/i,
      /^as\s+dawn/i,
      /^dawn\s+breaks/i,
      /^the\s+air\s+is\s+filled/i,
      /^the\s+gentle\s+lapping/i,
      /^you\s+are\s+greeted/i,
      /^bathed\s+in\s+soft/i,
      /^as\s+the\s+sun/i,
    ];

    for (const d of obj?.days || []) {
      const first = (d.activities || [])[0];
      if (!first || !first.description) continue;
      const desc = String(first.description || "").trim();

      if (!/^good\s+morning\b/i.test(desc)) errs.push(`Day ${d.day}: consider starting the first activity with "Good morning" for tone consistency.`);

      const sentences = desc.split(/(?<=[.!?])\s+/).slice(0, 2).join(" ");
      if (!/after\s+breakfast/i.test(sentences)) errs.push(`Day ${d.day}: consider adding "After breakfast..." in the opening for a consistent daily flow.`);

      if (bannedStarts.some((rx) => rx.test(desc))) errs.push(`Day ${d.day}: opening style is more flowery than preferred; use a clearer practical opener.`);
    }
    return errs;
  };

  const ACCENTS = [
    { stripe: "border-emerald-500", line: "border-emerald-200", dot: "border-emerald-400", link: "text-emerald-700 hover:text-emerald-800" },
    { stripe: "border-blue-500", line: "border-blue-200", dot: "border-blue-400", link: "text-blue-700 hover:text-blue-800" },
    { stripe: "border-amber-500", line: "border-amber-200", dot: "border-amber-400", link: "text-amber-700 hover:text-amber-800" },
    { stripe: "border-pink-500", line: "border-pink-200", dot: "border-pink-400", link: "text-pink-700 hover:text-pink-800" },
  ];

  const categoryBadge = (cat = "") => {
    const c = cat.toLowerCase();
    if (c.includes("dining") || c.includes("food")) return { icon: "🍽️", label: "dining" };
    if (c.includes("entertain")) return { icon: "🎭", label: "entertainment" };
    if (c.includes("transport")) return { icon: "🚌", label: "transport" };
    if (c.includes("shopping")) return { icon: "🛍️", label: "shopping" };
    if (c.includes("night")) return { icon: "🌃", label: "nightlife" };
    if (c.includes("museum")) return { icon: "🖼️", label: "museum" };
    if (c.includes("hike") || c.includes("nature")) return { icon: "🌿", label: "activity" };
    return { icon: "📍", label: c || "activity" };
  };

  const switchCityAndGenerate = (city) => {
    const override = {
      city: city?.cityName || "",
      country: city?.country || "",
      duration: String(city?.nights || "2"),
      budget: multiResult?.selectedBudget || selectedBudget || "Mid-range",
    };

    setDestinationData((prev) => ({
      ...prev,
      country: override.country,
      city: override.city,
      duration: override.duration,
      continent: "",
      region: "",
    }));
    setSelectedBudget(override.budget);
    setSelectedInterests([]);
    selectedInterestsRef.current = [];
    setTripMode("single");

    // Use explicit payload to avoid stale state when auto-generating from Multi-City.
    setTimeout(() => handleGenerate(override), 0);
  };

  const handleGenerate = async (overrideCityData = null) => {
    setLoading(true);
    setItineraryJson(null);
    setItineraryText("");
    setShowCtaPopup(true);

    try {
      const hasExplicitOverride =
        !!overrideCityData &&
        typeof overrideCityData === "object" &&
        !overrideCityData.nativeEvent &&
        (typeof overrideCityData.city === "string" ||
          typeof overrideCityData.country === "string" ||
          typeof overrideCityData.duration === "string" ||
          typeof overrideCityData.budget === "string");

      const cityData = hasExplicitOverride
        ? {
            city: overrideCityData.city || "Unknown",
            country: overrideCityData.country || "Unknown",
            duration: String(clampSingleTripDays(overrideCityData.duration || "2")),
            budget: overrideCityData.budget || "Mid-range",
          }
        : {
            city: destinationData.city || "Unknown",
            country: destinationData.country || "Unknown",
            duration: String(clampSingleTripDays(destinationData.duration || "2")),
            budget: selectedBudget || "Mid-range",
          };

      const enforcedInterests = (selectedInterestsRef.current || selectedInterests).slice(0, 4);
      const plannerUserId = getOrCreatePlannerUserId();
      const historySnapshot = await fetchItineraryHistory({
        userId: plannerUserId,
        city: cityData.city,
        country: cityData.country,
      });
      const blockedActivityKeys = new Set(
        (historySnapshot.excludeActivityKeys || [])
          .map((item) => String(item || "").trim().toLowerCase())
          .filter(Boolean)
      );
      const blockedActivityLabels = (historySnapshot.excludeActivityLabels || []).slice(0, 40);
      const outputLanguage = resolveOutputLanguage(language);
      const outputStyleInstruction = resolveOutputStyleInstruction(language);

      const definitiveInstruction = buildItineraryPrompt({
        city: cityData.city,
        country: cityData.country,
        days: cityData.duration,
        people: 2,
        theme: input || enforcedInterests[0] || "balanced sightseeing",
        budget: cityData.budget,
        pace: pace === 0 ? "Relaxed" : pace === 1 ? "Balanced" : "Action-packed",
        interests: enforcedInterests,
        excludedActivities: blockedActivityLabels,
        outputLanguage,
        outputStyleInstruction,
      });

      const prompt = `
${definitiveInstruction}

Return ONLY valid JSON (no commentary) that matches this schema:

{
  "days":[
    {
      "day": 1,
      "title": "string",
      "weather": {"summary":"string","condition":"string","high":"string","low":"string","humidity":"string","wind":"string"},
      "activities":[
        {
          "time":"string",
          "title":"string",
          "category":"string",
          "description":"string",
          "culturalStory":"string",
          "location":"string",
          "duration":"string",
          "cost":"string",
          "tip":"string",
          "specialties":"string",
          "theme":"string",
          "mapLink":"string"
        }
      ],
      "hotelSuggestions":["string"],
      "transportTips":"string"
    }
  ],
  "meta":{
    "destination":"string",
    "country":"string",
    "durationDays":0,
    "pace":"Relaxed | Balanced | Action-packed",
    "budget":"Budget | Mid-range | Luxury",
    "interests":["string"],
    "arrivalTransport":{
      "title":"Airport → City Center",
      "arrivalLabel":"Upon Arrival",
      "airports":["string"],
      "trains":[{"name":"string","note":"string with typical cost and travel time"}],
      "metro":[{"name":"string","note":"string with typical cost and travel time"}],
      "buses":{
        "express":[{"name":"string","note":"string with typical cost and travel time"}],
        "routes":[{"name":"string","note":"string with typical cost and travel time"}]
      },
      "taxis":{"ranks":"string","cost":"string with fare range and journey time","prebook":["string"]},
      "shuttles":[{"name":"string","note":"string"}],
      "carRental":{"location":"string","companies":["string"]},
      "note":"string"
    }
  }
}

STRICT:
- Output MUST be valid JSON only.
- Write all user-facing values and prose in ${outputLanguage}.
- ${outputStyleInstruction || "Use a clear and practical tone suitable for travel planning."}
- Include culturalStory for EVERY activity.
- Include weather for EVERY day (unknown fields: "N/A").
- Include long-form mapLink URLs (no short redirect links).
- The only allowed values for meta.interests are from this selected list: ${JSON.stringify(enforcedInterests)}.
- Do not add interest tags outside that selected list.
- Include meta.arrivalTransport with practical airport-to-city options.
- Include at least one bus option and taxi fare+time details in meta.arrivalTransport.
- Include train/metro details where available; if unavailable, explain this in meta.arrivalTransport.note.
- Include one dedicated 8:00 PM dinner activity for each day, tailored to the selected budget level.
- Provide exactly 5 hotelSuggestions for Day 1.
- Hotel suggestions must be budget-aware and realistic for ${cityData.budget}:
  Budget: low-cost hotels, hostels, guesthouses, and B&Bs only.
  Mid-range: comfortable 3-4 star stays.
  Luxury: premium 5-star and high-end boutique properties.
- Keep hotelSuggestions unique within the response (no duplicates).
- Rotate hotel names across runs; avoid repeating the same 1-2 properties in every response.
- Hotel suggestions should be dynamic strings: "Hotel — reason tied to selected budget — https://maps.google.com/?q=..."
Limit to 4 activities per day.
- Prefer starting Day 1, Activity 1 "description" with "Good morning".
- Prefer mentioning "After breakfast..." in the first or second sentence of Day 1, Activity 1.
`;

      const requestBody = JSON.stringify({
        prompt,
        generationConfig: { temperature: 0.7, maxOutputTokens: 8192 },
      });

      const fetchJson = async (url) => {
        const response = await fetch(url, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: requestBody,
        });

        let data;
        try {
          data = await response.json();
        } catch {
          data = { error: "Non-JSON response from server" };
        }

        return { response, data };
      };

      const primaryUrl = `${API_BASE}/api/gemini`;
      let { response, data } = await fetchJson(primaryUrl);

      const onCustomDomain =
        typeof window !== "undefined" &&
        /appiholidays\.com$/i.test(window.location.hostname) &&
        !/vercel\.app$/i.test(window.location.hostname);

      const shouldRetryWithFallback =
        process.env.NODE_ENV === "production" &&
        onCustomDomain &&
        (!response.ok || data?.error === "Non-JSON response from server");

      if (shouldRetryWithFallback) {
        ({ response, data } = await fetchJson(`${PROD_API_FALLBACK}/api/gemini`));
      }

      if (!response.ok) {
        const msg = data?.error || data?.details || data?.message || "Gemini request failed";
        showToast(`Gemini HTTP ${response.status}: ${String(msg).slice(0, 200)}`, "error", 6000);
        setItineraryJson(null);
        setItineraryText(`Gemini request failed:\n${JSON.stringify(data).slice(0, 4000)}`);
        return;
      }

      let parsed = null;

      if (data?.itinerary && typeof data.itinerary === "object") {
        parsed = data.itinerary;
      } else {
        const modelText =
          data?.candidates?.[0]?.content?.parts?.map((p) => (typeof p?.text === "string" ? p.text : "")).join("\n").trim() || "";

        if (!modelText) {
          showToast("Gemini returned no text.", "error", 6000);
          setItineraryJson(null);
          setItineraryText(`Gemini returned no text.\n\n${JSON.stringify(data).slice(0, 2000)}`);
          return;
        }

        const cleanedModelText = extractJson(modelText);

        try {
          parsed = JSON.parse(cleanedModelText);
        } catch {
          showToast("Model did not return valid JSON.", "error", 6000);
          setItineraryJson(null);
          setItineraryText(
            `Model did not return valid JSON.\n\nRaw model output:\n${modelText.slice(0, 4000)}\n\n---\n\nExtracted JSON:\n${cleanedModelText.slice(0, 4000)}`
          );
          return;
        }
      }

      const { errors } = validateItinerary(parsed);
      const storyErrs = extraStoryChecks(parsed);
      const openingErrs = openingStyleChecks(parsed);

      if (parsed && typeof parsed === "object") {
        const destination = parsed?.meta?.destination || destinationData.city || destinationData.country || "your destination";
        parsed.meta = parsed.meta && typeof parsed.meta === "object" ? parsed.meta : {};
        parsed.meta.interests =
          enforcedInterests.length > 0
            ? enforcedInterests
            : Array.isArray(parsed.meta.interests)
              ? parsed.meta.interests.slice(0, 4)
              : [];
        parsed.meta.arrivalTransport = normalizeArrivalTransport(parsed.meta.arrivalTransport, destination);

        if (Array.isArray(parsed.days)) {
          parsed.days = parsed.days.slice(0, ITINERARY_DAY_LIMIT).map((day, idx) => {
            if (!day || typeof day !== "object") return day;
            const destinationLabel = parsed?.meta?.destination || destinationData.city || destinationData.country || "city center";
            const budgetLabel = parsed?.meta?.budget || selectedBudget || "Mid-range";
            const activities = Array.isArray(day.activities)
              ? day.activities.map((activity) => {
                  if (!activity || typeof activity !== "object") return activity;
                  return {
                    ...activity,
                    nearbyEats: { breakfast: [], lunch: [], dinner: [] },
                  };
                })
              : [];
            const shouldAddDinner = !hasEightPmDinner(activities);
            const withDinner = shouldAddDinner
              ? [...activities, buildBudgetDinnerActivity({ destination: destinationLabel, budget: budgetLabel, dayNumber: day.day || 1 })]
              : activities;
            return { ...day, day: idx + 1, activities: sortActivitiesByTime(withDinner) };
          });
          parsed.meta.durationDays = parsed.days.length;

          const dedupeResult = dedupeItineraryActivities(parsed.days, blockedActivityKeys);
          parsed.days = dedupeResult.days;
          if (dedupeResult.removedCount > 0) {
            showToast(
              `Adjusted ${dedupeResult.removedCount} repeated activit${dedupeResult.removedCount === 1 ? "y" : "ies"} from your recent history.`,
              "info",
              2600
            );
          }
        }

        try {
          const hydrateActivities = [];
          (parsed.days || []).forEach((day, dayIndex) => {
            (day?.activities || []).forEach((activity, activityIndex) => {
              hydrateActivities.push({
                dayIndex,
                activityIndex,
                title: String(activity?.title || "").trim(),
                location: String(activity?.location || parsed?.meta?.destination || destinationData.city || destinationData.country || "").trim(),
              });
            });
          });

          if (hydrateActivities.length > 0) {
            const nearbyRes = await fetch(`${API_BASE}/api/nearby-eats`, {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({
                destination: parsed?.meta?.destination || destinationData.city || "",
                country: parsed?.meta?.country || destinationData.country || "",
                budget: parsed?.meta?.budget || selectedBudget || "Mid-range",
                activities: hydrateActivities,
              }),
            });

            if (nearbyRes.ok) {
              const nearbyData = await nearbyRes.json();
              const hydratedMap = new Map((nearbyData?.hydrated || []).map((entry) => [Number(entry?.index), entry?.nearbyEats || null]));

              let flatIndex = 0;
              parsed.days = (parsed.days || []).map((day) => {
                const nextActivities = (day?.activities || []).map((activity) => {
                  const hydrated = hydratedMap.get(flatIndex);
                  flatIndex += 1;
                  return hydrated
                    ? { ...activity, nearbyEats: normalizeNearbyEats({ nearbyEats: hydrated }, { destination: parsed?.meta?.destination || "", budget: parsed?.meta?.budget || selectedBudget || "Mid-range" }) }
                    : activity;
                });
                return { ...day, activities: nextActivities };
              });
            }
          }
        } catch (nearbyError) {
          console.warn("Nearby eats cache hydration failed:", nearbyError);
        }

        try {
          const weatherDays = await fetchLiveDailyWeather({
            city: destinationData.city || parsed?.meta?.destination || "",
            country: destinationData.country || parsed?.meta?.country || "",
            days: Array.isArray(parsed.days) ? parsed.days.length : destinationData.duration || 1,
          });

          if (Array.isArray(weatherDays) && Array.isArray(parsed.days)) {
            parsed.days = parsed.days.map((day, idx) => ({
              ...day,
              weather: weatherDays[idx] || day?.weather || {
                summary: "N/A",
                condition: "N/A",
                high: "N/A",
                low: "N/A",
                humidity: "N/A",
                wind: "N/A",
              },
            }));
          }
        } catch (weatherError) {
          console.warn("Live weather enrichment failed:", weatherError);
        }
      }

      if (errors.length === 0 && storyErrs.length === 0) {
        parsed = rewriteBookingCopy(parsed);
        setItineraryJson(parsed);
        setSingleCityHotelSeed(Date.now());
        trackAnalytics("generate_single_success", { city: cityData.city, country: cityData.country });
        setItineraryText("");

        const generatedActivities = (parsed.days || [])
          .flatMap((day) => (Array.isArray(day?.activities) ? day.activities : []));
        persistItineraryHistory({
          userId: plannerUserId,
          city: cityData.city,
          country: cityData.country,
          activities: generatedActivities,
        });

        if (openingErrs.length > 0) {
          showToast(
            `Itinerary generated with style notes:\n• ${openingErrs.slice(0, 2).join("\n• ")}${openingErrs.length > 2 ? "\n• …" : ""}`,
            "info",
            3500
          );
        }
        showToast("Itinerary validated ✓", "success", 2000);
      } else {
        const allErrs = [...errors, ...storyErrs];
        showToast(
          `Itinerary JSON had issues:\n• ${allErrs.slice(0, 4).join("\n• ")}${allErrs.length > 4 ? "\n• …" : ""}`,
          "error"
        );
        setItineraryJson(null);
        setItineraryText(JSON.stringify(parsed, null, 2));
      }
    } catch (error) {
      console.error(error);
      showToast("Request failed. Please try again.", "error");
      setItineraryJson(null);
      setItineraryText("Error generating itinerary. Please try again.");
    } finally {
      setLoading(false);
      setTimeout(() => setShowCtaPopup(false), 5000);
    }
  };

  const saveItinerary = async () => {
    if (!itineraryJson) {
      showToast("No itinerary to save yet.", "error");
      return;
    }

    try {
      localStorage.setItem("appi.itinerary.v1", JSON.stringify(itineraryJson));
    } catch (e) {
      console.warn("localStorage write failed", e);
    }

    const meta = itineraryJson.meta || {};
    const base = (meta.destination || "itinerary").replace(/[^a-z0-9_-]/gi, "_").toLowerCase();

    const now = new Date();
    const pad = (n) => String(n).padStart(2, "0");
    const ts = `${now.getFullYear()}${pad(now.getMonth() + 1)}${pad(now.getDate())}-${pad(now.getHours())}${pad(now.getMinutes())}`;
    const filename = `itinerary-${base}-${ts}.json`;

    const jsonText = JSON.stringify(itineraryJson, null, 2);
    const blob = new Blob([jsonText], { type: "application/json" });

    try {
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => {
        try {
          URL.revokeObjectURL(url);
        } catch {}
      }, 1000);

      showToast("Itinerary saved and downloaded ✓", "success", 3000);
      return;
    } catch (err) {
      console.warn("Download failed, falling back to clipboard", err);
    }

    if (navigator.clipboard && navigator.clipboard.writeText) {
      try {
        await navigator.clipboard.writeText(jsonText);
        showToast("Itinerary copied to clipboard ✓", "success", 3000);
        return;
      } catch (err) {
        console.warn("Clipboard fallback failed", err);
      }
    }

    showToast("Itinerary saved to browser storage ✓", "success", 3000);
  };

  const printPdf = () => {
    if (!itineraryJson) {
      showToast("No itinerary generated yet to print.", "error", 3000);
      return;
    }
    showToast("Attempting to open print preview...", "success", 1000);
    window.print();
  };

  // --- MULTI-CITY GENERATION ---
  const addMultiStop = () => {
    setMultiStops((prev) => [...prev, { id: Date.now(), city: "", country: "", nights: 2 }]);
  };
  const removeMultiStop = (id) => {
    setMultiStops((prev) => prev.length > 2 ? prev.filter((s) => s.id !== id) : prev);
  };
  const updateMultiStop = (id, field, value) => {
    setMultiStops((prev) => prev.map((s) => s.id === id ? { ...s, [field]: value } : s));
  };

  const handleMultiCityGenerate = async () => {
    const validStops = multiStops.filter((s) => s.city.trim() && s.country.trim());
    if (validStops.length < 2) {
      showToast("Please add at least 2 city stops with city and country filled in.", "error");
      return;
    }
    if (!selectedBudget) {
      showToast("Please select a budget before generating.", "error");
      return;
    }
    setMultiLoading(true);
    setMultiResult(null);

    const stopsList = validStops.map((s) => `${s.city}, ${s.country} (${s.nights} night${s.nights === 1 ? "" : "s"})`).join(" → ");
    const cityDateRanges = buildMultiCityDateRanges(validStops, multiStartDate);
    const budgetLabel = selectedBudget;
    const paceLabel = pace === 0 ? "Relaxed" : pace === 1 ? "Balanced" : "Action-packed";
    const startDateNote = parseIsoDateUtc(multiStartDate) ? multiStartDate : "not provided";

    const outputLanguage = resolveOutputLanguage(language);
    const outputStyleInstruction = resolveOutputStyleInstruction(language);

    const prompt = `You are an expert travel planner. Generate a detailed multi-city trip plan.

Trip route: ${stopsList}
  Trip start date (first city check-in): ${startDateNote}
Budget: ${budgetLabel}
Pace: ${paceLabel}
Output language: ${outputLanguage}

Return ONLY valid JSON (no markdown fences, no extra text) with this exact structure:
{
  "tripTitle": "Multi-City Trip: City1 → City2 → City3",
  "budget": "${budgetLabel}",
  "totalNights": ${validStops.reduce((s, x) => s + Number(x.nights), 0)},
  "cities": [
    {
      "cityName": "Paris",
      "country": "France",
      "nights": 2,
      "recommendedNeighborhood": "Le Marais",
      "neighborhoodWhy": "Central, walkable, great for mid-range travelers",
      "arrivalTransport": {
        "title": "Paris Airport → City Center",
        "train": { "name": "RER B", "duration": "35 min", "cost": "€11.80", "tip": "Runs every 15 min from CDG" },
        "bus": { "name": "Roissybus", "duration": "60 min", "cost": "€13.70", "tip": "Direct to Opéra" },
        "taxi": { "duration": "40–60 min", "cost": "€50–€70", "tip": "Fixed rate from CDG" },
        "note": ""
      },
      "transferToNext": {
        "to": "Rome",
        "options": [
          { "type": "Flight", "duration": "2h 20m", "cost": "€60–€180", "provider": "Air France / easyJet", "tip": "Book 4–6 weeks ahead for best price" },
          { "type": "Train", "duration": "N/A", "cost": "N/A", "tip": "No direct train, flight recommended" }
        ]
      },
      "accommodations": [
        {
          "rank": 1,
          "label": "Best Value Pick",
          "name": "Hotel Example",
          "type": "Hotel",
          "neighborhood": "Le Marais",
          "nightlyPrice": "€80–€110",
          "rating": "8.5/10",
          "whyItFits": "Central location, great reviews, suits ${budgetLabel} budget",
          "bookingLink": "https://www.booking.com/searchresults.html?ss=Paris"
        },
        {
          "rank": 2,
          "label": "Most Popular",
          "name": "Hotel Example 2",
          "type": "Hotel",
          "neighborhood": "Saint-Germain",
          "nightlyPrice": "€100–€130",
          "rating": "9.0/10",
          "whyItFits": "Highly rated by travellers, great breakfast, easy metro access",
          "bookingLink": "https://www.booking.com/searchresults.html?ss=Paris"
        },
        {
          "rank": 3,
          "label": "Best Location",
          "name": "Hotel Example 3",
          "type": "Boutique Hotel",
          "neighborhood": "Near Eiffel Tower",
          "nightlyPrice": "€120–€160",
          "rating": "8.8/10",
          "whyItFits": "Steps from main attractions, ideal for first-time visitors",
          "bookingLink": "https://www.booking.com/searchresults.html?ss=Paris"
        },
        {
          "rank": 4,
          "label": "Hidden Gem",
          "name": "Hotel Example 4",
          "type": "B&B",
          "neighborhood": "Montmartre",
          "nightlyPrice": "€65–€90",
          "rating": "9.2/10",
          "whyItFits": "Lesser known, authentic neighbourhood feel, outstanding value",
          "bookingLink": "https://www.booking.com/searchresults.html?ss=Paris"
        }
      ],
      "highlights": ["Top attraction 1", "Top attraction 2", "Top attraction 3"]
    }
  ]
}

Rules:
- Replace example data with REAL, accurate information for each city.
- Write all user-facing values/text in ${outputLanguage}. Keep JSON keys in English.
- ${outputStyleInstruction || "Use a clear and practical tone suitable for travel planning."}
- Each city MUST have exactly 4 accommodations with different labels: Best Value Pick, Most Popular, Best Location, Hidden Gem.
- Accommodation names must be real, well-known establishments appropriate for ${budgetLabel} budget.
- arrivalTransport must reflect the actual main airport for each city.
- Never invent a direct airport rail link. Only include a train/rail option when the airport has a direct rail/metro station.
- If there is no direct rail at that airport, set "train" to null and explain this clearly in arrivalTransport.note.
- Always include realistic bus/coach and taxi/chauffeur options for airport-to-city transfers.
- transferToNext is the recommended way to travel to the NEXT city. For the last city it should be null.
- nightlyPrice ranges must be realistic for ${budgetLabel} budget in that city.
- bookingLink should be a valid booking.com search URL for that city.
- Return ONLY valid JSON.`;

    try {
      const fetchJson = async (url) => {
        const res = await fetch(url, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ prompt, budget: budgetLabel }),
        });

        let data;
        try {
          data = await res.json();
        } catch {
          data = { error: "Non-JSON response from server" };
        }

        return { res, data };
      };

      const onCustomDomain =
        typeof window !== "undefined" &&
        /appiholidays\.com$/i.test(window.location.hostname) &&
        !/vercel\.app$/i.test(window.location.hostname);

      let { res, data } = await fetchJson(`${API_BASE}/api/gemini`);

      const shouldRetryWithFallback =
        process.env.NODE_ENV === "production" &&
        onCustomDomain &&
        (!res.ok || data?.error === "Non-JSON response from server");

      if (shouldRetryWithFallback) {
        ({ res, data } = await fetchJson(`${PROD_API_FALLBACK}/api/gemini`));
      }

      if (!res.ok) {
        const msg = formatApiError(data?.error || data?.details || data?.message || `HTTP ${res.status}`);
        throw new Error(msg);
      }

      if (data.error) {
        showToast(`${REGENERATE_NOTE}\nGemini error: ${formatApiError(data.error)}`, "error");
        return;
      }

      const raw = data.itinerary || data.text || data.candidates?.[0]?.content?.parts?.[0]?.text || "";
      let parsed = typeof raw === "object" ? raw : null;
      if (!parsed) {
        try {
          const jsonMatch = raw.match(/\{[\s\S]*\}/);
          parsed = jsonMatch ? JSON.parse(jsonMatch[0]) : null;
        } catch {
          parsed = null;
        }
      }

      if (!parsed || !Array.isArray(parsed.cities)) {
        showToast(`${REGENERATE_NOTE}\nCould not parse multi-city response. Please try again.`, "error");
        return;
      }

      parsed.cities = parsed.cities.map((cityObj, index) => {
        const stop = validStops[index] || {};
        const dateRange = cityDateRanges[index] || {};
        const cityName = cityObj?.cityName || stop.city || "City";
        const country = cityObj?.country || stop.country || "";
        const nights = Number(cityObj?.nights || stop.nights || 1);
        const accommodations = Array.isArray(cityObj?.accommodations)
          ? cityObj.accommodations.map((acc, accIndex) => ({
              ...acc,
              rank: Number(acc?.rank) || accIndex + 1,
              bookingLink: sanitizeAccommodationLink(
                acc?.bookingLink,
                cityName,
                country,
                String(acc?.name || "").trim(),
                dateRange.checkInDate,
                dateRange.checkOutDate,
                budgetLabel
              ),
            }))
          : [];

        return {
          ...cityObj,
          cityName,
          country,
          nights,
          checkInDate: dateRange.checkInDate || "",
          checkOutDate: dateRange.checkOutDate || "",
          accommodations,
          arrivalTransport: sanitizeMultiArrivalTransport(cityObj?.arrivalTransport, cityName, country),
        };
      });

      setMultiResult(parsed);
      trackAnalytics("generate_multi_success", { route: stopsList });
      showToast("Multi-city plan generated ✓", "success", 2500);
    } catch (err) {
      console.error(err);
      showToast(`${REGENERATE_NOTE}\nRequest failed: ${err.message}`, "error");
    } finally {
      setMultiLoading(false);
    }
  };

  const toggleRoadFeature = (feature) => {
    setRoadTripFeatures((prev) => {
      const isSelected = prev.includes(feature);
      let removedFeature = null;
      let addedFeature = null;
      let next = prev;

      if (isSelected) {
        removedFeature = feature;
        next = prev.filter((item) => item !== feature);
      } else if (prev.length >= 6) {
        removedFeature = prev[0];
        addedFeature = feature;
        next = [...prev.slice(1), feature];
      } else {
        addedFeature = feature;
        next = [...prev, feature];
      }

      setRoadFeatureColorClasses((prevClasses) => {
        const updated = { ...prevClasses };
        if (removedFeature) delete updated[removedFeature];
        if (addedFeature) updated[addedFeature] = getRandomRoadFeatureClass();
        return updated;
      });

      return next;
    });
  };

  const handleRoadTripGenerate = async () => {
    const start = roadTripForm.startPoint.trim();
    const end = roadTripForm.endPoint.trim();
    const tripDays = Math.max(2, Number(roadTripForm.tripDays) || 2);
    const isInterrailMode = tripMode === "interrail" || roadTripForm.vehicleType === "Interrail (Train)";

    if (!start || !end) {
      showToast("Please enter both a starting point and a final destination.", "error");
      return;
    }
    if (!selectedBudget) {
      showToast("Please select a budget before generating your road trip.", "error");
      return;
    }

    setRoadTripLoading(true);
    setRoadTripResult(null);

    const budgetLabel = selectedBudget;
    const sanitizedFeatures = isInterrailMode
      ? roadTripFeatures.filter((feature) => !INTERRAIL_EXCLUDED_FEATURES.has(feature))
      : roadTripFeatures;
    const features = sanitizedFeatures.length > 0 ? sanitizedFeatures : ["Places of interest"];
    const countryHint = roadTripForm.countryContext.trim();

    const outputLanguage = resolveOutputLanguage(language);
    const outputStyleInstruction = resolveOutputStyleInstruction(language);

    const prompt = `You are a ${isInterrailMode ? "European Interrail train journey planner" : "road trip planner"}. Build a practical and realistic route plan.

Start: ${start}
End: ${end}
Trip length: ${tripDays} days
Vehicle: ${roadTripForm.vehicleType}
Route style: ${roadTripForm.routeStyle}
Budget: ${budgetLabel}
Country context (optional): ${countryHint || "none"}
Requested features: ${features.join(", ")}
Output language: ${outputLanguage}

Return ONLY valid JSON with this exact structure:
{
  "title": "Road Trip: Start → End",
  "summary": "short practical overview",
  "totalDays": 0,
  "totalDistance": "string",
  "estimatedDrivingTime": "string",
  "routeMapLinks": {
    "googleMaps": "https://...",
    "appleMaps": "https://...",
    "waze": "https://..."
  },
  "legs": [
    {
      "day": 1,
      "from": "string",
      "to": "string",
      "distance": "string",
      "driveTime": "string",
      "stopover": "string",
      "highlights": ["string"],
      "activities": ["string"],
      "fuelAndCharging": {
        "evCharging": ["string"],
        "hvoStops": ["string"],
        "fuelTip": "string"
      },
    }
  ],
  "safetyNotes": ["string"],
  "singleCityReminder": "Generate a full city itinerary from your hotel in Single City mode."
}

Rules:
- Keep data realistic; no invented transport modes.
- Write all user-facing values/text in ${outputLanguage}. Keep JSON keys in English.
- ${outputStyleInstruction || "Use a clear and practical tone suitable for travel planning."}
- ${isInterrailMode ? "Prioritize practical rail operators, station-to-station routing, and overnight train options where relevant." : "Include camping/glamping/campervan options where relevant to the route."}
- Include hotel and B&B options when requested in features.
- For highlights, include a map or official URL when possible (either inline in the string or as an object field).
- ${isInterrailMode ? "If fuel or EV fields are not relevant, clearly state N/A for train travel." : "Include motorbike-friendly note if route is suitable."}
- ${isInterrailMode ? "Include rail pass practical tips, transfer buffers, and booking windows for high-demand routes." : "Include EV charging and HVO guidance where available; if unavailable, say so."}
- Tailor costs to ${budgetLabel} budget.
- Return only JSON.`;

    try {
      const fetchRoad = async (url) => {
        const res = await fetch(url, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ prompt, budget: budgetLabel }),
        });

        let data;
        try {
          data = await res.json();
        } catch {
          data = { error: "Non-JSON response from server" };
        }

        return { res, data };
      };

      const onCustomDomain =
        typeof window !== "undefined" &&
        /appiholidays\.com$/i.test(window.location.hostname) &&
        !/vercel\.app$/i.test(window.location.hostname);

      let { res, data } = await fetchRoad(`${API_BASE}/api/gemini`);

      const shouldRetryWithFallback =
        process.env.NODE_ENV === "production" &&
        onCustomDomain &&
        (!res.ok || data?.error === "Non-JSON response from server");

      if (shouldRetryWithFallback) {
        ({ res, data } = await fetchRoad(`${PROD_API_FALLBACK}/api/gemini`));
      }

      if (!res.ok || data?.error) {
        const msg = formatApiError(data?.error || data?.details || `HTTP ${res.status}`);
        showToast(`${REGENERATE_NOTE}\nRoad Trip error: ${msg}`, "error");
        return;
      }

      const raw = data.itinerary || data.text || data.candidates?.[0]?.content?.parts?.[0]?.text || "";
      let parsed = typeof raw === "object" ? raw : null;
      if (!parsed) {
        const jsonCandidate = extractJson(String(raw));
        parsed = JSON.parse(jsonCandidate);
      }

      if (!parsed || !Array.isArray(parsed.legs) || parsed.legs.length === 0) {
        showToast(`${REGENERATE_NOTE}\nCould not parse road trip response. Please try again.`, "error");
        return;
      }

      // Build reliable map links from actual start/end/waypoint data
      // Google Maps format works on mobile browser, Android Auto, and car infotainment
      const origin = encodeURIComponent(roadTripForm.startPoint);
      const destination = encodeURIComponent(roadTripForm.endPoint);
      const waypoints = parsed.legs.length > 1
        ? parsed.legs.slice(0, -1).map(l => encodeURIComponent(l.to || "")).filter(Boolean).join("|")
        : "";
      const googleUrl = `https://www.google.com/maps/dir/?api=1&origin=${origin}&destination=${destination}&travelmode=driving${waypoints ? `&waypoints=${waypoints}` : ""}`;
      const appleUrl = `https://maps.apple.com/?saddr=${origin}&daddr=${destination}&dirflg=d`;
      const wazeUrl = `https://waze.com/ul?q=${destination}&navigate=yes`;
      parsed.routeMapLinks = { googleMaps: googleUrl, appleMaps: appleUrl, waze: wazeUrl };

      const legDateRanges = buildRoadTripLegDateRanges(
        parsed.legs,
        roadTripForm.startDate,
        parsed.totalDays || tripDays
      );

      parsed.legs = parsed.legs.map((leg, index) => {
        const stopCity = extractRoadTripStopCity(leg?.to || leg?.stopover || leg?.from || `Stop ${index + 1}`);
        const country = roadTripForm.countryContext.trim();
        const dateRange = legDateRanges[index] || {};

        const fallback = buildRoadTripOvernightFallback({
          stopCity,
          country,
          budget: budgetLabel,
          features: roadTripFeatures,
          checkIn: dateRange.checkInDate,
          checkOut: dateRange.checkOutDate,
        });

        return {
          ...leg,
          checkInDate: dateRange.checkInDate || "",
          checkOutDate: dateRange.checkOutDate || "",
          stayNights: dateRange.nights || 1,
          overnightOptions: fallback.slice(0, 4).map((opt, optIndex) => ({
            ...opt,
            name: buildRoadTripStayDisplayName({
              stopCity,
              type: opt.type,
              budget: budgetLabel,
              candidateName: opt.name,
            }),
            label: opt.label || ROAD_TRIP_OVERNIGHT_LABELS[optIndex] || `Option ${optIndex + 1}`,
          })),
        };
      });

      setRoadTripResult(parsed);
      trackAnalytics("generate_road_success", { start, end });
      showToast("Road Trip plan generated ✓", "success", 2500);
    } catch (err) {
      console.error(err);
      showToast(`${REGENERATE_NOTE}\nRoad Trip request failed: ${err.message}`, "error");
    } finally {
      setRoadTripLoading(false);
    }
  };

  const [bookingPanelStartDate, setBookingPanelStartDate] = useState(destinationData.startDate || "");
  const [bookingPanelCheckOutDate, setBookingPanelCheckOutDate] = useState("");
  useEffect(() => {
    setBookingPanelStartDate((prev) => (String(prev || "").trim() ? prev : destinationData.startDate || ""));
  }, [destinationData.startDate]);

  const derivedSingleCityBookingDates = useMemo(
    () => buildSingleCityStayDates(bookingPanelStartDate, destinationData.duration),
    [bookingPanelStartDate, destinationData.duration]
  );
  const singleCityBookingDates = {
    ...derivedSingleCityBookingDates,
    checkOutDate: bookingPanelCheckOutDate || derivedSingleCityBookingDates.checkOutDate,
  };
  const [bookingGuests, setBookingGuests] = useState({ rooms: 1, adults: 2, children: 0 });
  const [desktopBookingPopover, setDesktopBookingPopover] = useState("");
  const [desktopCalendarMonth, setDesktopCalendarMonth] = useState(() => {
    const initial = parseIsoDateUtc(destinationData.startDate || "");
    return getCalendarMonthStart(initial || new Date());
  });
  const desktopBookingPanelRef = useRef(null);
  const mobileBookingPanelRef = useRef(null);

  useEffect(() => {
    const onPointerDown = (event) => {
      const clickedDesktopPanel = desktopBookingPanelRef.current?.contains(event.target);
      const clickedMobilePanel = mobileBookingPanelRef.current?.contains(event.target);
      if (clickedDesktopPanel || clickedMobilePanel) return;
      setDesktopBookingPopover("");
    };

    document.addEventListener("mousedown", onPointerDown);
    return () => document.removeEventListener("mousedown", onPointerDown);
  }, []);

  const adjustGuestCount = (field, delta, min = 0, max = 20) => {
    setBookingGuests((prev) => {
      const current = Number(prev[field]) || 0;
      const next = Math.min(max, Math.max(min, current + delta));
      return { ...prev, [field]: next };
    });
  };

  const bookingGuestSummary = useMemo(() => {
    const roomLabel = `${bookingGuests.rooms} room${bookingGuests.rooms === 1 ? "" : "s"}`;
    const adultLabel = `${bookingGuests.adults} adult${bookingGuests.adults === 1 ? "" : "s"}`;
    const childLabel = bookingGuests.children > 0 ? `, ${bookingGuests.children} child${bookingGuests.children === 1 ? "" : "ren"}` : "";
    return `${roomLabel}, ${adultLabel}${childLabel}`;
  }, [bookingGuests]);

  const bookingDateSummary = useMemo(() => {
    const checkInLabel = formatBookingDateLabel(singleCityBookingDates.checkInDate);
    const checkOutLabel = formatBookingDateLabel(bookingPanelCheckOutDate || singleCityBookingDates.checkOutDate);
    if (!checkInLabel) return "Select dates";
    if (!bookingPanelCheckOutDate) return `${checkInLabel} - Select end date`;
    if (!checkOutLabel) return "Select dates";
    return `${checkInLabel} - ${checkOutLabel}`;
  }, [singleCityBookingDates.checkInDate, singleCityBookingDates.checkOutDate, bookingPanelCheckOutDate]);

  const desktopCalendarPanels = useMemo(() => {
    return [0, 1].map((offset) => {
      const monthStart = shiftCalendarMonth(desktopCalendarMonth, offset);
      return {
        monthStart,
        title: monthStart.toLocaleDateString("en-IE", { month: "long", year: "numeric", timeZone: "UTC" }),
        cells: buildCalendarMonthCells(monthStart),
      };
    });
  }, [desktopCalendarMonth]);

  const openDesktopDatesPopover = () => {
    if (isBookingFormLocked) return;
    const nextState = desktopBookingPopover === "dates" ? "" : "dates";
    if (nextState === "dates") {
      const anchor = parseIsoDateUtc(singleCityBookingDates.checkInDate || bookingPanelStartDate || "");
      setDesktopCalendarMonth(getCalendarMonthStart(anchor || new Date()));
    }
    setDesktopBookingPopover(nextState);
  };

  const openMobileDatesPopover = () => {
    if (isBookingFormLocked) return;
    const nextState = desktopBookingPopover === "mobile-dates" ? "" : "mobile-dates";
    if (nextState === "mobile-dates") {
      const anchor = parseIsoDateUtc(singleCityBookingDates.checkInDate || bookingPanelStartDate || "");
      setDesktopCalendarMonth(getCalendarMonthStart(anchor || new Date()));
    }
    setDesktopBookingPopover(nextState);
  };

  const selectDesktopBookingDate = (isoDate) => {
    const currentCheckIn = bookingPanelStartDate;
    const currentCheckOut = bookingPanelCheckOutDate;

    // First click (or restarting after a complete range): set check-in only.
    if (!currentCheckIn || currentCheckOut) {
      setBookingPanelStartDate(isoDate);
      setBookingPanelCheckOutDate("");
      return;
    }

    // If second click is same day or earlier, treat it as new check-in.
    if (isoDate <= currentCheckIn) {
      setBookingPanelStartDate(isoDate);
      setBookingPanelCheckOutDate("");
      return;
    }

    // Valid second click sets checkout and closes the popover.
    setBookingPanelCheckOutDate(isoDate);
    setDesktopBookingPopover("");
  };
  const defaultSingleCityBookingCity = destinationData.city || destinationData.country || "";
  const [bookingPanelCity, setBookingPanelCity] = useState(defaultSingleCityBookingCity);
  useEffect(() => {
    setBookingPanelCity((prev) => (String(prev || "").trim() ? prev : defaultSingleCityBookingCity));
  }, [defaultSingleCityBookingCity]);

  const singleCityBookingCity = bookingPanelCity;
  const singleCityBookingLink = buildBookingCityLink(
    singleCityBookingCity,
    destinationData.country || "",
    "",
    singleCityBookingDates.checkInDate,
    singleCityBookingDates.checkOutDate,
    selectedBudget,
    bookingGuests.adults
  );
  const isBookingFormLocked = false;
  const canOpenSingleCityBooking = !isBookingFormLocked;
  const canRequestInstall = Boolean(deferredInstallPrompt && typeof deferredInstallPrompt.prompt === "function");
  const isIosDevice = useMemo(() => {
    if (typeof window === "undefined") return false;
    const ua = window.navigator?.userAgent || "";
    const platform = window.navigator?.platform || "";
    const touchPoints = Number(window.navigator?.maxTouchPoints || 0);
    return /iPad|iPhone|iPod/i.test(ua) || (platform === "MacIntel" && touchPoints > 1);
  }, []);

  const dismissInstallPrompt = () => {
    setIsInstallPromptDismissed(true);
    try {
      window.localStorage.setItem(INSTALL_PROMPT_DISMISSED_KEY, "1");
    } catch {}
  };

  const requestAppInstall = async () => {
    if (!canRequestInstall) {
      showToast("Install prompt is not available yet on this browser/device.", "error", 2800);
      return;
    }

    trackAnalytics("app_install_clicked", { source: "install_shell" });

    try {
      await deferredInstallPrompt.prompt();
      const userChoice = await deferredInstallPrompt.userChoice;
      if (userChoice?.outcome === "accepted") {
        showToast("Thanks. App install started.", "success", 2200);
      } else {
        showToast("Install was dismissed by the user.", "error", 2200);
      }
    } catch {
      showToast("Could not open install prompt.", "error", 2200);
    } finally {
      setDeferredInstallPrompt(null);
    }
  };

  return (
    <div className="min-h-screen bg-white p-4 sm:p-5 md:p-6 lg:bg-[radial-gradient(circle_at_top,_rgba(16,185,129,0.10),_transparent_28%),linear-gradient(135deg,_#f8fafc_0%,_#eef6ff_52%,_#f7fee7_100%)] lg:p-8">
      {toast.show && (
        <div
          className={`fixed top-4 left-1/2 -translate-x-1/2 z-50 px-4 py-3 rounded-xl shadow-lg text-sm ${
            toast.type === "success" ? "bg-green-600 text-white" : "bg-rose-600 text-white"
          }`}
          role="alert"
        >
          <div className="flex items-start gap-2">
            <span>{toast.type === "success" ? "✅" : "⚠️"}</span>
            <div className="whitespace-pre-line">
              {toast.type === "error" && <div className="font-medium mb-1">{REGENERATE_NOTE}</div>}
              <div>{toast.message}</div>
            </div>
            <button className="ml-2 opacity-80" onClick={() => setToast((t) => ({ ...t, show: false }))}>
              ✖
            </button>
          </div>
        </div>
      )}

      {currentPage === "planner" && !isAppInstalled && !isInstallPromptDismissed && (
        <div className="fixed bottom-4 right-4 z-40 w-[calc(100vw-2rem)] max-w-sm rounded-2xl border border-emerald-200 bg-white/95 p-4 shadow-xl backdrop-blur">
          <div className="flex items-start justify-between gap-3">
            <div>
              <div className="text-xs font-semibold uppercase tracking-wide text-emerald-700">Install App</div>
              <div className="mt-1 text-sm font-bold text-slate-900">Download AppiHolidays</div>
              <p className="mt-1 text-xs text-slate-600">Install for faster access and an app-like experience on mobile and desktop.</p>
            </div>
            <button
              type="button"
              onClick={dismissInstallPrompt}
              className="rounded-md px-2 py-1 text-xs font-semibold text-slate-500 hover:bg-slate-100"
              aria-label="Dismiss install prompt"
            >
              Not now
            </button>
          </div>
          <div className="mt-3 flex flex-wrap gap-2">
            <button
              type="button"
              onClick={requestAppInstall}
              disabled={!canRequestInstall}
              className="inline-flex items-center rounded-xl bg-emerald-600 px-3 py-2 text-xs font-semibold text-white hover:bg-emerald-700 disabled:cursor-not-allowed disabled:bg-slate-300"
            >
              Install AppiHolidays
            </button>
            {!canRequestInstall && !isIosDevice && (
              <span className="inline-flex items-center rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-[11px] font-medium text-slate-600">
                Install prompt is unavailable on this device/browser.
              </span>
            )}
            {!canRequestInstall && isIosDevice && (
              <div className="w-full rounded-xl border border-emerald-200 bg-emerald-50 px-3 py-2 text-[11px] text-emerald-900">
                <div className="font-semibold">Install on iPhone:</div>
                <div className="mt-1">1) Tap the Share icon in Safari (square with arrow).</div>
                <div>2) Scroll and tap Add to Home Screen.</div>
                <div>3) Tap Add in the top-right corner.</div>
              </div>
            )}
          </div>
        </div>
      )}

      <div className="max-w-2xl lg:max-w-7xl mx-auto desktop-container">

        {currentPage === "admin" ? (
          adminAuthorized ? (
            <AdminDashboardPage analytics={analyticsSnapshot} installInsights={installInsights} visitorInsights={visitorInsights} bookingInsights={bookingInsights} onBackToPlanner={lockAdminDashboard} />
          ) : (
            <AdminAccessGate
              loading={adminAuthLoading}
              errorMessage={adminAccessError}
              onLogin={unlockAdminDashboard}
              onBackToPlanner={() => {
                setAdminAccessError("");
                window.location.hash = "";
              }}
            />
          )
        ) : currentPage !== "planner" && policyData[currentPage] ? (
          <PolicyPage policyName={currentPage} onBackToPlanner={() => (window.location.hash = "")} />
        ) : (
          <>
            {tripMode === "single" && (
              <div className="mb-5">
                <div ref={mobileBookingPanelRef} className="relative lg:hidden rounded-[24px] border border-sky-300 bg-[radial-gradient(circle_at_top_right,rgba(74,180,176,0.48),transparent_44%),radial-gradient(circle_at_bottom_right,rgba(136,199,255,0.52),transparent_40%),linear-gradient(145deg,#1f4d8c_0%,#1f569a_58%,#215fa8_100%)] p-5 shadow-[0_16px_34px_rgba(15,23,42,0.22)]">
                  <div className="mt-1 flex justify-center">
                    <span className="inline-flex items-center justify-center overflow-hidden rounded-xl bg-white px-[0.875rem] pt-1.5 pb-1 shadow-lg">
                      <img
                        src="/appihotels-logo-smile.png"
                        alt="AppiHotels"
                        className="h-14 w-auto -translate-y-1 sm:h-16"
                      />
                    </span>
                  </div>
                  <p className="mx-auto mt-2 max-w-xl text-center text-sm text-sky-100/95 sm:text-base">
                    Compare hotels worldwide, see the all-in price up front, and confirm instantly.
                  </p>
                  <div className="mt-3 space-y-2 text-xs">
                    <div className="rounded-xl border border-slate-200 bg-white px-3 py-2">
                      <label className="block font-semibold text-slate-600 mb-1">Where</label>
                      <input
                        type="text"
                        value={singleCityBookingCity}
                        onChange={(e) => setBookingPanelCity(e.target.value)}
                        placeholder="City, airport, hotel, or landmark"
                        disabled={isBookingFormLocked}
                        className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-slate-900 focus:border-sky-400 focus:ring-2 focus:ring-sky-100 disabled:cursor-not-allowed disabled:bg-slate-100 disabled:text-slate-500"
                      />
                    </div>
                    <div className="rounded-xl border border-slate-200 bg-white px-3 py-2">
                      <label className="block font-semibold text-slate-600 mb-1">Dates</label>
                      <button
                        type="button"
                        onClick={openMobileDatesPopover}
                        disabled={isBookingFormLocked}
                        className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-left text-sm font-medium text-slate-900 hover:bg-sky-50 disabled:cursor-not-allowed disabled:bg-slate-100 disabled:text-slate-500"
                      >
                        {bookingDateSummary}
                      </button>
                    </div>
                    <div className="rounded-xl border border-slate-200 bg-white px-3 py-2">
                      <label className="block font-semibold text-slate-600 mb-1">Guests</label>
                      <button
                        type="button"
                        onClick={() => setDesktopBookingPopover((prev) => (prev === "mobile-guests" ? "" : "mobile-guests"))}
                        disabled={isBookingFormLocked}
                        className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-left text-sm font-medium text-slate-900 hover:bg-sky-50 disabled:cursor-not-allowed disabled:bg-slate-100 disabled:text-slate-500"
                      >
                        {bookingGuestSummary}
                      </button>
                    </div>
                  </div>

                  {!isBookingFormLocked && desktopBookingPopover === "mobile-dates" && (
                    <div className="absolute left-4 right-4 top-[260px] z-[80] rounded-2xl border border-slate-200 bg-white p-3 shadow-2xl">
                      <div className="mb-2 flex items-center justify-between">
                        <button
                          type="button"
                          onClick={() => setDesktopCalendarMonth((prev) => shiftCalendarMonth(prev, -1))}
                          className="rounded-lg border border-slate-200 px-2.5 py-1 text-xs font-semibold text-slate-700 hover:bg-slate-50"
                        >
                          Prev
                        </button>
                        <button
                          type="button"
                          onClick={() => setDesktopCalendarMonth((prev) => shiftCalendarMonth(prev, 1))}
                          className="rounded-lg border border-slate-200 px-2.5 py-1 text-xs font-semibold text-slate-700 hover:bg-slate-50"
                        >
                          Next
                        </button>
                      </div>

                      <div className="grid grid-cols-1 gap-3 max-h-[55vh] overflow-auto">
                        {desktopCalendarPanels.map((panel) => (
                          <div key={`mobile-top-${panel.title}`}>
                            <div className="mb-1.5 text-xs font-semibold text-slate-800">{panel.title}</div>
                            <div className="grid grid-cols-7 gap-0.5 text-center text-[10px] text-slate-500">
                              {CALENDAR_WEEKDAY_LABELS.map((dayLabel) => (
                                <div key={`${panel.title}-mobile-top-${dayLabel}`} className="py-0.5">{dayLabel}</div>
                              ))}
                            </div>
                            <div className="grid grid-cols-7 gap-0.5 text-center">
                              {panel.cells.map((cell) => {
                                const checkIn = bookingPanelStartDate || singleCityBookingDates.checkInDate;
                                const checkOut = bookingPanelCheckOutDate;
                                const isSelectedStart = Boolean(checkIn) && cell.iso === checkIn;
                                const isSelectedEnd = Boolean(checkOut) && cell.iso === checkOut;
                                const inRange = Boolean(checkIn && checkOut) && cell.iso > checkIn && cell.iso < checkOut;

                                return (
                                  <button
                                    key={`${panel.title}-mobile-top-${cell.iso}`}
                                    type="button"
                                    onClick={() => selectDesktopBookingDate(cell.iso)}
                                    className={`rounded-md py-1 text-xs transition ${
                                      isSelectedStart || isSelectedEnd
                                        ? "bg-sky-600 text-white"
                                        : inRange
                                        ? "bg-sky-100 text-sky-900"
                                        : cell.inMonth
                                        ? "text-slate-800 hover:bg-slate-100"
                                        : "text-slate-300 hover:bg-slate-50"
                                    }`}
                                  >
                                    {cell.day}
                                  </button>
                                );
                              })}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {!isBookingFormLocked && desktopBookingPopover === "mobile-guests" && (
                    <div className="absolute left-4 right-4 top-[260px] z-[80] rounded-2xl border border-slate-200 bg-white p-4 shadow-2xl">
                      <div className="space-y-3">
                        <div className="flex items-center justify-between">
                          <div>
                            <div className="text-sm font-semibold text-slate-900">Rooms</div>
                            <div className="text-xs text-slate-500">1 or more</div>
                          </div>
                          <div className="flex items-center gap-2">
                            <button type="button" onClick={() => adjustGuestCount("rooms", -1, 1, 8)} className="h-8 w-8 rounded-full border border-slate-300 text-slate-700">-</button>
                            <span className="w-5 text-center text-sm font-semibold text-slate-900">{bookingGuests.rooms}</span>
                            <button type="button" onClick={() => adjustGuestCount("rooms", 1, 1, 8)} className="h-8 w-8 rounded-full border border-slate-300 text-slate-700">+</button>
                          </div>
                        </div>

                        <div className="flex items-center justify-between">
                          <div>
                            <div className="text-sm font-semibold text-slate-900">Adults</div>
                            <div className="text-xs text-slate-500">Age 18+</div>
                          </div>
                          <div className="flex items-center gap-2">
                            <button type="button" onClick={() => adjustGuestCount("adults", -1, 1, 16)} className="h-8 w-8 rounded-full border border-slate-300 text-slate-700">-</button>
                            <span className="w-5 text-center text-sm font-semibold text-slate-900">{bookingGuests.adults}</span>
                            <button type="button" onClick={() => adjustGuestCount("adults", 1, 1, 16)} className="h-8 w-8 rounded-full border border-slate-300 text-slate-700">+</button>
                          </div>
                        </div>

                        <div className="flex items-center justify-between">
                          <div>
                            <div className="text-sm font-semibold text-slate-900">Children</div>
                            <div className="text-xs text-slate-500">Age 0-17</div>
                          </div>
                          <div className="flex items-center gap-2">
                            <button type="button" onClick={() => adjustGuestCount("children", -1, 0, 10)} className="h-8 w-8 rounded-full border border-slate-300 text-slate-700">-</button>
                            <span className="w-5 text-center text-sm font-semibold text-slate-900">{bookingGuests.children}</span>
                            <button type="button" onClick={() => adjustGuestCount("children", 1, 0, 10)} className="h-8 w-8 rounded-full border border-slate-300 text-slate-700">+</button>
                          </div>
                        </div>
                      </div>

                      <button
                        type="button"
                        onClick={() => setDesktopBookingPopover("")}
                        className="mt-4 w-full rounded-lg bg-sky-600 px-3 py-2 text-sm font-semibold text-white hover:bg-sky-500"
                      >
                        Done
                      </button>
                    </div>
                  )}

                  <a
                    href={canOpenSingleCityBooking ? singleCityBookingLink : "#"}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={(e) => {
                      if (!canOpenSingleCityBooking) {
                        e.preventDefault();
                        return;
                      }
                      trackAnalytics("booking_click", { city: singleCityBookingCity || "Single City" });
                    }}
                    className={`mt-3 inline-flex w-full items-center justify-center rounded-lg px-3 py-2 text-sm font-semibold ${
                      canOpenSingleCityBooking
                        ? "bg-gradient-to-r from-sky-600 to-blue-600 text-white hover:from-sky-500 hover:to-blue-500"
                        : "bg-slate-200 text-slate-500 cursor-not-allowed"
                    }`}
                  >
                    <span className="inline-flex items-center gap-2">
                      <Search className="h-4 w-4" />
                      <span>{isBookingFormLocked ? "Temporarily locked" : "Search"}</span>
                    </span>
                  </a>
                </div>

                <div className="hidden lg:block">
                  <div id="booking-panel" ref={desktopBookingPanelRef} className="relative mx-auto max-w-6xl rounded-[24px] border border-sky-300 bg-[radial-gradient(circle_at_top_right,rgba(74,180,176,0.48),transparent_44%),radial-gradient(circle_at_bottom_right,rgba(136,199,255,0.52),transparent_40%),linear-gradient(145deg,#1f4d8c_0%,#1f569a_58%,#215fa8_100%)] p-5 shadow-[0_16px_34px_rgba(15,23,42,0.22)]">
                    <div className="mt-1 flex justify-center">
                      <span className="inline-flex items-center justify-center overflow-hidden rounded-xl bg-white px-[0.875rem] pt-1.5 pb-1 shadow-lg">
                        <img
                          src="/appihotels-logo-smile.png"
                          alt="AppiHotels"
                          className="h-14 w-auto -translate-y-1 sm:h-16"
                        />
                      </span>
                    </div>
                    <p className="mx-auto mt-2 max-w-xl text-center text-sm text-sky-100/95 sm:text-base">
                      Compare hotels worldwide, see the all-in price up front, and confirm instantly.
                    </p>
                    <div className="mt-3 rounded-2xl border border-slate-200 bg-white shadow-[0_6px_14px_rgba(15,23,42,0.12)]">
                      <div className="grid grid-cols-12">
                        <div className="col-span-4 border-r border-slate-200 px-3 py-2">
                          <label className="block text-[11px] font-semibold text-slate-500">Where</label>
                          <input
                            type="text"
                            value={singleCityBookingCity}
                            onChange={(e) => setBookingPanelCity(e.target.value)}
                            placeholder="City, airport, hotel, or landmark"
                            disabled={isBookingFormLocked}
                            className="w-full border-0 p-0 text-base font-medium text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-0 disabled:cursor-not-allowed disabled:text-slate-500"
                          />
                        </div>
                        <button
                          type="button"
                          onClick={openDesktopDatesPopover}
                          disabled={isBookingFormLocked}
                          className="col-span-3 border-r border-slate-200 px-3 py-2 text-left hover:bg-sky-50 disabled:cursor-not-allowed disabled:bg-slate-100"
                        >
                          <span className="block text-[11px] font-semibold text-slate-500">Dates</span>
                          <span className="block text-base font-medium text-slate-900">{bookingDateSummary}</span>
                        </button>
                        <button
                          type="button"
                          onClick={() => setDesktopBookingPopover((prev) => (prev === "guests" ? "" : "guests"))}
                          disabled={isBookingFormLocked}
                          className="col-span-3 border-r border-slate-200 px-3 py-2 text-left hover:bg-sky-50 disabled:cursor-not-allowed disabled:bg-slate-100"
                        >
                          <span className="block text-[11px] font-semibold text-slate-500">Guests</span>
                          <span className="block text-base font-medium text-slate-900 truncate">{bookingGuestSummary}</span>
                        </button>
                        <a
                          href={canOpenSingleCityBooking ? singleCityBookingLink : "#"}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={(e) => {
                            if (!canOpenSingleCityBooking) {
                              e.preventDefault();
                              return;
                            }
                            trackAnalytics("booking_click", { city: singleCityBookingCity || "Single City" });
                          }}
                          className={`col-span-2 inline-flex items-center justify-center gap-2 px-3 py-2 text-sm font-semibold transition ${
                            canOpenSingleCityBooking
                              ? "text-sky-700 hover:bg-sky-50"
                              : "text-slate-400 cursor-not-allowed"
                          }`}
                        >
                          <Search className="h-4 w-4" />
                          <span>{isBookingFormLocked ? "Locked" : "Search"}</span>
                        </a>
                      </div>
                    </div>

                    {!isBookingFormLocked && desktopBookingPopover === "dates" && (
                      <div className="absolute right-5 top-[192px] z-[80] w-[560px] max-w-[calc(100vw-4rem)] rounded-2xl border border-slate-200 bg-white p-3 shadow-2xl">
                        <div className="mb-2 flex items-center justify-between">
                          <button
                            type="button"
                            onClick={() => setDesktopCalendarMonth((prev) => shiftCalendarMonth(prev, -1))}
                            className="rounded-lg border border-slate-200 px-2.5 py-1 text-xs font-semibold text-slate-700 hover:bg-slate-50"
                          >
                            Prev
                          </button>
                          <button
                            type="button"
                            onClick={() => setDesktopCalendarMonth((prev) => shiftCalendarMonth(prev, 1))}
                            className="rounded-lg border border-slate-200 px-2.5 py-1 text-xs font-semibold text-slate-700 hover:bg-slate-50"
                          >
                            Next
                          </button>
                        </div>

                        <div className="grid grid-cols-2 gap-3">
                          {desktopCalendarPanels.map((panel) => (
                            <div key={`top-${panel.title}`}>
                              <div className="mb-1.5 text-xs font-semibold text-slate-800">{panel.title}</div>
                              <div className="grid grid-cols-7 gap-0.5 text-center text-[10px] text-slate-500">
                                {CALENDAR_WEEKDAY_LABELS.map((dayLabel) => (
                                  <div key={`${panel.title}-top-${dayLabel}`} className="py-0.5">{dayLabel}</div>
                                ))}
                              </div>
                              <div className="grid grid-cols-7 gap-0.5 text-center">
                                {panel.cells.map((cell) => {
                                  const checkIn = bookingPanelStartDate || singleCityBookingDates.checkInDate;
                                  const checkOut = bookingPanelCheckOutDate;
                                  const isSelectedStart = Boolean(checkIn) && cell.iso === checkIn;
                                  const isSelectedEnd = Boolean(checkOut) && cell.iso === checkOut;
                                  const inRange = Boolean(checkIn && checkOut) && cell.iso > checkIn && cell.iso < checkOut;

                                  return (
                                    <button
                                      key={`${panel.title}-top-${cell.iso}`}
                                      type="button"
                                      onClick={() => selectDesktopBookingDate(cell.iso)}
                                      className={`rounded-md py-1 text-xs transition ${
                                        isSelectedStart || isSelectedEnd
                                          ? "bg-sky-600 text-white"
                                          : inRange
                                          ? "bg-sky-100 text-sky-900"
                                          : cell.inMonth
                                          ? "text-slate-800 hover:bg-slate-100"
                                          : "text-slate-300 hover:bg-slate-50"
                                      }`}
                                    >
                                      {cell.day}
                                    </button>
                                  );
                                })}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {!isBookingFormLocked && desktopBookingPopover === "guests" && (
                      <div className="absolute right-5 top-[192px] z-[80] w-[320px] rounded-2xl border border-slate-200 bg-white p-4 shadow-2xl">
                        <div className="space-y-3">
                          <div className="flex items-center justify-between">
                            <div>
                              <div className="text-sm font-semibold text-slate-900">Rooms</div>
                              <div className="text-xs text-slate-500">1 or more</div>
                            </div>
                            <div className="flex items-center gap-2">
                              <button type="button" onClick={() => adjustGuestCount("rooms", -1, 1, 8)} className="h-8 w-8 rounded-full border border-slate-300 text-slate-700">-</button>
                              <span className="w-5 text-center text-sm font-semibold text-slate-900">{bookingGuests.rooms}</span>
                              <button type="button" onClick={() => adjustGuestCount("rooms", 1, 1, 8)} className="h-8 w-8 rounded-full border border-slate-300 text-slate-700">+</button>
                            </div>
                          </div>

                          <div className="flex items-center justify-between">
                            <div>
                              <div className="text-sm font-semibold text-slate-900">Adults</div>
                              <div className="text-xs text-slate-500">Age 18+</div>
                            </div>
                            <div className="flex items-center gap-2">
                              <button type="button" onClick={() => adjustGuestCount("adults", -1, 1, 16)} className="h-8 w-8 rounded-full border border-slate-300 text-slate-700">-</button>
                              <span className="w-5 text-center text-sm font-semibold text-slate-900">{bookingGuests.adults}</span>
                              <button type="button" onClick={() => adjustGuestCount("adults", 1, 1, 16)} className="h-8 w-8 rounded-full border border-slate-300 text-slate-700">+</button>
                            </div>
                          </div>

                          <div className="flex items-center justify-between">
                            <div>
                              <div className="text-sm font-semibold text-slate-900">Children</div>
                              <div className="text-xs text-slate-500">Age 0-17</div>
                            </div>
                            <div className="flex items-center gap-2">
                              <button type="button" onClick={() => adjustGuestCount("children", -1, 0, 10)} className="h-8 w-8 rounded-full border border-slate-300 text-slate-700">-</button>
                              <span className="w-5 text-center text-sm font-semibold text-slate-900">{bookingGuests.children}</span>
                              <button type="button" onClick={() => adjustGuestCount("children", 1, 0, 10)} className="h-8 w-8 rounded-full border border-slate-300 text-slate-700">+</button>
                            </div>
                          </div>
                        </div>

                        <button
                          type="button"
                          onClick={() => setDesktopBookingPopover("")}
                          className="mt-4 w-full rounded-lg bg-sky-600 px-3 py-2 text-sm font-semibold text-white hover:bg-sky-500"
                        >
                          Done
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* ── Mode Toggle ── */}
            <div className="flex justify-center mb-5">
              <div className="flex flex-wrap items-center justify-center gap-3">
                <button
                  type="button"
                  onClick={() => handleTripModeSelect("single")}
                  className={`relative overflow-hidden rounded-2xl border border-gray-200 px-5 py-2.5 pl-7 text-sm font-semibold transition-all before:absolute before:left-0 before:top-0 before:h-full before:w-1.5 ${modeButtonStripeClasses.single} ${
                    tripMode === "single"
                      ? modeButtonActiveClasses.single
                      : "bg-white text-gray-700 hover:-translate-y-0.5 hover:bg-blue-50"
                  }`}
                >
                  🗺️ {plannerText.modeSingle}
                </button>
                <button
                  type="button"
                  onClick={() => handleTripModeSelect("multi")}
                  className={`relative overflow-hidden rounded-2xl border border-gray-200 px-5 py-2.5 pl-7 text-sm font-semibold transition-all before:absolute before:left-0 before:top-0 before:h-full before:w-1.5 ${modeButtonStripeClasses.multi} ${
                    tripMode === "multi"
                      ? modeButtonActiveClasses.multi
                      : "bg-white text-gray-700 hover:-translate-y-0.5 hover:bg-green-50"
                  }`}
                >
                  🌍 {plannerText.modeMulti}
                </button>
                <button
                  type="button"
                  onClick={() => handleTripModeSelect("road")}
                  className={`relative overflow-hidden rounded-2xl border border-gray-200 px-5 py-2.5 pl-7 text-sm font-semibold transition-all before:absolute before:left-0 before:top-0 before:h-full before:w-1.5 ${modeButtonStripeClasses.road} ${
                    tripMode === "road"
                      ? modeButtonActiveClasses.road
                      : "bg-white text-gray-700 hover:-translate-y-0.5 hover:bg-red-50"
                  }`}
                >
                  🚗 {plannerText.modeRoad}
                </button>
                <button
                  type="button"
                  onClick={() => handleTripModeSelect("interrail")}
                  className={`relative overflow-hidden rounded-2xl border border-gray-200 px-5 py-2.5 pl-7 text-sm font-semibold transition-all before:absolute before:left-0 before:top-0 before:h-full before:w-1.5 ${modeButtonStripeClasses.interrail} ${
                    tripMode === "interrail"
                      ? modeButtonActiveClasses.interrail
                      : "bg-white text-gray-700 hover:-translate-y-0.5 hover:bg-emerald-50"
                  }`}
                >
                  🚆 {plannerText.modeInterrail}
                </button>
              </div>
            </div>
            {/* ── Single City Mode ── */}
            {tripMode === "single" && (
            <><div className="lg:grid lg:grid-cols-12 lg:gap-6 xl:gap-8">
              <div className="lg:col-span-12 lg:max-w-none">
                <div className="space-y-5">
                <div className="hidden lg:grid lg:grid-cols-12 lg:gap-3 rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
                  <div className="lg:col-span-3 rounded-xl border border-emerald-200 bg-emerald-50 p-3">
                    <div className="text-xs uppercase tracking-wide text-emerald-700 font-semibold">{plannerText.step1}</div>
                    <div className="text-sm font-semibold text-emerald-900 mt-1">{plannerText.setDestination}</div>
                    <div className="text-xs text-emerald-800 mt-1">Country, city, and number of days</div>
                  </div>
                  <div className="lg:col-span-3 rounded-xl border border-blue-200 bg-blue-50 p-3">
                    <div className="text-xs uppercase tracking-wide text-blue-700 font-semibold">{plannerText.step2}</div>
                    <div className="text-sm font-semibold text-blue-900 mt-1">{plannerText.chooseTripStyle}</div>
                    <div className="text-xs text-blue-800 mt-1">{plannerText.styleHint}</div>
                  </div>
                  <div className="lg:col-span-3 rounded-xl border border-amber-200 bg-amber-50 p-3">
                    <div className="text-xs uppercase tracking-wide text-amber-700 font-semibold">{plannerText.step3}</div>
                    <div className="text-sm font-semibold text-amber-900 mt-1">{plannerText.generate}</div>
                    <div className="text-xs text-amber-800 mt-1">{plannerText.generateHint}</div>
                  </div>
                  <div className="lg:col-span-3 rounded-xl border border-indigo-200 bg-indigo-50 p-3">
                    <div className="text-xs uppercase tracking-wide text-indigo-700 font-semibold">{plannerText.selectedNow}</div>
                    <div className="text-xs text-indigo-900 mt-1"><strong>{plannerText.destination}:</strong> {destinationData.city || destinationData.country || plannerText.notSelected}</div>
                    <div className="text-xs text-indigo-900 mt-1"><strong>{plannerText.budget}:</strong> {selectedBudget ? displayBudgetLabel(selectedBudget) : plannerText.notSelected}</div>
                    <div className="text-xs text-indigo-900 mt-1"><strong>{plannerText.interests}:</strong> {selectedInterests.length > 0 ? selectedInterests.map(displayInterestLabel).join(", ") : plannerText.notSelected}</div>
                  </div>
                </div>
                <div className="lg:bg-white lg:rounded-[24px] lg:shadow-xl lg:p-6 xl:p-7">
                  <div className="space-y-5 lg:space-y-0 lg:grid lg:grid-cols-12 lg:gap-4 xl:gap-5">
                  <div className="lg:col-span-12">
                    <DestinationInputs onDataChange={setDestinationData} initialData={destinationData} language={language} />
                  </div>

                  {/* Pace */}
                  <div className="bg-white shadow-md rounded-2xl p-4 sm:p-5 mb-5 lg:mb-0 border-l-4 border-yellow-500 lg:col-span-4 lg:order-1">
                    <h2 className="text-base sm:text-lg font-bold text-gray-800 mb-2">{plannerText.paceQuestion}</h2>
                    <input
                      type="range"
                      min="0"
                      max="2"
                      value={pace}
                      onChange={(e) => setPace(Number(e.target.value))}
                      className="w-full"
                    />
                    <p className="text-gray-600 mt-2 text-sm sm:text-base">
                      {pace === 0 ? `😌 ${plannerText.paceRelaxed}` : pace === 1 ? `😊 ${plannerText.paceBalanced}` : `⚡ ${plannerText.paceAction}`}
                    </p>
                    <div className="mt-3 grid grid-cols-1 sm:grid-cols-3 gap-2">
                      <button
                        onClick={() => setPace(0)}
                        className={`w-full flex items-center justify-center gap-2 px-3 sm:px-4 py-2 rounded-xl border font-semibold text-sm sm:text-base ${
                          pace === 0 ? "bg-green-500 text-white border-green-500" : "bg-gray-100 text-gray-800 border-gray-300"
                        }`}
                        aria-pressed={pace === 0}
                      >
                        <span>😌</span> {plannerText.paceRelaxed}
                      </button>
                      <button
                        onClick={() => setPace(1)}
                        className={`w-full flex items-center justify-center gap-2 px-3 sm:px-4 py-2 rounded-xl border font-semibold text-sm sm:text-base ${
                          pace === 1 ? "bg-blue-500 text-white border-blue-500" : "bg-gray-100 text-gray-800 border-gray-300"
                        }`}
                        aria-pressed={pace === 1}
                      >
                        <span>😊</span> {plannerText.paceBalanced}
                      </button>
                      <button
                        onClick={() => setPace(2)}
                        className={`w-full flex items-center justify-center gap-2 px-3 sm:px-4 py-2 rounded-xl border font-semibold text-sm sm:text-base ${
                          pace === 2 ? "bg-amber-500 text-white border-amber-500" : "bg-gray-100 text-gray-800 border-gray-300"
                        }`}
                        aria-pressed={pace === 2}
                      >
                        <span>⚡</span> {plannerText.paceAction}
                      </button>
                    </div>
                  </div>

                  {/* Budget */}
                  <div className="bg-white shadow-md rounded-2xl p-4 sm:p-5 mb-5 lg:mb-0 border-l-4 border-purple-500 lg:col-span-4 lg:order-2">
                    <h2 className="text-base sm:text-lg font-bold text-gray-800 mb-2">{plannerText.budget}</h2>

                    <div className="grid grid-cols-3 gap-2 sm:gap-3">
                      <button
                        onClick={() => setSelectedBudget("Budget")}
                        className={`w-full text-sm sm:text-base flex items-center justify-center gap-2 px-3 py-3 rounded-xl border font-semibold ${
                          selectedBudget === "Budget" ? "bg-green-500 text-white border-green-500" : "bg-gray-100 text-gray-800 border-gray-300"
                        }`}
                      >
                        <span className="text-green-600">💶</span> {plannerText.budgetOptionBudget}
                      </button>

                      <button
                        onClick={() => setSelectedBudget("Mid-range")}
                        className={`w-full text-sm sm:text-base flex items-center justify-center gap-2 px-3 py-3 rounded-xl border font-semibold ${
                          selectedBudget === "Mid-range" ? "bg-blue-500 text-white border-blue-500" : "bg-gray-100 text-gray-800 border-gray-300"
                        }`}
                      >
                        <span className="text-blue-600">💵</span> {plannerText.budgetOptionMidRange}
                      </button>

                      <button
                        onClick={() => setSelectedBudget("Luxury")}
                        className={`w-full text-sm sm:text-base flex items-center justify-center gap-2 px-3 py-3 rounded-xl border font-semibold ${
                          selectedBudget === "Luxury" ? "bg-yellow-500 text-white border-yellow-500" : "bg-gray-100 text-gray-800 border-gray-300"
                        }`}
                      >
                        <span className="text-yellow-500">💎</span> {plannerText.budgetOptionLuxury}
                      </button>
                    </div>
                  </div>

                  {/* Interests */}
                  <div className="bg-white shadow-md rounded-2xl p-4 sm:p-5 mb-5 lg:mb-0 border-l-4 border-pink-500 lg:col-span-8 lg:col-start-3 lg:order-4">
                    <h2 className="text-base sm:text-lg font-bold text-gray-800 mb-2">{plannerText.interests}</h2>
                    <p className="text-xs text-gray-500 mb-2">{plannerText.selectInterestsHint}</p>
                    <div className="flex flex-wrap gap-2">
                      {[
                        { key: "Culture", label: plannerText.interestCulture, icon: "🏛️" },
                        { key: "Nature", label: plannerText.interestNature, icon: "🌿" },
                        { key: "Food", label: plannerText.interestFood, icon: "🍽️" },
                        { key: "Nightlife", label: plannerText.interestNightlife, icon: "🌃" },
                        { key: "Shopping", label: plannerText.interestShopping, icon: "🛍️" },
                        { key: "Beaches", label: plannerText.interestBeaches, icon: "🏖️" },
                        { key: "Museums", label: plannerText.interestMuseums, icon: "🖼️" },
                        { key: "History", label: plannerText.interestHistory, icon: "📜" },
                        { key: "Hiking", label: plannerText.interestHiking, icon: "🥾" },
                        { key: "Adventure", label: plannerText.interestAdventure, icon: "🧗" },
                        { key: "Family", label: plannerText.interestFamily, icon: "👨‍👩‍👧‍👦" },
                        { key: "Romance", label: plannerText.interestRomance, icon: "💕" },
                        { key: "Photography", label: plannerText.interestPhotography, icon: "📸" },
                        { key: "Wildlife", label: plannerText.interestWildlife, icon: "🐘" },
                        { key: "Wellness", label: plannerText.interestWellness, icon: "🧖" },
                        { key: "Festivals", label: plannerText.interestFestivals, icon: "🎪" },
                        { key: "Sports", label: plannerText.interestSports, icon: "⚽" },
                        { key: "Wine", label: plannerText.interestWine, icon: "🍷" },
                        { key: "Road Trips", label: plannerText.interestRoadTrips, icon: "🚗" },
                        { key: "Winter Sports", label: plannerText.interestWinterSports, icon: "🎿" },
                      ].map((opt) => (
                        <button
                          key={opt.key}
                          onClick={() => toggleInterest(opt.key)}
                          className={`px-2 sm:px-3 py-1.5 sm:py-2 rounded-xl border text-xs sm:text-sm ${
                            selectedInterests.includes(opt.key)
                              ? "bg-green-500 text-white border-green-500"
                              : "bg-gray-100 text-gray-800 border-gray-300"
                          }`}
                          title={opt.label}
                        >
                          <span className="mr-1">{opt.icon}</span>
                          {opt.label}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Generate Button */}
                  <div className="relative lg:col-span-8 lg:col-start-3 lg:order-5">
                    {showCtaPopup && (
                      <div
                        className="absolute inset-x-0 -top-24 z-10 bg-indigo-600 p-4 rounded-xl shadow-2xl text-white text-center border-b-4 border-indigo-700 animate-fadeIn transition duration-300"
                        role="status"
                      >
                        <h3 className="font-extrabold text-base sm:text-lg mb-1">{plannerText.itineraryProgressTitle} ⏳</h3>
                        <p className="text-xs sm:text-sm">{plannerText.itineraryProgressBody}</p>
                        <button
                          onClick={() => setShowCtaPopup(false)}
                          className="absolute top-1 right-2 text-white/70 hover:text-white text-xl leading-none"
                          aria-label="Close message"
                        >
                          &times;
                        </button>
                      </div>
                    )}
                    <button
                      onClick={() => handleGenerate()}
                      disabled={loading || !destinationData.country || !destinationData.duration}
                      aria-busy={loading}
                      className="w-full bg-green-600 hover:bg-green-700 text-white py-3 rounded-2xl font-semibold shadow-md transition disabled:opacity-60 disabled:cursor-not-allowed text-sm sm:text-base"
                    >
                      <span className="inline-flex items-center justify-center gap-2">
                        {loading ? <Loader className="w-5 h-5 animate-spin" /> : <Send className="w-5 h-5 transform rotate-45" />}
                        <span>{loading ? plannerText.generating : plannerText.generateItinerary}</span>
                      </span>
                    </button>
                  </div>

                  {/* Extra Notes */}
                  <div className="bg-white shadow-md rounded-2xl p-4 sm:p-5 mb-5 lg:mb-0 border-l-4 border-gray-500 lg:col-span-4 lg:order-3">
                    <h2 className="text-base sm:text-lg font-bold text-gray-800 mb-2">{plannerText.extraNotes}</h2>
                    <textarea
                      value={input}
                      onChange={(e) => setInput(e.target.value)}
                      placeholder={plannerText.extraNotesPlaceholder}
                      title="Enter Country - City/Town - Activity"
                      className="w-full border border-gray-300 rounded-xl p-3 text-sm sm:text-base"
                      rows={3}
                    />
                  </div>

                  {/* Mobile Booking Panel moved above mode toggle */}
                  {false && (<div ref={mobileBookingPanelRef} className="relative lg:hidden rounded-[24px] border border-sky-300 bg-[radial-gradient(circle_at_top_right,rgba(74,180,176,0.48),transparent_44%),radial-gradient(circle_at_bottom_right,rgba(136,199,255,0.52),transparent_40%),linear-gradient(145deg,#1f4d8c_0%,#1f569a_58%,#215fa8_100%)] p-5 shadow-[0_16px_34px_rgba(15,23,42,0.22)] mb-5">
                    <div className="mt-1 flex justify-center">
                      <span className="inline-flex items-center justify-center overflow-hidden rounded-xl bg-white px-[0.875rem] pt-1.5 pb-1 shadow-lg">
                        <img
                          src="/appihotels-logo-smile.png"
                          alt="AppiHotels"
                          className="h-14 w-auto -translate-y-1 sm:h-16"
                        />
                      </span>
                    </div>
                    <p className="mx-auto mt-2 max-w-xl text-center text-sm text-sky-100/95 sm:text-base">
                      Compare hotels worldwide, see the all-in price up front, and confirm instantly.
                    </p>
                    <p className="mx-auto mt-2 max-w-xl rounded-xl border border-white/30 bg-white/10 px-3 py-2 text-center text-xs font-semibold text-white/95">
                      AppiHotels is temporarily locked while we are testing.
                    </p>

                    <div className="mt-3 space-y-2 text-xs">
                      <div className="rounded-xl border border-slate-200 bg-white px-3 py-2">
                        <label className="block font-semibold text-slate-600 mb-1">Where</label>
                        <input
                          type="text"
                          value={singleCityBookingCity}
                          onChange={(e) => setBookingPanelCity(e.target.value)}
                          placeholder="City, airport, hotel, or landmark"
                          disabled={isBookingFormLocked}
                          className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-slate-900 focus:border-sky-400 focus:ring-2 focus:ring-sky-100 disabled:cursor-not-allowed disabled:bg-slate-100 disabled:text-slate-500"
                        />
                      </div>
                      <div className="rounded-xl border border-slate-200 bg-white px-3 py-2">
                        <label className="block font-semibold text-slate-600 mb-1">Dates</label>
                        <button
                          type="button"
                          onClick={openMobileDatesPopover}
                          disabled={isBookingFormLocked}
                          className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-left text-sm font-medium text-slate-900 hover:bg-sky-50 disabled:cursor-not-allowed disabled:bg-slate-100 disabled:text-slate-500"
                        >
                          {bookingDateSummary}
                        </button>
                      </div>
                      <div className="rounded-xl border border-slate-200 bg-white px-3 py-2">
                        <label className="block font-semibold text-slate-600 mb-1">Guests</label>
                        <button
                          type="button"
                          onClick={() => setDesktopBookingPopover((prev) => (prev === "mobile-guests" ? "" : "mobile-guests"))}
                          disabled={isBookingFormLocked}
                          className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-left text-sm font-medium text-slate-900 hover:bg-sky-50 disabled:cursor-not-allowed disabled:bg-slate-100 disabled:text-slate-500"
                        >
                          {bookingGuestSummary}
                        </button>
                      </div>
                    </div>

                    {!isBookingFormLocked && desktopBookingPopover === "mobile-dates" && (
                      <div className="absolute left-4 right-4 top-[260px] z-[80] rounded-2xl border border-slate-200 bg-white p-3 shadow-2xl">
                        <div className="mb-2 flex items-center justify-between">
                          <button
                            type="button"
                            onClick={() => setDesktopCalendarMonth((prev) => shiftCalendarMonth(prev, -1))}
                            className="rounded-lg border border-slate-200 px-2.5 py-1 text-xs font-semibold text-slate-700 hover:bg-slate-50"
                          >
                            Prev
                          </button>
                          <button
                            type="button"
                            onClick={() => setDesktopCalendarMonth((prev) => shiftCalendarMonth(prev, 1))}
                            className="rounded-lg border border-slate-200 px-2.5 py-1 text-xs font-semibold text-slate-700 hover:bg-slate-50"
                          >
                            Next
                          </button>
                        </div>

                        <div className="grid grid-cols-1 gap-3 max-h-[55vh] overflow-auto">
                          {desktopCalendarPanels.map((panel) => (
                            <div key={`mobile-${panel.title}`}>
                              <div className="mb-1.5 text-xs font-semibold text-slate-800">{panel.title}</div>
                              <div className="grid grid-cols-7 gap-0.5 text-center text-[10px] text-slate-500">
                                {CALENDAR_WEEKDAY_LABELS.map((dayLabel) => (
                                  <div key={`${panel.title}-mobile-${dayLabel}`} className="py-0.5">{dayLabel}</div>
                                ))}
                              </div>
                              <div className="grid grid-cols-7 gap-0.5 text-center">
                                {panel.cells.map((cell) => {
                                  const checkIn = bookingPanelStartDate || singleCityBookingDates.checkInDate;
                                  const checkOut = bookingPanelCheckOutDate;
                                  const isSelectedStart = Boolean(checkIn) && cell.iso === checkIn;
                                  const isSelectedEnd = Boolean(checkOut) && cell.iso === checkOut;
                                  const inRange = Boolean(checkIn && checkOut) && cell.iso > checkIn && cell.iso < checkOut;

                                  return (
                                    <button
                                      key={`${panel.title}-mobile-${cell.iso}`}
                                      type="button"
                                      onClick={() => selectDesktopBookingDate(cell.iso)}
                                      className={`rounded-md py-1 text-xs transition ${
                                        isSelectedStart || isSelectedEnd
                                          ? "bg-sky-600 text-white"
                                          : inRange
                                          ? "bg-sky-100 text-sky-900"
                                          : cell.inMonth
                                          ? "text-slate-800 hover:bg-slate-100"
                                          : "text-slate-300 hover:bg-slate-50"
                                      }`}
                                    >
                                      {cell.day}
                                    </button>
                                  );
                                })}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {!isBookingFormLocked && desktopBookingPopover === "mobile-guests" && (
                      <div className="absolute left-4 right-4 top-[260px] z-[80] rounded-2xl border border-slate-200 bg-white p-4 shadow-2xl">
                        <div className="space-y-3">
                          <div className="flex items-center justify-between">
                            <div>
                              <div className="text-sm font-semibold text-slate-900">Rooms</div>
                              <div className="text-xs text-slate-500">1 or more</div>
                            </div>
                            <div className="flex items-center gap-2">
                              <button type="button" onClick={() => adjustGuestCount("rooms", -1, 1, 8)} className="h-8 w-8 rounded-full border border-slate-300 text-slate-700">-</button>
                              <span className="w-5 text-center text-sm font-semibold text-slate-900">{bookingGuests.rooms}</span>
                              <button type="button" onClick={() => adjustGuestCount("rooms", 1, 1, 8)} className="h-8 w-8 rounded-full border border-slate-300 text-slate-700">+</button>
                            </div>
                          </div>

                          <div className="flex items-center justify-between">
                            <div>
                              <div className="text-sm font-semibold text-slate-900">Adults</div>
                              <div className="text-xs text-slate-500">Age 18+</div>
                            </div>
                            <div className="flex items-center gap-2">
                              <button type="button" onClick={() => adjustGuestCount("adults", -1, 1, 16)} className="h-8 w-8 rounded-full border border-slate-300 text-slate-700">-</button>
                              <span className="w-5 text-center text-sm font-semibold text-slate-900">{bookingGuests.adults}</span>
                              <button type="button" onClick={() => adjustGuestCount("adults", 1, 1, 16)} className="h-8 w-8 rounded-full border border-slate-300 text-slate-700">+</button>
                            </div>
                          </div>

                          <div className="flex items-center justify-between">
                            <div>
                              <div className="text-sm font-semibold text-slate-900">Children</div>
                              <div className="text-xs text-slate-500">Age 0-17</div>
                            </div>
                            <div className="flex items-center gap-2">
                              <button type="button" onClick={() => adjustGuestCount("children", -1, 0, 10)} className="h-8 w-8 rounded-full border border-slate-300 text-slate-700">-</button>
                              <span className="w-5 text-center text-sm font-semibold text-slate-900">{bookingGuests.children}</span>
                              <button type="button" onClick={() => adjustGuestCount("children", 1, 0, 10)} className="h-8 w-8 rounded-full border border-slate-300 text-slate-700">+</button>
                            </div>
                          </div>
                        </div>

                        <button
                          type="button"
                          onClick={() => setDesktopBookingPopover("")}
                          className="mt-4 w-full rounded-lg bg-sky-600 px-3 py-2 text-sm font-semibold text-white hover:bg-sky-500"
                        >
                          Done
                        </button>
                      </div>
                    )}

                    <a
                      href={canOpenSingleCityBooking ? singleCityBookingLink : "#"}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={(e) => {
                        if (!canOpenSingleCityBooking) {
                          e.preventDefault();
                          return;
                        }
                        trackAnalytics("booking_click", { city: singleCityBookingCity || "Single City" });
                      }}
                      className={`mt-3 inline-flex w-full items-center justify-center rounded-lg px-3 py-2 text-sm font-semibold ${
                        canOpenSingleCityBooking
                          ? "bg-gradient-to-r from-sky-600 to-blue-600 text-white hover:from-sky-500 hover:to-blue-500"
                          : "bg-slate-200 text-slate-500 cursor-not-allowed"
                      }`}
                    >
                      <span className="inline-flex items-center gap-2">
                        <Search className="h-4 w-4" />
                        <span>{isBookingFormLocked ? "Temporarily locked" : "Search"}</span>
                      </span>
                    </a>
                  </div>)}

                </div>
                </div>
              </div>

              {/* Itinerary */}
              <div className="mt-8 lg:mt-0 lg:col-span-12">
                {itineraryJson && (
                  <div ref={itineraryRef} className="bg-transparent mt-5 lg:mt-0">
                    {/* Summary */}
                    {(() => {
                      const meta = itineraryJson?.meta || {};
                      const days = meta.durationDays || itineraryJson?.days?.length || 0;
                      const budgetLabel = meta.budget || selectedBudget || "Mid-range";
                      const selectedSet = new Set(selectedInterestsRef.current || []);
                      const interests = Array.isArray(meta.interests)
                        ? meta.interests.filter((tag) => selectedSet.size === 0 || selectedSet.has(tag)).slice(0, 4)
                        : [];
                      const countActivities = Array.isArray(itineraryJson?.days)
                        ? itineraryJson.days.reduce((sum, d) => sum + (Array.isArray(d.activities) ? d.activities.length : 0), 0)
                        : 0;

                      const formatCurrency = (n, currency = "EUR", locale = "en-IE") =>
                        new Intl.NumberFormat(locale, { style: "currency", currency }).format(n);

                      const estCost = (days || 1) * estimateNightly(budgetLabel);

                      return (
                        <div className="bg-white shadow-md rounded-2xl p-4 sm:p-5 mb-5 border-l-4 border-indigo-500">
                          <div className="flex flex-col lg:flex-row items-start justify-between gap-3">
                            <div className="flex-1">
                              <h2 className="text-lg sm:text-xl font-extrabold text-gray-900">
                                {meta.destination ? `Trip to ${meta.destination}` : "Your Trip Summary"}
                              </h2>
                              <p className="text-gray-600 text-xs sm:text-sm">
                                {meta.country ? `${meta.country} · ` : ""}
                                {days} day{days === 1 ? "" : "s"} · {budgetLabel}
                              </p>

                              {!!interests.length && (
                                <div className="mt-2 flex flex-wrap gap-1">
                                  {interests.map((tag) => (
                                    <span
                                      key={tag}
                                      className="text-xs px-2 py-1 rounded-full bg-emerald-50 text-emerald-800 border border-emerald-200"
                                    >
                                      {tag}
                                    </span>
                                  ))}
                                </div>
                              )}
                            </div>

                            <div className="grid grid-cols-3 gap-2 shrink-0 w-full lg:w-auto">
                              <div className="flex items-center gap-2 px-2 sm:px-3 py-2 rounded-xl bg-emerald-50 border border-emerald-200">
                                <span className="text-emerald-600 text-lg sm:text-xl">📆</span>
                                <div className="leading-tight">
                                  <div className="text-xs text-emerald-700">Days</div>
                                  <div className="font-bold text-sm sm:text-base text-emerald-900">{days}</div>
                                </div>
                              </div>
                              <div className="flex items-center gap-2 px-2 sm:px-3 py-2 rounded-xl bg-indigo-50 border border-indigo-200">
                                <span className="text-indigo-600 text-lg sm:text-xl">✅</span>
                                <div className="leading-tight">
                                  <div className="text-xs text-indigo-700">Activities</div>
                                  <div className="font-bold text-sm sm:text-base text-indigo-900">{countActivities}</div>
                                </div>
                              </div>
                              <div className="flex items-center gap-2 px-2 sm:px-3 py-2 rounded-xl bg-amber-50 border border-amber-200">
                                <span className="text-amber-600 text-lg sm:text-xl">💰</span>
                                <div className="leading-tight">
                                  <div className="text-xs text-amber-700">Est. cost</div>
                                  <div className="font-bold text-sm sm:text-base text-amber-900">{formatCurrency(estCost)}</div>
                                </div>
                              </div>
                            </div>
                          </div>

                          <div className="mt-4 flex flex-wrap gap-2">
                            <button
                              onClick={saveItinerary}
                              className="px-3 py-2 rounded-xl border border-emerald-600 bg-emerald-600 hover:bg-emerald-700 text-white text-xs sm:text-sm disabled:opacity-60"
                              disabled={loading || !itineraryJson}
                              type="button"
                            >
                              ✅ Save Itinerary
                            </button>

                            <button
                              onClick={printPdf}
                              className="px-3 py-2 rounded-xl border border-indigo-600 bg-indigo-50 hover:bg-indigo-100 text-indigo-700 text-xs sm:text-sm"
                              disabled={loading || !itineraryJson}
                              type="button"
                            >
                              🖨️ Print / PDF
                            </button>

                            <button
                              onClick={() => handleShareClick("whatsapp")}
                              className="px-3 py-2 rounded-xl border border-green-600 bg-green-50 hover:bg-green-100 text-green-700 text-xs sm:text-sm"
                              disabled={loading || !itineraryJson}
                              type="button"
                            >
                              Share WhatsApp
                            </button>

                            <button
                              onClick={() => handleShareClick("facebook")}
                              className="px-3 py-2 rounded-xl border border-blue-600 bg-blue-50 hover:bg-blue-100 text-blue-700 text-xs sm:text-sm"
                              disabled={loading || !itineraryJson}
                              type="button"
                            >
                              Share Facebook
                            </button>

                            <button
                              onClick={() => handleShareClick("linkedin")}
                              className="px-3 py-2 rounded-xl border border-sky-600 bg-sky-50 hover:bg-sky-100 text-sky-700 text-xs sm:text-sm"
                              disabled={loading || !itineraryJson}
                              type="button"
                            >
                              Share LinkedIn
                            </button>

                            <button
                              onClick={() => handleShareClick("instagram")}
                              className="px-3 py-2 rounded-xl border border-rose-600 bg-rose-50 hover:bg-rose-100 text-rose-700 text-xs sm:text-sm"
                              disabled={loading || !itineraryJson}
                              type="button"
                            >
                              Instagram (copy link)
                            </button>
                          </div>
                        </div>
                      );
                    })()}

                    {/* Day 0 Hotels */}
                    {(() => {
                      const acc0 = ACCENTS[0];
                      const meta = itineraryJson?.meta || {};
                      const budgetLabel = meta.budget || selectedBudget || "Mid-range";
                      const destination = meta.destination || destinationData.city || "your destination";
                      const stayDates = buildSingleCityStayDates(destinationData.startDate, meta.durationDays || itineraryJson?.days?.length || destinationData.duration || 1);

                      const aiHotels = Array.isArray(itineraryJson?.days?.[0]?.hotelSuggestions)
                        ? itineraryJson.days[0].hotelSuggestions.filter(Boolean)
                        : [];

                      const pool = selectStayPool(destination);
                      const curatedList = pool[budgetLabel] || pool["Mid-range"] || [];
                      const hotelCards = buildSingleCityHotelOptions({
                        aiHotelLines: aiHotels,
                        curatedStays: curatedList,
                        seed: `${singleCityHotelSeed}|${destination}|${budgetLabel}|${stayDates.checkInDate}|${stayDates.checkOutDate}`,
                        limit: 5,
                        includeAi: true,
                      });
                      const hasAiCards = hotelCards.some((card) => card.kind === "ai");
                      const nightly = estimateNightly(budgetLabel);

                      const renderAiCard = (line, i) => {
                        const cleanedLine = sanitizeHotelSuggestionLine(line);
                        const urlMatch = (line.match(/https?:\/\/\S+/) || [])[0];
                        const withoutUrl = urlMatch ? cleanedLine.replace(urlMatch, "").trim() : cleanedLine.trim();
                        const parts = withoutUrl.split(/—| - /).map((s) => s.trim()).filter(Boolean);
                        const name = parts[0] || "Hotel";
                        const desc = parts.slice(1).join(" — ") || "Recommended stay";
                        const parsedMapLink = extractMapLinkFromSuggestion(line);
                        const mapLink = parsedMapLink && !/booking\.com/i.test(parsedMapLink) ? parsedMapLink : "";
                        const bookingLink = sanitizeAccommodationLink(
                          /booking\.com/i.test(urlMatch || "") ? urlMatch : "",
                          destination,
                          destinationData.country || meta.country || "",
                          name,
                          stayDates.checkInDate,
                          stayDates.checkOutDate,
                          budgetLabel
                        );
                        return (
                          <div key={`aih${i}`} className={`bg-white rounded-xl p-4 border-l-4 ${acc0.stripe} shadow-sm`}>
                            <div className="flex items-start justify-between gap-2">
                              <div>
                                <div className="font-semibold text-gray-900">{name}</div>
                                {desc && <div className="text-sm text-gray-700 mt-0.5">{desc}</div>}
                              </div>
                              {mapLink && (
                                <a
                                  href={mapLink}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className={`text-sm underline ${acc0.link} pointer-events-auto relative z-10`}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                  }}
                                >
                                  Map ↗
                                </a>
                              )}
                            </div>
                            {HOTEL_LINKS_ENABLED && bookingLink && (
                              <a href={bookingLink} target="_blank" rel="noopener noreferrer" className={`mt-2 inline-block text-sm underline ${acc0.link}`} onClick={() => trackAnalytics("booking_click", { city: destination })}>
                                Hotels on Appi ↗
                              </a>
                            )}
                            {!HOTEL_LINKS_ENABLED && (
                              <span className="mt-2 inline-block text-xs text-amber-700 font-medium">
                                {HOTEL_LINKS_DISABLED_LABEL}
                              </span>
                            )}
                          </div>
                        );
                      };

                      const renderCuratedCard = (h, i) => {
                        const bookingLink = sanitizeAccommodationLink(
                          "",
                          destination,
                          destinationData.country || meta.country || "",
                          h.name,
                          stayDates.checkInDate,
                          stayDates.checkOutDate,
                          budgetLabel
                        );
                        return (
                        <div key={`cur${i}`} className={`bg-white rounded-xl p-4 border-l-4 ${acc0.stripe} shadow-sm`}>
                          <div className="font-semibold text-gray-900">{h.name}</div>
                          <div className="text-sm text-gray-700 mt-0.5">{h.desc}</div>
                          {HOTEL_LINKS_ENABLED && bookingLink && (
                            <a href={bookingLink} target="_blank" rel="noopener noreferrer" className={`mt-2 inline-block text-sm underline ${acc0.link}`} onClick={() => trackAnalytics("booking_click", { city: destination })}>
                              Hotels on Appi ↗
                            </a>
                          )}
                          {!HOTEL_LINKS_ENABLED && (
                            <span className="mt-2 inline-block text-xs text-amber-700 font-medium">
                              {HOTEL_LINKS_DISABLED_LABEL}
                            </span>
                          )}
                        </div>
                        );
                      };

                      return (
                        <div className={`bg-white shadow-md rounded-2xl p-5 mb-5 border-l-4 ${acc0.stripe}`}>
                          <div className="flex items-center justify-between mb-2">
                            <h3 className="font-semibold text-gray-900">
                              <span className="inline-flex items-center justify-center w-8 h-8 mr-2 rounded-full bg-gray-100 text-gray-800 font-bold">
                                0
                              </span>
                              {hasAiCards ? "Suggested Hotels" : `${budgetLabel} ${destination} Stays`}
                            </h3>
                            <div className="text-sm text-gray-600 flex items-center gap-3">
                              <span>🏨 Accommodation</span>
                              <span>{stayDates.checkInDate && stayDates.checkOutDate ? `🕒 ${stayDates.checkInDate} to ${stayDates.checkOutDate}` : "🕒 Check-in"}</span>
                              <span>💶 {nightly}/night (est.)</span>
                            </div>
                          </div>

                          {stayDates.checkInDate && stayDates.checkOutDate && (
                            <p className="text-xs text-gray-600 mb-3">Stay dates: {stayDates.checkInDate} to {stayDates.checkOutDate} ({stayDates.nights} night{stayDates.nights === 1 ? "" : "s"})</p>
                          )}

                          <p className="text-gray-700 mb-3">
                            {hasAiCards
                              ? `Top picks for ${destination} (AI-curated and budget-aware):`
                              : `5 ${budgetLabel.toLowerCase()}-aligned options for ${destination}:`}
                          </p>

                          <div className="grid grid-cols-1 gap-3">
                            {hotelCards.map((card, index) =>
                              card.kind === "ai"
                                ? renderAiCard(card.line, index)
                                : renderCuratedCard(card.stay, index)
                            )}
                          </div>
                        </div>
                      );
                    })()}

                    {/* Airport Transport */}
                    {(() => {
                      const acc1 = ACCENTS[1];
                      const meta = itineraryJson?.meta || {};
                      const destination = meta.destination || destinationData.city || "your destination";
                      const t = normalizeArrivalTransport(meta.arrivalTransport, destination);

                      return (
                        <div className={`bg-white shadow-md rounded-2xl p-5 mb-5 border-l-4 ${acc1.stripe}`}>
                          <div className="flex items-center justify-between mb-2">
                            <h3 className="font-semibold text-gray-900">
                              <span className="inline-flex items-center justify-center w-8 h-8 mr-2 rounded-full bg-gray-100 text-gray-800 font-bold">
                                i
                              </span>
                              {t.title || plannerText.airportToCity}
                            </h3>
                            <div className="text-sm text-gray-600">{t.arrivalLabel || plannerText.uponArrival}</div>
                          </div>

                          {Array.isArray(t.airports) && t.airports.length > 0 && (
                            <div className="text-sm text-gray-700 mb-2">
                              <strong>{plannerText.airportsLabel}:</strong> {t.airports.join(", ")}
                            </div>
                          )}

                          {Array.isArray(t.trains) && t.trains.length > 0 && (
                            <div className="mt-2">
                              <div className="font-semibold text-gray-800 mb-1">🚆 {plannerText.trainsLabel}</div>
                              <ul className="list-disc list-inside text-gray-800 text-sm">
                                {t.trains.map((r, i) => (
                                  <li key={`tr${i}`}>
                                    <strong>{r.name}</strong>
                                    {r.note ? ` — ${r.note}` : ""}
                                  </li>
                                ))}
                              </ul>
                            </div>
                          )}

                          {Array.isArray(t.metro) && t.metro.length > 0 && (
                            <div className="mt-2">
                              <div className="font-semibold text-gray-800 mb-1">🚇 {plannerText.metroLabel}</div>
                              <ul className="list-disc list-inside text-gray-800 text-sm">
                                {t.metro.map((m, i) => (
                                  <li key={`me${i}`}>
                                    <strong>{m.name}</strong>
                                    {m.note ? ` — ${m.note}` : ""}
                                  </li>
                                ))}
                              </ul>
                            </div>
                          )}

                          <div className="mt-2">
                            <div className="font-semibold text-gray-800 mb-1">🚌 {plannerText.busServicesLabel}</div>

                            {Array.isArray(t.buses?.express) && t.buses.express.length > 0 && (
                              <div className="ml-1 mb-2">
                                <div className="text-sm text-gray-700 font-semibold">{plannerText.expressServicesLabel}</div>
                                <ul className="list-disc list-inside text-gray-800 text-sm">
                                  {t.buses.express.map((b, i) => (
                                    <li key={`be${i}`}>
                                      <strong>{b.name}</strong>
                                      {b.note ? ` — ${b.note}` : ""}
                                    </li>
                                  ))}
                                </ul>
                              </div>
                            )}

                            {Array.isArray(t.buses?.routes) && t.buses.routes.length > 0 && (
                              <div className="ml-1 mb-2">
                                <div className="text-sm text-gray-700 font-semibold">{plannerText.cityRoutesLabel}</div>
                                <ul className="list-disc list-inside text-gray-800 text-sm">
                                  {t.buses.routes.map((b, i) => (
                                    <li key={`br${i}`}>
                                      <strong>{b.name}</strong>
                                      {b.note ? ` — ${b.note}` : ""}
                                    </li>
                                  ))}
                                </ul>
                              </div>
                            )}
                          </div>

                          {(t.taxis?.ranks || t.taxis?.cost || (Array.isArray(t.taxis?.prebook) && t.taxis.prebook.length > 0)) && (
                            <div className="mt-3">
                              <div className="font-semibold text-gray-800 mb-1">🚕 {plannerText.taxisLabel}</div>
                              <ul className="list-disc list-inside text-gray-800 text-sm">
                                {t.taxis?.ranks && (
                                  <li>
                                    <strong>{plannerText.taxiRanksLabel}:</strong> {t.taxis.ranks}
                                  </li>
                                )}
                                {t.taxis?.cost && (
                                  <li>
                                    <strong>{plannerText.costLabel}:</strong> {t.taxis.cost}
                                  </li>
                                )}
                                {Array.isArray(t.taxis?.prebook) && t.taxis.prebook.length > 0 && (
                                  <li>
                                    <strong>{plannerText.prebookLabel}:</strong> {t.taxis.prebook.join(", ")}
                                  </li>
                                )}
                              </ul>
                            </div>
                          )}

                          {Array.isArray(t.shuttles) && t.shuttles.length > 0 && (
                            <div className="mt-3">
                              <div className="font-semibold text-gray-800 mb-1">🚐 {plannerText.hotelShuttlesLabel}</div>
                              <ul className="list-disc list-inside text-gray-800 text-sm">
                                {t.shuttles.map((s, i) => (
                                  <li key={`sh${i}`}>
                                    <strong>{s.name}</strong>
                                    {s.note ? ` — ${s.note}` : ""}
                                  </li>
                                ))}
                              </ul>
                            </div>
                          )}

                          {(t.carRental?.location || (Array.isArray(t.carRental?.companies) && t.carRental.companies.length > 0)) && (
                            <div className="mt-3">
                              <div className="font-semibold text-gray-800 mb-1">🚗 {plannerText.carRentalLabel}</div>
                              <ul className="list-disc list-inside text-gray-800 text-sm">
                                {t.carRental?.location && (
                                  <li>
                                    <strong>{plannerText.locationLabel}:</strong> {t.carRental.location}
                                  </li>
                                )}
                                {Array.isArray(t.carRental?.companies) && t.carRental.companies.length > 0 && (
                                  <li>
                                    <strong>{plannerText.companiesLabel}:</strong> {t.carRental.companies.join(", ")}
                                  </li>
                                )}
                              </ul>
                            </div>
                          )}

                          {t.note && (
                            <div className="mt-4 p-3 rounded-xl bg-gray-50 border border-gray-200 text-gray-700 text-sm">
                              <span className="mr-1">⚠️</span>
                              {t.note}
                            </div>
                          )}
                        </div>
                      );
                    })()}

                    {/* Days */}
                    <div className="space-y-5 lg:space-y-4">
                      {itineraryJson.days.map((d, idx) => {
                        const acc = ACCENTS[(idx + 2) % 4];
                        return (
                          <div key={idx} className={`bg-white rounded-2xl p-4 sm:p-5 shadow-sm border-l-4 ${acc.stripe}`}>
                            <div className="flex items-center justify-between mb-2">
                              <h3 className="text-base sm:text-lg font-semibold text-gray-900">
                                <span className="inline-flex items-center justify-center w-7 h-7 sm:w-8 sm:h-8 mr-2 rounded-full bg-gray-100 text-gray-800 font-bold text-sm sm:text-base">
                                  {d.day}
                                </span>
                                {d.title}
                              </h3>
                            </div>

                            {/* Weather */}
                            {(() => {
                              const wRaw = d.weather ?? d.Weather ?? d.forecast ?? d.climate ?? null;
                              const w = typeof wRaw === "string" ? { summary: wRaw } : wRaw && typeof wRaw === "object" ? wRaw : {};

                              const icon = (() => {
                                const c = (w.condition || w.summary || "").toLowerCase();
                                if (c.includes("storm") || c.includes("thunder")) return "⛈️";
                                if (c.includes("rain") || c.includes("shower") || c.includes("drizzle")) return "🌧️";
                                if (c.includes("snow") || c.includes("sleet")) return "🌨️";
                                if (c.includes("cloud") || c.includes("overcast")) return "☁️";
                                if (c.includes("wind")) return "🌬️";
                                if (c.includes("sun") || c.includes("clear")) return "☀️";
                                if (c.includes("partly") || c.includes("mixed")) return "⛅";
                                return "⛅";
                              })();

                              const fmtTemp = (t) => {
                                if (!t) return "";
                                const s = String(t).trim();
                                return /°/.test(s) ? s : `${s}°`;
                              };

                              const advice = (() => {
                                const txt = `${w.summary || ""} ${w.condition || ""}`.toLowerCase();
                                if (txt.includes("rain") || txt.includes("shower") || txt.includes("storm")) return "Wet weather, indoor activities recommended";
                                if (txt.includes("snow")) return "Cold & slippery—layer up and allow extra time";
                                if (txt.includes("wind")) return "Windy—secure hats/umbrellas and use transit where possible";
                                if (txt.includes("hot") || txt.includes("heat") || /([3-9]\d)°?c/.test(`${w.high || ""}`)) return "Hot—carry water, sunscreen, and plan shade breaks";
                                if (txt.includes("cold") || /(\b-?\d{1,2})°?c/.test(`${w.low || ""}`)) return "Chilly—bring layers and consider warm indoor stops";
                                return "Plan flexibly and check live updates";
                              })();

                              return (
                                <div className="mb-3 rounded-xl border border-sky-200 bg-sky-50 p-3">
                                  <div className="flex items-start justify-between gap-3">
                                    <div className="flex items-start gap-3">
                                      <div className="text-2xl leading-none">{icon}</div>
                                      <div>
                                        <div className="font-semibold text-sky-900">{w.condition || w.summary || "Weather"}</div>
                                        <div className="text-xs text-sky-700">{advice}</div>
                                      </div>
                                    </div>
                                    <div className="text-right">
                                      <div className="text-lg font-bold text-sky-900">{fmtTemp(w.high) || "—"}</div>
                                      <div className="text-xs text-sky-700">Low: {fmtTemp(w.low) || "—"}</div>
                                    </div>
                                  </div>

                                  <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-xs text-sky-800">
                                    <span className="inline-flex items-center gap-1">💧 {w.humidity || "—"}</span>
                                    <span className="inline-flex items-center gap-1">🌬️ {w.wind || "—"}</span>
                                  </div>
                                </div>
                              );
                            })()}

                            {/* Activities */}
                            {Array.isArray(d.activities) && d.activities.length > 0 && (
                              <ol className={`relative z-0 border-s ${acc.line} pl-8 md:pl-10 space-y-6 mt-3`}>
                                {d.activities.map((a, i) => {
                                  const badge = categoryBadge(a.category);
                                  return (
                                    <li key={i} className="relative">
                                      <span
                                        className={`absolute -left-3 md:-left-3.5 top-2 w-3.5 h-3.5 rounded-full border-2 ${acc.dot} bg-white pointer-events-none z-0`}
                                      ></span>

                                      <div className="relative z-10 flex flex-col sm:flex-row sm:items-start sm:justify-between gap-2">
                                        <div>
                                          <div className="text-base font-bold text-gray-900">
                                            <span className="font-semibold">{a.time || ""}</span>
                                            {a.title ? ` | ${a.title}` : ""}
                                          </div>

                                          <div className="mt-1 flex flex-wrap items-center gap-3 text-xs text-gray-600">
                                            <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-gray-100 border border-gray-200">
                                              <span>{badge.icon}</span>
                                              <span className="capitalize">{badge.label}</span>
                                            </span>
                                            {a.cost && (
                                              <span className="inline-flex items-center gap-1">
                                                <span>💵</span>
                                                <span>{a.cost}</span>
                                              </span>
                                            )}
                                            {a.duration && (
                                              <span className="inline-flex items-center gap-1">
                                                <span>⏱️</span>
                                                <span>{a.duration}</span>
                                              </span>
                                            )}
                                          </div>

                                          {a.description && <p className="mt-2 text-sm text-gray-800">{a.description}</p>}
                                          {a.culturalStory && <p className="mt-1 text-sm text-gray-700 italic">{a.culturalStory}</p>}

                                          <div className="mt-2 grid gap-1 text-sm text-gray-700">
                                            {a.location && (
                                              <div>
                                                <strong>Location:</strong> {a.location}
                                              </div>
                                            )}
                                            {a.specialties && (
                                              <div>
                                                <strong>Specialties:</strong> {a.specialties}
                                              </div>
                                            )}
                                            {a.tip && (
                                              <div>
                                                <strong>Tip:</strong> {a.tip}
                                              </div>
                                            )}
                                            {a.theme && (
                                              <div>
                                                <strong>🎯 Theme:</strong> {a.theme}
                                              </div>
                                            )}
                                          </div>

                                          {(() => {
                                            const nearbyEats = normalizeNearbyEats(a);
                                            const hasNearbyEats =
                                              nearbyEats.breakfast.length > 0 || nearbyEats.lunch.length > 0 || nearbyEats.dinner.length > 0;

                                            if (!hasNearbyEats) return null;

                                            const mealSections = [
                                              { key: "breakfast", title: "Breakfast", icon: "🥐", items: nearbyEats.breakfast },
                                              { key: "lunch", title: "Lunch", icon: "🥗", items: nearbyEats.lunch },
                                              { key: "dinner", title: "Dinner", icon: "🍝", items: nearbyEats.dinner },
                                            ];

                                            return (
                                              <div className="mt-3 rounded-xl bg-lime-100 border border-lime-300 p-3">
                                                <div className="text-xs font-semibold text-lime-900 mb-2">Nearby Eats</div>

                                                <div className="space-y-3">
                                                  {mealSections.map((meal) =>
                                                    meal.items.length > 0 ? (
                                                      <div key={meal.key}>
                                                        <div className="text-xs font-semibold text-lime-900 mb-1">
                                                          {meal.icon} {meal.title}
                                                        </div>
                                                        <div className="flex gap-3 overflow-x-auto pb-1 snap-x snap-mandatory [scrollbar-width:thin]">
                                                          {meal.items.map((rObj, ri) => (
                                                            <article
                                                              key={`${meal.key}-${ri}`}
                                                              className="min-w-[240px] sm:min-w-[280px] snap-start rounded-lg border border-lime-300 bg-white/90 p-3 shadow-sm"
                                                            >
                                                              {(() => {
                                                                const destinationHint = [
                                                                  rObj.name || "",
                                                                  a.location || "",
                                                                  itineraryJson?.meta?.destination || destinationData.city || "",
                                                                  destinationData.country || itineraryJson?.meta?.country || "",
                                                                ]
                                                                  .filter(Boolean)
                                                                  .join(" ");
                                                                const fallbackMap = buildGooglePlaceLink(destinationHint);
                                                                const eatMapLink = resolveGooglePlaceLink(rObj.mapLink || fallbackMap, destinationHint);

                                                                return (
                                                                  <>
                                                                    <div className="text-sm font-semibold text-lime-900">{rObj.name || "Restaurant"}</div>
                                                                    <div className="mt-1 flex flex-wrap items-center gap-2 text-xs text-lime-800">
                                                                      {rObj.cuisine && <span>• {rObj.cuisine}</span>}
                                                                      {rObj.price && <span>• {rObj.price}</span>}
                                                                      {rObj.distance && <span>• {rObj.distance}</span>}
                                                                    </div>
                                                                    <a
                                                                      href={eatMapLink}
                                                                      target="_blank"
                                                                      rel="noopener noreferrer"
                                                                      className="mt-2 inline-flex items-center gap-1 text-xs font-semibold underline text-lime-900 hover:text-lime-950"
                                                                    >
                                                                      Open in Maps ↗
                                                                    </a>
                                                                  </>
                                                                );
                                                              })()}
                                                            </article>
                                                          ))}
                                                        </div>
                                                      </div>
                                                    ) : null
                                                  )}
                                                </div>
                                              </div>
                                            );
                                          })()}
                                        </div>

                                        {a.mapLink && (
                                          <a
                                            href={a.mapLink}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className={`text-sm inline-flex items-center gap-1 underline ${acc.link}`}
                                          >
                                            Map ↗
                                          </a>
                                        )}
                                      </div>
                                    </li>
                                  );
                                })}
                              </ol>
                            )}

                            {d.transportTips && (
                              <div className="mt-4 p-3 rounded-xl bg-gray-50 border border-gray-200 text-gray-700 text-sm">
                                <span className="mr-1">ℹ️</span>
                                {d.transportTips}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}


              </div>

              {/* Desktop Booking Rail moved above mode toggle */}
              {false && (<aside className="hidden lg:block lg:col-span-4">
                <div className="desktop-sidebar-sticky space-y-4" style={{ maxHeight: "none", overflow: "visible" }}>
                  <div id="booking-panel" ref={desktopBookingPanelRef} className="relative rounded-[24px] border border-sky-300 bg-[radial-gradient(circle_at_top_right,rgba(74,180,176,0.48),transparent_44%),radial-gradient(circle_at_bottom_right,rgba(136,199,255,0.52),transparent_40%),linear-gradient(145deg,#1f4d8c_0%,#1f569a_58%,#215fa8_100%)] p-5 shadow-[0_16px_34px_rgba(15,23,42,0.22)]">
                    <div className="mt-1 flex justify-center">
                      <span className="inline-flex items-center justify-center overflow-hidden rounded-xl bg-white px-[0.875rem] pt-1.5 pb-1 shadow-lg">
                        <img
                          src="/appihotels-logo-smile.png"
                          alt="AppiHotels"
                          className="h-14 w-auto -translate-y-1 sm:h-16"
                        />
                      </span>
                    </div>
                    <p className="mx-auto mt-2 max-w-xl text-center text-sm text-sky-100/95 sm:text-base">
                      Compare hotels worldwide, see the all-in price up front, and confirm instantly.
                    </p>
                    <p className="mx-auto mt-2 max-w-xl rounded-xl border border-white/30 bg-white/10 px-3 py-2 text-center text-xs font-semibold text-white/95">
                      AppiHotels is temporarily locked while we are testing.
                    </p>

                    <div className="mt-3 rounded-2xl border border-slate-200 bg-white shadow-[0_6px_14px_rgba(15,23,42,0.12)]">
                      <div className="grid grid-cols-12">
                        <div className="col-span-4 border-r border-slate-200 px-3 py-2">
                          <label className="block text-[11px] font-semibold text-slate-500">Where</label>
                          <input
                            type="text"
                            value={singleCityBookingCity}
                            onChange={(e) => setBookingPanelCity(e.target.value)}
                            placeholder="City, airport, hotel, or landmark"
                            disabled={isBookingFormLocked}
                            className="w-full border-0 p-0 text-base font-medium text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-0 disabled:cursor-not-allowed disabled:text-slate-500"
                          />
                        </div>
                        <button
                          type="button"
                          onClick={openDesktopDatesPopover}
                          disabled={isBookingFormLocked}
                          className="col-span-3 border-r border-slate-200 px-3 py-2 text-left hover:bg-sky-50 disabled:cursor-not-allowed disabled:bg-slate-100"
                        >
                          <span className="block text-[11px] font-semibold text-slate-500">Dates</span>
                          <span className="block text-base font-medium text-slate-900">{bookingDateSummary}</span>
                        </button>
                        <button
                          type="button"
                          onClick={() => setDesktopBookingPopover((prev) => (prev === "guests" ? "" : "guests"))}
                          disabled={isBookingFormLocked}
                          className="col-span-3 border-r border-slate-200 px-3 py-2 text-left hover:bg-sky-50 disabled:cursor-not-allowed disabled:bg-slate-100"
                        >
                          <span className="block text-[11px] font-semibold text-slate-500">Guests</span>
                          <span className="block text-base font-medium text-slate-900 truncate">{bookingGuestSummary}</span>
                        </button>
                        <a
                          href={canOpenSingleCityBooking ? singleCityBookingLink : "#"}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={(e) => {
                            if (!canOpenSingleCityBooking) {
                              e.preventDefault();
                              return;
                            }
                            trackAnalytics("booking_click", { city: singleCityBookingCity || "Single City" });
                          }}
                          className={`col-span-2 inline-flex items-center justify-center gap-2 px-3 py-2 text-sm font-semibold transition ${
                            canOpenSingleCityBooking
                              ? "text-sky-700 hover:bg-sky-50"
                              : "text-slate-400 cursor-not-allowed"
                          }`}
                        >
                          <Search className="h-4 w-4" />
                          <span>{isBookingFormLocked ? "Locked" : "Search"}</span>
                        </a>
                      </div>
                    </div>

                    {!isBookingFormLocked && desktopBookingPopover === "dates" && (
                      <div className="absolute right-5 top-[192px] z-[80] w-[560px] max-w-[calc(100vw-4rem)] rounded-2xl border border-slate-200 bg-white p-3 shadow-2xl">
                        <div className="mb-2 flex items-center justify-between">
                          <button
                            type="button"
                            onClick={() => setDesktopCalendarMonth((prev) => shiftCalendarMonth(prev, -1))}
                            className="rounded-lg border border-slate-200 px-2.5 py-1 text-xs font-semibold text-slate-700 hover:bg-slate-50"
                          >
                            Prev
                          </button>
                          <button
                            type="button"
                            onClick={() => setDesktopCalendarMonth((prev) => shiftCalendarMonth(prev, 1))}
                            className="rounded-lg border border-slate-200 px-2.5 py-1 text-xs font-semibold text-slate-700 hover:bg-slate-50"
                          >
                            Next
                          </button>
                        </div>

                        <div className="grid grid-cols-2 gap-3">
                          {desktopCalendarPanels.map((panel) => (
                            <div key={panel.title}>
                              <div className="mb-1.5 text-xs font-semibold text-slate-800">{panel.title}</div>
                              <div className="grid grid-cols-7 gap-0.5 text-center text-[10px] text-slate-500">
                                {CALENDAR_WEEKDAY_LABELS.map((dayLabel) => (
                                  <div key={`${panel.title}-${dayLabel}`} className="py-0.5">{dayLabel}</div>
                                ))}
                              </div>
                              <div className="grid grid-cols-7 gap-0.5 text-center">
                                {panel.cells.map((cell) => {
                                  const checkIn = bookingPanelStartDate || singleCityBookingDates.checkInDate;
                                  const checkOut = bookingPanelCheckOutDate;
                                  const isSelectedStart = Boolean(checkIn) && cell.iso === checkIn;
                                  const isSelectedEnd = Boolean(checkOut) && cell.iso === checkOut;
                                  const inRange = Boolean(checkIn && checkOut) && cell.iso > checkIn && cell.iso < checkOut;

                                  return (
                                    <button
                                      key={`${panel.title}-${cell.iso}`}
                                      type="button"
                                      onClick={() => selectDesktopBookingDate(cell.iso)}
                                      className={`rounded-md py-1 text-xs transition ${
                                        isSelectedStart || isSelectedEnd
                                          ? "bg-sky-600 text-white"
                                          : inRange
                                          ? "bg-sky-100 text-sky-900"
                                          : cell.inMonth
                                          ? "text-slate-800 hover:bg-slate-100"
                                          : "text-slate-300 hover:bg-slate-50"
                                      }`}
                                    >
                                      {cell.day}
                                    </button>
                                  );
                                })}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {!isBookingFormLocked && desktopBookingPopover === "guests" && (
                      <div className="absolute right-5 top-[192px] z-[80] w-[320px] rounded-2xl border border-slate-200 bg-white p-4 shadow-2xl">
                        <div className="space-y-3">
                          <div className="flex items-center justify-between">
                            <div>
                              <div className="text-sm font-semibold text-slate-900">Rooms</div>
                              <div className="text-xs text-slate-500">1 or more</div>
                            </div>
                            <div className="flex items-center gap-2">
                              <button type="button" onClick={() => adjustGuestCount("rooms", -1, 1, 8)} className="h-8 w-8 rounded-full border border-slate-300 text-slate-700">-</button>
                              <span className="w-5 text-center text-sm font-semibold text-slate-900">{bookingGuests.rooms}</span>
                              <button type="button" onClick={() => adjustGuestCount("rooms", 1, 1, 8)} className="h-8 w-8 rounded-full border border-slate-300 text-slate-700">+</button>
                            </div>
                          </div>

                          <div className="flex items-center justify-between">
                            <div>
                              <div className="text-sm font-semibold text-slate-900">Adults</div>
                              <div className="text-xs text-slate-500">Age 18+</div>
                            </div>
                            <div className="flex items-center gap-2">
                              <button type="button" onClick={() => adjustGuestCount("adults", -1, 1, 16)} className="h-8 w-8 rounded-full border border-slate-300 text-slate-700">-</button>
                              <span className="w-5 text-center text-sm font-semibold text-slate-900">{bookingGuests.adults}</span>
                              <button type="button" onClick={() => adjustGuestCount("adults", 1, 1, 16)} className="h-8 w-8 rounded-full border border-slate-300 text-slate-700">+</button>
                            </div>
                          </div>

                          <div className="flex items-center justify-between">
                            <div>
                              <div className="text-sm font-semibold text-slate-900">Children</div>
                              <div className="text-xs text-slate-500">Age 0-17</div>
                            </div>
                            <div className="flex items-center gap-2">
                              <button type="button" onClick={() => adjustGuestCount("children", -1, 0, 10)} className="h-8 w-8 rounded-full border border-slate-300 text-slate-700">-</button>
                              <span className="w-5 text-center text-sm font-semibold text-slate-900">{bookingGuests.children}</span>
                              <button type="button" onClick={() => adjustGuestCount("children", 1, 0, 10)} className="h-8 w-8 rounded-full border border-slate-300 text-slate-700">+</button>
                            </div>
                          </div>
                        </div>

                        <button
                          type="button"
                          onClick={() => setDesktopBookingPopover("")}
                          className="mt-4 w-full rounded-lg bg-sky-600 px-3 py-2 text-sm font-semibold text-white hover:bg-sky-500"
                        >
                          Done
                        </button>
                      </div>
                    )}


                  </div>


                </div>
              </aside>)}
            </div>

            {!itineraryJson && itineraryText && (
              <div className="bg-white shadow-md rounded-2xl p-4 sm:p-5 mt-5 border-l-4 border-green-500 whitespace-pre-line">
                <h2 className="text-base sm:text-lg font-bold text-gray-800 mb-2">Your Itinerary</h2>
                <p className="text-sm sm:text-base font-medium text-gray-700 mb-2">{REGENERATE_NOTE}</p>
                <p className="text-sm sm:text-base">{itineraryText}</p>
              </div>
            )}
            </div>
            </> )} {/* end tripMode === "single" */}

            {/* ── Multi-City Mode ── */}
            {tripMode === "multi" && (
              <div className="space-y-5">
                {/* Intro note */}
                <div className="bg-blue-50 border border-blue-200 rounded-2xl p-4 text-sm text-blue-900">
                  <p className="font-semibold mb-1">🌍 Multi-City Trip Planner</p>
                  <p>We help you plan your route, nights, and where to stay in each city. Once you arrive in each city, use <strong>Single City</strong> mode to generate a full detailed day-by-day itinerary from the comfort of your hotel.</p>
                </div>

                {/* Stop builder */}
                <div className="bg-white shadow-md rounded-2xl p-4 sm:p-5 border-l-4 border-red-600">
                  <h2 className="text-base sm:text-lg font-bold text-gray-800 mb-3">Your City Stops</h2>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-3">
                    <div>
                      <label className="block text-xs font-semibold text-gray-700 mb-1">Trip Start Date (optional)</label>
                      <input
                        type="date"
                        value={multiStartDate}
                        onChange={(e) => setMultiStartDate(e.target.value)}
                        className="w-full border border-gray-300 rounded-xl px-3 py-2 text-sm"
                      />
                    </div>
                    <div className="text-xs text-gray-500 self-end pb-1">
                      Add this to auto-fill Booking.com check-in/check-out dates for each city stop.
                    </div>
                  </div>
                  <div className="space-y-3">
                    {multiStops.map((stop, idx) => (
                      <div key={stop.id} className="space-y-2 rounded-xl border border-gray-200 p-2 sm:p-3">
                        <div className="grid grid-cols-12 gap-2 items-center">
                          <div className="col-span-1 text-center text-sm font-bold text-green-700">{idx + 1}</div>
                          <div className="col-span-4 sm:col-span-4">
                            <input
                              type="text"
                              placeholder="City"
                              value={stop.city}
                              onChange={(e) => updateMultiStop(stop.id, "city", e.target.value)}
                              className="w-full border border-gray-300 rounded-xl px-3 py-2 text-sm"
                            />
                          </div>
                          <div className="col-span-4 sm:col-span-4">
                            <input
                              type="text"
                              placeholder="Country"
                              value={stop.country}
                              onChange={(e) => updateMultiStop(stop.id, "country", e.target.value)}
                              className="w-full border border-gray-300 rounded-xl px-3 py-2 text-sm"
                            />
                          </div>
                          <div className="col-span-2 sm:col-span-2">
                            <input
                              type="number"
                              min="1"
                              max="14"
                              placeholder="Nights"
                              value={stop.nights}
                              onChange={(e) => updateMultiStop(stop.id, "nights", Math.max(1, parseInt(e.target.value) || 1))}
                              className="w-full border border-gray-300 rounded-xl px-2 py-2 text-sm text-center"
                            />
                          </div>
                          <div className="col-span-1 flex justify-center">
                            {multiStops.length > 2 && (
                              <button
                                onClick={() => removeMultiStop(stop.id)}
                                className="text-rose-500 hover:text-rose-700 text-lg font-bold"
                                aria-label="Remove stop"
                              >×</button>
                            )}
                          </div>
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                          <div>
                            <label className="block text-[11px] font-semibold text-gray-600 mb-1">Check-in</label>
                            <input
                              type="date"
                              value={multiStopDateRanges[idx]?.checkInDate || ""}
                              readOnly
                              disabled
                              className="w-full border border-gray-200 bg-gray-50 text-gray-500 rounded-xl px-3 py-2 text-xs"
                            />
                          </div>
                          <div>
                            <label className="block text-[11px] font-semibold text-gray-600 mb-1">Check-out</label>
                            <input
                              type="date"
                              value={multiStopDateRanges[idx]?.checkOutDate || ""}
                              readOnly
                              disabled
                              className="w-full border border-gray-200 bg-gray-50 text-gray-500 rounded-xl px-3 py-2 text-xs"
                            />
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                  <div className="text-xs text-gray-500 mt-2 mb-3">Nights = number of nights in each city</div>
                  <button
                    onClick={addMultiStop}
                    className="text-sm text-green-700 border border-green-400 rounded-xl px-4 py-2 hover:bg-green-50 font-semibold"
                  >
                    + Add Another City
                  </button>
                </div>

                {/* Budget (reuse) */}
                <div className="bg-white shadow-md rounded-2xl p-4 sm:p-5 border-l-4 border-purple-500">
                  <h2 className="text-base sm:text-lg font-bold text-gray-800 mb-2">Budget</h2>
                  <div className="grid grid-cols-3 gap-2">
                    {["Budget", "Mid-range", "Luxury"].map((b) => (
                      <button
                        key={b}
                        onClick={() => setSelectedBudget(b)}
                        className={`py-3 rounded-xl border font-semibold text-sm ${
                          selectedBudget === b ? "bg-green-500 text-white border-green-500" : "bg-gray-100 text-gray-800 border-gray-300"
                        }`}
                      >
                        {b === "Budget" ? "💶" : b === "Mid-range" ? "💵" : "💎"} {b}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Generate button */}
                <button
                  onClick={handleMultiCityGenerate}
                  disabled={multiLoading}
                  className="w-full bg-green-600 hover:bg-green-700 text-white py-3 rounded-2xl font-semibold shadow-md transition disabled:opacity-60 text-sm sm:text-base"
                >
                  <span className="inline-flex items-center justify-center gap-2">
                    {multiLoading ? <Loader className="w-5 h-5 animate-spin" /> : <Send className="w-5 h-5 transform rotate-45" />}
                    <span>{multiLoading ? "Generating Multi-City Plan…" : "Generate Multi-City Plan"}</span>
                  </span>
                </button>

                {/* Results */}
                {multiResult && (
                  <div className="space-y-6 mt-2">
                    {/* Trip header */}
                    <div className="bg-white shadow-md rounded-2xl p-4 sm:p-5 border-l-4 border-indigo-500">
                      <h2 className="text-lg sm:text-xl font-extrabold text-gray-900">{multiResult.tripTitle}</h2>
                      <p className="text-sm text-gray-600 mt-1">
                        {multiResult.totalNights} nights · {multiResult.budget} · {multiResult.cities?.length} cities
                      </p>
                      <div className="mt-3 flex flex-wrap gap-2">
                        {multiResult.cities?.map((c) => (
                          <span key={c.cityName} className="text-xs px-2 py-1 rounded-full bg-indigo-50 text-indigo-800 border border-indigo-200">
                            {c.cityName} ({c.nights}n)
                          </span>
                        ))}
                      </div>
                      <div className="mt-4 flex flex-wrap gap-2">
                        <button
                          onClick={() => handleShareClick("whatsapp", "multi")}
                          className="px-3 py-2 rounded-xl border border-green-600 bg-green-50 hover:bg-green-100 text-green-700 text-xs sm:text-sm"
                          type="button"
                        >
                          Share WhatsApp
                        </button>
                        <button
                          onClick={() => handleShareClick("facebook", "multi")}
                          className="px-3 py-2 rounded-xl border border-blue-600 bg-blue-50 hover:bg-blue-100 text-blue-700 text-xs sm:text-sm"
                          type="button"
                        >
                          Share Facebook
                        </button>
                        <button
                          onClick={() => handleShareClick("linkedin", "multi")}
                          className="px-3 py-2 rounded-xl border border-sky-600 bg-sky-50 hover:bg-sky-100 text-sky-700 text-xs sm:text-sm"
                          type="button"
                        >
                          Share LinkedIn
                        </button>
                        <button
                          onClick={() => handleShareClick("instagram", "multi")}
                          className="px-3 py-2 rounded-xl border border-rose-600 bg-rose-50 hover:bg-rose-100 text-rose-700 text-xs sm:text-sm"
                          type="button"
                        >
                          Instagram (copy link)
                        </button>
                      </div>
                    </div>

                    {/* City Cards */}
                    {multiResult.cities?.map((city, cityIdx) => (
                      <div key={city.cityName} className="bg-white shadow-md rounded-2xl overflow-hidden border border-gray-200">
                        {/* City header */}
                        <div className="bg-gradient-to-r from-green-600 to-emerald-500 px-5 py-4 text-white">
                          <div className="flex items-center justify-between flex-wrap gap-2">
                            <div>
                              <h3 className="text-lg font-extrabold">{city.cityName}, {city.country}</h3>
                              <p className="text-sm opacity-90">{city.nights} night{city.nights === 1 ? "" : "s"} · Stay in {city.recommendedNeighborhood}</p>
                              {city.checkInDate && city.checkOutDate && (
                                <p className="text-xs opacity-90 mt-1">Stay dates: {city.checkInDate} to {city.checkOutDate}</p>
                              )}
                            </div>
                            <div className="text-right text-xs opacity-80">Stop {cityIdx + 1} of {multiResult.cities.length}</div>
                          </div>
                          {city.neighborhoodWhy && (
                            <p className="text-xs mt-1 opacity-80">{city.neighborhoodWhy}</p>
                          )}
                        </div>

                        <div className="p-4 sm:p-5 space-y-4">
                          {/* Airport → City Center */}
                          {city.arrivalTransport && (
                            <div className="border border-sky-200 bg-sky-50 rounded-2xl p-4">
                              <div className="font-bold text-sky-900 mb-3 text-sm">✈️ {city.arrivalTransport.title || `${city.cityName} Airport → City Center`}</div>
                              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-xs">
                                {city.arrivalTransport.train && (
                                  <div className="bg-white rounded-xl p-3 border border-sky-200">
                                    <div className="font-semibold text-sky-800 mb-1">🚂 {city.arrivalTransport.train.name || "Train"}</div>
                                    <div className="text-gray-700">⏱ {city.arrivalTransport.train.duration}</div>
                                    <div className="text-gray-700">💶 {city.arrivalTransport.train.cost}</div>
                                    {city.arrivalTransport.train.tip && <div className="text-sky-700 mt-1 italic">{city.arrivalTransport.train.tip}</div>}
                                  </div>
                                )}
                                {city.arrivalTransport.bus && (
                                  <div className="bg-white rounded-xl p-3 border border-sky-200">
                                    <div className="font-semibold text-sky-800 mb-1">🚌 {city.arrivalTransport.bus.name || "Bus"}</div>
                                    <div className="text-gray-700">⏱ {city.arrivalTransport.bus.duration}</div>
                                    <div className="text-gray-700">💶 {city.arrivalTransport.bus.cost}</div>
                                    {city.arrivalTransport.bus.tip && <div className="text-sky-700 mt-1 italic">{city.arrivalTransport.bus.tip}</div>}
                                  </div>
                                )}
                                {city.arrivalTransport.taxi && (
                                  <div className="bg-white rounded-xl p-3 border border-sky-200">
                                    <div className="font-semibold text-sky-800 mb-1">🚕 Taxi</div>
                                    <div className="text-gray-700">⏱ {city.arrivalTransport.taxi.duration}</div>
                                    <div className="text-gray-700">💶 {city.arrivalTransport.taxi.cost}</div>
                                    {city.arrivalTransport.taxi.tip && <div className="text-sky-700 mt-1 italic">{city.arrivalTransport.taxi.tip}</div>}
                                  </div>
                                )}
                              </div>
                              {city.arrivalTransport.note && (
                                <div className="mt-2 text-xs text-sky-800 italic">{city.arrivalTransport.note}</div>
                              )}
                            </div>
                          )}

                          {/* 4 Accommodations */}
                          <div>
                            <div className="font-bold text-gray-800 text-sm mb-3">🏨 Where to Stay — {city.recommendedNeighborhood}</div>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                              {city.accommodations?.map((acc) => (
                                <div key={acc.rank} className="border border-gray-200 rounded-2xl p-3 bg-gray-50 hover:shadow-md transition">
                                  <div className="flex items-center justify-between mb-1">
                                    <span className="text-xs font-bold text-green-700 bg-green-100 px-2 py-0.5 rounded-full">{acc.label}</span>
                                    {acc.rating && <span className="text-xs text-amber-700">⭐ {acc.rating}</span>}
                                  </div>
                                  <div className="font-semibold text-gray-900 text-sm">{acc.name}</div>
                                  <div className="text-xs text-gray-500 mb-1">{acc.type} · {acc.neighborhood}</div>
                                  <div className="text-sm font-bold text-indigo-700 mb-1">{acc.nightlyPrice} / night</div>
                                  <div className="text-xs text-gray-600 mb-2">{acc.whyItFits}</div>
                                  {acc.bookingLink && HOTEL_LINKS_ENABLED && (
                                    <a
                                      href={sanitizeAccommodationLink(
                                        acc.bookingLink,
                                        city.cityName || "",
                                        city.country || destinationData.country || "",
                                        acc.name || "",
                                        city.checkInDate || "",
                                        city.checkOutDate || "",
                                        selectedBudget,
                                        2
                                      )}
                                      onClick={() => trackAnalytics("booking_click", { city: city.cityName })}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="inline-block text-xs bg-green-600 hover:bg-green-700 text-white px-3 py-1.5 rounded-lg font-semibold"
                                    >
                                      Hotels on Appi ↗
                                    </a>
                                  )}
                                  {acc.bookingLink && !HOTEL_LINKS_ENABLED && (
                                    <span className="inline-block text-xs bg-amber-100 text-amber-800 px-3 py-1.5 rounded-lg font-semibold">
                                      {HOTEL_LINKS_DISABLED_LABEL}
                                    </span>
                                  )}
                                </div>
                              ))}
                            </div>
                          </div>

                          {/* Highlights */}
                          {Array.isArray(city.highlights) && city.highlights.length > 0 && (
                            <div className="bg-amber-50 border border-amber-200 rounded-2xl p-3 text-xs">
                              <div className="font-semibold text-amber-900 mb-1">🌟 Top Highlights</div>
                              <div className="flex flex-wrap gap-2">
                                {city.highlights.map((h, i) => (
                                  <span key={i} className="bg-white border border-amber-200 text-amber-800 px-2 py-0.5 rounded-full">{h}</span>
                                ))}
                              </div>
                            </div>
                          )}

                          {/* Transfer to next city */}
                          {city.transferToNext && cityIdx < multiResult.cities.length - 1 && (
                            <div className="bg-slate-50 border border-slate-200 rounded-2xl p-3 text-xs">
                              <div className="font-semibold text-slate-800 mb-2">🚄 Getting to {city.transferToNext.to}</div>
                              <div className="space-y-2">
                                {city.transferToNext.options?.map((opt, oi) => (
                                  <div key={oi} className="flex flex-wrap gap-x-4 gap-y-1 text-slate-700 bg-white p-2 rounded-xl border border-slate-200">
                                    <span className="font-semibold">{opt.type}</span>
                                    {opt.duration !== "N/A" && <span>⏱ {opt.duration}</span>}
                                    {opt.cost !== "N/A" && <span>💶 {opt.cost}</span>}
                                    {opt.provider && <span>🏢 {opt.provider}</span>}
                                    {opt.tip && <span className="text-slate-500 italic w-full">{opt.tip}</span>}
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}

                          {/* CTA to Single City */}
                          <div className="bg-green-50 border border-green-200 rounded-2xl p-3 text-xs text-green-900">
                            <span className="font-semibold">🗺️ Arriving in {city.cityName}?</span> Switch to <strong>Single City</strong> mode to generate a full day-by-day itinerary from the comfort of your hotel.
                            <button
                              type="button"
                              onClick={() => switchCityAndGenerate(city)}
                              className="ml-2 inline-block text-xs bg-green-600 hover:bg-green-700 text-white px-3 py-1 rounded-lg font-semibold"
                            >
                              Generate Full City Itinerary
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )} {/* end tripMode === "multi" */}

            {/* ── Road Trip / InterRail Mode ── */}
            {(tripMode === "road" || tripMode === "interrail") && (
              <div className="space-y-5">
                <div className="bg-emerald-50 border border-emerald-200 rounded-2xl p-4 text-sm text-emerald-900">
                  {tripMode === "interrail" ? (
                    <>
                      <p className="font-semibold mb-1">🚆 InterRail Planner</p>
                      <p>
                        Plan station-to-station train journeys with practical transfer timing, overnight rail options, and city stop ideas.
                        For a full day-by-day city guide when you arrive, use <strong>Single City</strong> mode from your hotel.
                      </p>
                    </>
                  ) : (
                    <>
                      <p className="font-semibold mb-1">🚗 Road Trip Planner</p>
                      <p>
                        Plan point-to-point driving journeys with stopovers, campsites, charging/fuel hints, and route activities.
                        For a full day-by-day city guide when you arrive, use <strong>Single City</strong> mode from your hotel.
                      </p>
                    </>
                  )}
                </div>

                <div className="bg-white shadow-md rounded-2xl p-4 sm:p-5 border-l-4 border-green-600">
                  <h2 className="text-base sm:text-lg font-bold text-gray-800 mb-3">Route Setup</h2>
                  <div className="grid grid-cols-1 lg:grid-cols-12 gap-3">
                    <div className="lg:col-span-4">
                      <label className="text-xs font-semibold text-gray-600">Starting Point</label>
                      <input
                        type="text"
                        value={roadTripForm.startPoint}
                        onChange={(e) => setRoadTripForm((prev) => ({ ...prev, startPoint: e.target.value }))}
                        placeholder="City, town, port, or landmark"
                        className="mt-1 w-full border border-gray-300 rounded-xl px-3 py-2 text-sm"
                      />
                    </div>
                    <div className="lg:col-span-4">
                      <label className="text-xs font-semibold text-gray-600">Final Destination</label>
                      <input
                        type="text"
                        value={roadTripForm.endPoint}
                        onChange={(e) => setRoadTripForm((prev) => ({ ...prev, endPoint: e.target.value }))}
                        placeholder="City, town, port, or landmark"
                        className="mt-1 w-full border border-gray-300 rounded-xl px-3 py-2 text-sm"
                      />
                    </div>
                    <div className="lg:col-span-2">
                      <label className="text-xs font-semibold text-gray-600">Trip Days</label>
                      <input
                        type="number"
                        min="2"
                        max="30"
                        value={roadTripForm.tripDays}
                        onChange={(e) => setRoadTripForm((prev) => ({ ...prev, tripDays: Math.max(2, parseInt(e.target.value, 10) || 2) }))}
                        className="mt-1 w-full border border-gray-300 rounded-xl px-3 py-2 text-sm"
                      />
                    </div>
                    <div className="lg:col-span-2">
                      <label className="text-xs font-semibold text-gray-600">Vehicle</label>
                      <select
                        value={roadTripForm.vehicleType}
                        onChange={(e) => setRoadTripForm((prev) => ({ ...prev, vehicleType: e.target.value }))}
                        className="mt-1 w-full border border-gray-300 rounded-xl px-3 py-2 text-sm"
                      >
                        <option>Car</option>
                        <option>Campervan</option>
                        <option>Motorbike</option>
                        <option>EV</option>
                        <option>Interrail (Train)</option>
                      </select>
                    </div>

                    <div className="lg:col-span-4">
                      <label className="text-xs font-semibold text-gray-600">Trip Start Date (optional)</label>
                      <input
                        type="date"
                        value={roadTripForm.startDate}
                        onChange={(e) => setRoadTripForm((prev) => ({ ...prev, startDate: e.target.value }))}
                        className="mt-1 w-full border border-gray-300 rounded-xl px-3 py-2 text-sm"
                      />
                    </div>

                    <div className="lg:col-span-4">
                      <label className="text-xs font-semibold text-gray-600">Country / Region Context (optional)</label>
                      <input
                        type="text"
                        value={roadTripForm.countryContext}
                        onChange={(e) => setRoadTripForm((prev) => ({ ...prev, countryContext: e.target.value }))}
                        placeholder="e.g. Ireland + UK"
                        className="mt-1 w-full border border-gray-300 rounded-xl px-3 py-2 text-sm"
                      />
                    </div>
                    <div className="lg:col-span-4">
                      <label className="text-xs font-semibold text-gray-600">Route Style</label>
                      <select
                        value={roadTripForm.routeStyle}
                        onChange={(e) => setRoadTripForm((prev) => ({ ...prev, routeStyle: e.target.value }))}
                        className="mt-1 w-full border border-gray-300 rounded-xl px-3 py-2 text-sm"
                      >
                        <option>Relaxed scenic</option>
                        <option>Balanced</option>
                        <option>Fastest practical</option>
                        <option>Adventure-focused</option>
                      </select>
                    </div>
                    <div className="lg:col-span-4">
                      <label className="text-xs font-semibold text-gray-600">Budget</label>
                      <div className="mt-1 grid grid-cols-3 gap-2">
                        {["Budget", "Mid-range", "Luxury"].map((b) => (
                          <button
                            key={`road-budget-${b}`}
                            type="button"
                            onClick={() => setSelectedBudget(b)}
                            className={`py-2 rounded-xl border text-xs font-semibold ${
                              selectedBudget === b ? "bg-green-500 text-white border-green-500" : "bg-gray-100 text-gray-800 border-gray-300"
                            }`}
                          >
                            {b}
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>

                <div className="bg-white shadow-md rounded-2xl p-4 sm:p-5 border-l-4 border-blue-500">
                  <h3 className="text-base font-bold text-gray-800 mb-2">{tripMode === "interrail" ? "InterRail Features" : "Road Trip Features"}</h3>
                  <p className="text-xs text-gray-500 mb-3">Select up to 6 preferences</p>
                  <div className="flex flex-wrap gap-2">
                    {visibleRoadFeatures.map((feature) => (
                      <button
                        key={feature}
                        type="button"
                        onClick={() => toggleRoadFeature(feature)}
                        className={`px-3 py-2 rounded-xl border text-xs sm:text-sm ${
                          roadTripFeatures.includes(feature)
                            ? roadFeatureColorClasses[feature] || "bg-emerald-500 text-white border-emerald-500"
                            : "bg-gray-100 text-gray-800 border-gray-300"
                        }`}
                      >
                        {feature}
                      </button>
                    ))}
                  </div>
                </div>

                <button
                  onClick={handleRoadTripGenerate}
                  disabled={roadTripLoading}
                  className="w-full bg-green-600 hover:bg-green-700 text-white py-3 rounded-2xl font-semibold shadow-md transition disabled:opacity-60 text-sm sm:text-base"
                >
                  <span className="inline-flex items-center justify-center gap-2">
                    {roadTripLoading ? <Loader className="w-5 h-5 animate-spin" /> : <Send className="w-5 h-5 transform rotate-45" />}
                    <span>{roadTripLoading ? (tripMode === "interrail" ? "Generating InterRail Journey…" : "Generating Road Trip…") : (tripMode === "interrail" ? "Generate InterRail Journey" : "Generate Road Trip")}</span>
                  </span>
                </button>

                {roadTripResult && (
                  <div className="space-y-5">
                    <div className="bg-white shadow-md rounded-2xl p-4 sm:p-5 border-l-4 border-amber-500">
                      <h2 className="text-lg sm:text-xl font-extrabold text-gray-900">{roadTripResult.title || (tripMode === "interrail" ? "InterRail Journey Plan" : "Road Trip Plan")}</h2>
                      {roadTripResult.summary && <p className="text-sm text-gray-700 mt-1">{roadTripResult.summary}</p>}
                      <div className="mt-3 grid grid-cols-1 sm:grid-cols-3 gap-2 text-xs">
                        <div className="rounded-xl border border-red-200 bg-red-50 p-2"><strong>Days:</strong> {roadTripResult.totalDays || roadTripForm.tripDays}</div>
                        <div className="rounded-xl border border-blue-200 bg-blue-50 p-2"><strong>Distance:</strong> {roadTripResult.totalDistance || "N/A"}</div>
                        <div className="rounded-xl border border-green-200 bg-green-50 p-2"><strong>{tripMode === "interrail" ? "Transit:" : "Driving:"}</strong> {roadTripResult.estimatedDrivingTime || "N/A"}</div>
                      </div>
                      <div className="mt-3 flex flex-wrap gap-2">
                        <button
                          onClick={() => handleShareClick("whatsapp", tripMode)}
                          className="px-3 py-2 rounded-xl border border-green-600 bg-green-50 hover:bg-green-100 text-green-700 text-xs sm:text-sm"
                          type="button"
                        >
                          Share WhatsApp
                        </button>
                        <button
                          onClick={() => handleShareClick("facebook", tripMode)}
                          className="px-3 py-2 rounded-xl border border-blue-600 bg-blue-50 hover:bg-blue-100 text-blue-700 text-xs sm:text-sm"
                          type="button"
                        >
                          Share Facebook
                        </button>
                        <button
                          onClick={() => handleShareClick("linkedin", tripMode)}
                          className="px-3 py-2 rounded-xl border border-sky-600 bg-sky-50 hover:bg-sky-100 text-sky-700 text-xs sm:text-sm"
                          type="button"
                        >
                          Share LinkedIn
                        </button>
                        <button
                          onClick={() => handleShareClick("instagram", tripMode)}
                          className="px-3 py-2 rounded-xl border border-rose-600 bg-rose-50 hover:bg-rose-100 text-rose-700 text-xs sm:text-sm"
                          type="button"
                        >
                          Instagram (copy link)
                        </button>
                      </div>
                      {roadTripResult.routeMapLinks && (
                        <div className="mt-3 flex flex-wrap gap-2 text-xs">
                          {roadTripResult.routeMapLinks.googleMaps && (
                            <a className="px-3 py-1.5 rounded-lg border border-emerald-600 text-emerald-700 hover:bg-emerald-50" target="_blank" rel="noopener noreferrer" href={roadTripResult.routeMapLinks.googleMaps} onClick={() => trackAnalytics("route_map_open", { provider: "google" })}>🗺️ Google Maps / Android Auto ↗</a>
                          )}
                          {roadTripResult.routeMapLinks.appleMaps && (
                            <a className="px-3 py-1.5 rounded-lg border border-slate-500 text-slate-700 hover:bg-slate-50" target="_blank" rel="noopener noreferrer" href={roadTripResult.routeMapLinks.appleMaps} onClick={() => trackAnalytics("route_map_open", { provider: "apple" })}>🍎 Apple Maps ↗</a>
                          )}
                          {roadTripResult.routeMapLinks.waze && (
                            <a className="px-3 py-1.5 rounded-lg border border-blue-600 text-blue-700 hover:bg-blue-50" target="_blank" rel="noopener noreferrer" href={roadTripResult.routeMapLinks.waze} onClick={() => trackAnalytics("route_map_open", { provider: "waze" })}>🚗 Waze ↗</a>
                          )}
                        </div>
                      )}
                    </div>

                    {Array.isArray(roadTripResult.legs) && roadTripResult.legs.map((leg, idx) => (
                      <div
                        key={`leg-${idx}`}
                        className={`bg-white shadow-md rounded-2xl p-4 sm:p-5 border-l-4 ${
                          idx % 4 === 0
                            ? "border-blue-500"
                            : idx % 4 === 1
                            ? "border-red-500"
                            : idx % 4 === 2
                            ? "border-amber-500"
                            : "border-green-500"
                        }`}
                      >
                        <div className="flex flex-wrap items-center justify-between gap-2">
                          <h3 className="text-base sm:text-lg font-semibold text-gray-900">Day {leg.day || idx + 1}: {leg.from || "Start"} → {leg.to || "Stop"}</h3>
                          <div className="text-xs text-gray-600">{leg.distance || "N/A"} · {leg.driveTime || "N/A"}</div>
                        </div>
                        {leg.stopover && <p className="text-sm text-gray-700 mt-1"><strong>Stopover:</strong> {leg.stopover}</p>}

                        {Array.isArray(leg.activities) && leg.activities.length > 0 && (
                          <div className="mt-2 text-xs text-gray-700">
                            <strong>Route activities:</strong>{" "}
                            {leg.activities.map((activity, activityIndex) => {
                              const raw = typeof activity === "string" ? activity : (activity?.name || activity?.title || "");
                              const objectLink = typeof activity === "object"
                                ? normalizeUrl(activity?.mapLink || activity?.link || activity?.url || activity?.website || activity?.maps || "")
                                : "";
                              const parsedActivity = parseNameAndUrl(raw);
                              const activityParts = splitNameAndDetail(parsedActivity.name || "");
                              const activityName = activityParts.name?.trim() || "Route activity";
                              const activityLink = objectLink || parsedActivity.mapLink || "";
                              const extractedDestination = extractMapsDestinationFromUrl(activityLink);
                              const fallbackDestination = [cleanDirectionsDestination(activityName), leg.stopover || leg.to || "", roadTripForm.countryContext || ""]
                                .filter(Boolean)
                                .join(", ")
                                .trim();
                              const mapsDestination = extractedDestination || fallbackDestination;
                              const placeLink = resolveGooglePlaceLink(activityLink, mapsDestination);

                              return (
                                <React.Fragment key={`route-activity-${activityIndex}`}>
                                  {activityIndex > 0 && <span>{" • "}</span>}
                                  {placeLink ? (
                                    <a
                                      href={placeLink}
                                      onClick={() => trackAnalytics("poi_direction_open", { city: leg.to || leg.stopover || "" })}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="font-medium text-blue-700 underline decoration-blue-300 hover:text-blue-800"
                                    >
                                      {activityName}
                                    </a>
                                  ) : (
                                    <span>{activityName}</span>
                                  )}
                                </React.Fragment>
                              );
                            })}
                          </div>
                        )}

                        <div className="mt-3 grid grid-cols-1 lg:grid-cols-2 gap-3">
                          <div className="rounded-xl border border-blue-200 bg-blue-50 p-3 text-xs text-blue-900">
                            <div className="font-semibold mb-1">Energy & Fuel</div>
                            <div><strong>EV:</strong> {Array.isArray(leg.fuelAndCharging?.evCharging) && leg.fuelAndCharging.evCharging.length > 0 ? leg.fuelAndCharging.evCharging.join(", ") : "No specific EV points listed"}</div>
                            <div className="mt-1"><strong>HVO:</strong> {Array.isArray(leg.fuelAndCharging?.hvoStops) && leg.fuelAndCharging.hvoStops.length > 0 ? leg.fuelAndCharging.hvoStops.join(", ") : "No specific HVO stops listed"}</div>
                            {leg.fuelAndCharging?.fuelTip && <div className="mt-1 italic">{leg.fuelAndCharging.fuelTip}</div>}

                            {Array.isArray(leg.highlights) && leg.highlights.length > 0 && (
                              <div className="mt-3 pt-3 border-t border-blue-200">
                                <div className="font-semibold mb-1">Places of Interest</div>
                                <ul className="space-y-2">
                                  {leg.highlights.slice(0, 6).map((poi, pi) => {
                                    const raw = typeof poi === "string" ? poi : (poi?.name || "");
                                    const objectDetail = typeof poi === "object"
                                      ? (poi?.detail || poi?.description || poi?.note || poi?.why || "")
                                      : "";
                                    const objectLink = typeof poi === "object"
                                      ? normalizeUrl(poi?.mapLink || poi?.link || poi?.url || poi?.website || poi?.maps || "")
                                      : "";

                                    const parsed = parseNameAndUrl(raw);
                                    const split = splitNameAndDetail(parsed.name || "");
                                    const name = split.name?.trim() || "Point of interest";
                                    const inlineDetail = split.detail || "";
                                    const detail = objectDetail || inlineDetail;
                                    const poiLink = objectLink || parsed.mapLink || "";
                                    const fallbackDestination = [name, leg.to || "", roadTripForm.countryContext || ""]
                                      .filter(Boolean)
                                      .map((value) => cleanDirectionsDestination(value))
                                      .join(", ")
                                      .trim();
                                    const extractedDestination = extractMapsDestinationFromUrl(poiLink);
                                    const mapsDestination = extractedDestination || fallbackDestination;
                                    const placeLink = resolveGooglePlaceLink(poiLink, mapsDestination);
                                    const isOptional = /\(optional\)|\boptional\b/i.test(raw) || Boolean(poi?.optional);

                                    return (
                                      <li key={`poi-${pi}`} className="rounded-lg border border-blue-200 bg-white p-2">
                                        <div className="font-semibold text-blue-900">
                                          {placeLink ? (
                                            <a
                                              href={placeLink}
                                              onClick={() => trackAnalytics("poi_direction_open", { city: leg.to || leg.stopover || "" })}
                                              target="_blank"
                                              rel="noopener noreferrer"
                                              className="underline decoration-blue-400 hover:text-blue-700"
                                            >
                                              {name}
                                            </a>
                                          ) : (
                                            name
                                          )}
                                        </div>
                                        {detail && <div className="text-blue-800 mt-0.5">{detail}</div>}
                                        {placeLink && (
                                          <a
                                            href={placeLink}
                                            onClick={() => trackAnalytics("poi_direction_open", { city: leg.to || leg.stopover || "" })}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="text-[11px] font-semibold text-blue-700 underline mt-1 inline-block"
                                          >
                                            Open in Google Maps ↗
                                          </a>
                                        )}
                                        {isOptional && <div className="text-[11px] text-blue-700 mt-1">Optional stop</div>}
                                      </li>
                                    );
                                  })}
                                </ul>
                              </div>
                            )}
                          </div>

                          <div className="rounded-xl border border-red-200 bg-red-50 p-3 text-xs text-red-900">
                            <div className="font-semibold mb-1">Overnight Options</div>
                            {leg.checkInDate && leg.checkOutDate && (
                              <div className="mb-2 rounded-lg border border-red-200 bg-white p-2 text-[11px] text-red-800">
                                Stay dates: {leg.checkInDate} to {leg.checkOutDate} ({leg.stayNights || 1} night{Number(leg.stayNights || 1) === 1 ? "" : "s"})
                              </div>
                            )}
                            {Array.isArray(leg.overnightOptions) && leg.overnightOptions.length > 0 ? (
                              <ul className="space-y-2">
                                {leg.overnightOptions.slice(0, 4).map((opt, oi) => (
                                  <li key={`overnight-${oi}`} className="bg-white border border-red-200 rounded-lg p-2">
                                    <div className="font-semibold">{opt.type}: {opt.name}</div>
                                    <div>{opt.priceRange || "Price N/A"}</div>
                                    {opt.why && <div className="text-red-800">{opt.why}</div>}
                                    {opt.bookingLink && HOTEL_LINKS_ENABLED && (
                                      <a
                                        href={sanitizeAccommodationLink(
                                          opt.bookingLink,
                                          leg.stopover || leg.to || leg.from || "",
                                          destinationData.country || "",
                                          opt.name || "",
                                          leg.checkInDate || "",
                                          leg.checkOutDate || "",
                                          selectedBudget,
                                          2
                                        )}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="underline font-semibold text-red-800"
                                        onClick={() => trackAnalytics("booking_click", { city: leg.stopover || leg.to || leg.from || "Route stop" })}
                                      >
                                        Hotels on Appi ↗
                                      </a>
                                    )}
                                    {opt.bookingLink && !HOTEL_LINKS_ENABLED && (
                                      <span className="inline-block text-[11px] font-semibold text-amber-800">
                                        {HOTEL_LINKS_DISABLED_LABEL}
                                      </span>
                                    )}
                                  </li>
                                ))}
                              </ul>
                            ) : (
                              <div>No overnight options listed for this leg.</div>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}

                    {Array.isArray(roadTripResult.safetyNotes) && roadTripResult.safetyNotes.length > 0 && (
                      <div className="bg-amber-50 border border-amber-300 rounded-2xl p-4 text-xs text-amber-900">
                        <div className="font-semibold mb-1">Safety Notes</div>
                        <ul className="list-disc list-inside space-y-1">
                          {roadTripResult.safetyNotes.map((note, i) => <li key={`safety-${i}`}>{note}</li>)}
                        </ul>
                      </div>
                    )}

                    <div className="bg-green-50 border border-green-300 rounded-2xl p-4 text-xs text-green-900">
                      <strong>🗺️ Next step:</strong> {roadTripResult.singleCityReminder || "When you arrive in a stop city, switch to Single City mode to generate a detailed day-by-day itinerary from your hotel."}
                    </div>
                  </div>
                )}
              </div>
            )} {/* end tripMode === "road" || tripMode === "interrail" */}
          </>
        )}

        {false && currentPage === "planner" && (
          <>
            <button
              type="button"
              onClick={() => setShowMobileMoreSheet(true)}
              className="fixed bottom-4 right-4 z-40 inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-4 py-3 text-sm font-semibold text-slate-800 shadow-xl lg:hidden"
              aria-label="Open More menu"
            >
              <span className="text-base leading-none">⋯</span>
              <span>More</span>
            </button>

            {showMobileMoreSheet && (
              <div className="fixed inset-0 z-50 lg:hidden" role="dialog" aria-modal="true" aria-label="Appi Ecosystem">
                <button
                  type="button"
                  className="absolute inset-0 bg-black/40"
                  aria-label="Close More menu"
                  onClick={() => setShowMobileMoreSheet(false)}
                />

                <div className="absolute bottom-0 left-0 right-0 rounded-t-3xl border border-slate-200 bg-white p-5 shadow-2xl">
                  <div className="mx-auto mb-3 h-1.5 w-12 rounded-full bg-slate-300" />
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <h3 className="text-base font-extrabold text-slate-900">Appi Ecosystem</h3>
                      <p className="mt-1 text-xs text-slate-500">Connected products across travel and lifestyle</p>
                    </div>
                    <button
                      type="button"
                      onClick={() => setShowMobileMoreSheet(false)}
                      className="rounded-lg border border-slate-200 px-2 py-1 text-sm font-semibold text-slate-600"
                    >
                      Close
                    </button>
                  </div>

                  <div className="mt-4 space-y-2 text-sm">
                    <a
                      href="https://www.appiholidays.com"
                      className="block rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 font-semibold text-slate-800"
                    >
                      AppiHolidays
                    </a>
                    <div className="block rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 font-semibold text-slate-500">
                      AppiHotels (Coming Soon Sept'26)
                    </div>
                    <div className="block rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 font-semibold text-slate-500">
                      AppiFriends (Coming 2027)
                    </div>
                    <div className="block rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 font-semibold text-slate-500">
                      AppiFlights (Coming 2027)
                    </div>
                  </div>
                </div>
              </div>
            )}
          </>
        )}

        <footer className="mt-8 pt-6 border-t border-gray-300 text-center text-xs sm:text-sm text-gray-600">
          <p className="mb-3">© {new Date().getFullYear()} AppiHolidays.com.</p>
          <div className="flex flex-wrap justify-center gap-2 sm:gap-4">
            <a
              href="#privacy"
              onClick={(e) => {
                e.preventDefault();
                window.location.hash = "privacy";
              }}
              className="hover:text-gray-900 transition underline"
              aria-label="Privacy Policy"
            >
              Privacy Policy
            </a>
            <a
              href="#cookies"
              onClick={(e) => {
                e.preventDefault();
                window.location.hash = "cookies";
              }}
              className="hover:text-gray-900 transition underline"
              aria-label="Cookies Policy"
            >
              Cookies Policy
            </a>
            <a
              href="#security"
              onClick={(e) => {
                e.preventDefault();
                window.location.hash = "security";
              }}
              className="hover:text-gray-900 transition underline"
              aria-label="Security Policy"
            >
              Security Policy
            </a>
            <a
              href="#about"
              onClick={(e) => {
                e.preventDefault();
                window.location.hash = "about";
              }}
              className="hover:text-gray-900 transition underline"
              aria-label="About AppiHolidays.com"
            >
              About AppiHolidays.com
            </a>
          </div>
        </footer>
      </div>
    </div>
  );
}

